/*
 * SuperCalc Pro - Advanced Retirement Planning Calculator
 * Copyright © 2024-2025 SuperCalcPro Pty Ltd
 * All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL - TRADE SECRET
 * 
 * This file contains proprietary phased retirement optimization algorithms,
 * binary search implementations, and calculation methods that constitute
 * trade secrets. Unauthorized access, copying, distribution, or use is
 * strictly prohibited and may result in legal action.
 * 
 * For licensing inquiries: support@supercalcpro.com.au
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM, { flushSync } from 'react-dom';
import { useAdvancedState } from './AdvancedState';
import { SimulationEngine } from './SimulationEngine';
import { ScenarioWrapper } from './ScenarioWrapper';
import { loadScenario, getScenario, FEATURED_ADVANCED_SCENARIO_IDS } from './data/exampleScenarios';
import { ExampleScenariosSelector } from './components/ExampleScenariosSelector.js';
import AdvancedTutorialGuide from './AdvancedTutorialGuide.js';
import DraggableTutorialPanel from './components/DraggableTutorialPanel.js';
import { MobileCard, MobileNumberInput, MobileButton, useMobileDetection } from './components/MobileOptimizedUI.js';
import { MobileCurrencyInput, MobileAgeInput, MobilePercentageInput, MobileSelect, MobileCheckbox } from './components/MobileOptimizedInput';
import MobileLoadingSpinner from './components/MobileLoadingSpinner';
import ResponsiveTable from './components/ResponsiveTable';
import { getResponsiveChartConfig, formatChartCurrency } from './utils/mobileChartConfig';
import { trackLeadCaptureEvent, postCalculatorLeadCapture } from './leadCaptureAnalytics.js';
import PushNotificationService from './PushNotificationService.js';
import PDFExportManager from './PDFExportManager.js';
import StrategyComparison from './components/StrategyComparison.js';
import FixedIncomeVariesNote, { buildFixedIncomeVariesNote } from './components/FixedIncomeVariesNote.js';
import { FinancialConfig, calculateAfterTaxIncome, normalizeGeneralTransferBalanceCap } from './FinancialConfig.js';
import {
  buildHistoricalReturnsSnapshot,
  resolveBaselineReturns,
  applySequenceRiskToReturns,
  computeStrategyMSI,
  computeStrategyFullResult,
  buildBaselineCalcInputs,
  buildWhatIfCalcInputs,
  computeRollingPeriodAnalysis,
} from './whatIfEngine.js';
import { computeFixedSustainableBinarySearch as computeFixedMSIShared, computeVanguardGuardrailsSeries as computeVGSeriesShared, computeDynamicSWRSeries as computeDynSeriesShared, computeFloorCeilingSeries as computeFCSeriesShared } from './AdvancedCalculatorShared';
import { parseLumpSumWithdrawals, hasLumpSumWithdrawals, defaultLumpSumWithdrawalRows, normalizeLumpSumWithdrawalRows, isLumpSumRowTouched, getVisibleLumpSumRows, coerceLumpSumRowsForProjection, getLumpSumScheduleDiagnostics, formatLumpSumRow } from './lumpSumWithdrawals.js';
import { parseLumpSumContributions, hasLumpSumContributions, defaultLumpSumContributionRows, normalizeLumpSumContributionRows, isLumpSumContributionRowTouched, getVisibleLumpSumContributionRows, coerceLumpSumContributionRowsForProjection, getLumpSumContributionScheduleDiagnostics, formatLumpSumContributionRow } from './lumpSumContributions.js';
import { runMonteCarloSimulationAsync } from './MonteCarloEngine.js';
import { scrollToElement } from './utils/mobileScrollFix.js';
import { PhasedRetirementEngine } from './PhasedRetirementEngine.js';
import {
  alertCheckoutFailure,
  startPdfUnlockCheckout,
} from './utils/recommendedCheckout.js';
import { openPricingModal } from './utils/openPricingModal.js';
import {
  subscribeWithTheirNumberLabel,
  exhaustedSupportCopy,
} from './utils/paywallCopy.js';
import { formatInputValue } from './utils/inputHelpers.js';
import CookieConsent from './components/CookieConsent.js';
import InfoTooltip from './components/InfoTooltip.js';
import { EmailCaptureForm } from './components/EmailCaptureForm.js';
import { AdvancedWalkthroughVideo } from './components/AdvancedWalkthroughVideo.js';
import CalculationTransparency from './components/CalculationTransparency.js';
import { SubscriptionManager } from './SubscriptionManager.js';
import {
  trackCalculationComplete,
  trackAdvancedDemoShown,
  trackAdvancedRunClicked,
  trackAdvancedRunSucceeded,
} from './utils/funnelAnalytics.js';
import { shouldShowLocalDevChrome, isOfflineLocalServer } from './utils/presentationMode.js';

// --- Withdrawal Strategies ---

// Helper function: Calculate annuity payment (PMT) for Dynamic SWR
// Formula: PMT = PV × [r(1+r)^n] / [(1+r)^n - 1]
// This calculates the withdrawal amount that will deplete the balance to targetFinalBalance over n years
// assuming a constant rate of return. The PMT formula naturally increases the withdrawal
// percentage as years remaining decreases (e.g., 5.3% at age 64 with 36 years left,
// 6.0% at age 75 with 25 years left, etc.)
const calculateAnnuityPayment = (presentValue, annualRate, yearsRemaining, targetFinalBalance = 0) => {
  // Calculate withdrawable amount (preserve target final balance)
  const withdrawableAmount = Math.max(0, presentValue - targetFinalBalance);
  if (withdrawableAmount <= 0 || yearsRemaining <= 0) return 0;
  if (annualRate === 0) return withdrawableAmount / yearsRemaining;
  
  const ratePerPeriod = annualRate;
  const numPeriods = yearsRemaining;
  const factor = Math.pow(1 + ratePerPeriod, numPeriods);
  
  return withdrawableAmount * (ratePerPeriod * factor) / (factor - 1);
};

const AGE_PENSION_AGE = 67;

const calculateAgePensionForHousehold = ({
  totalAssets,
  otherIncome,
  isCouple,
  isHomeowner,
  year = 0,
  pensionGrowthRate,
  rentPaidAnnual = 0,
  employmentIncome = 0,
  ageA,
  ageB,
  includePension = true
}) => {
  if (!includePension) return 0;

  if (!isCouple) {
    return ageA >= AGE_PENSION_AGE
      ? (Number(SimulationEngine.calculatePension(totalAssets, otherIncome, false, isHomeowner, year, pensionGrowthRate, rentPaidAnnual, employmentIncome)) || 0)
      : 0;
  }

  const eligibleCount = (ageA >= AGE_PENSION_AGE ? 1 : 0) + (ageB >= AGE_PENSION_AGE ? 1 : 0);
  if (eligibleCount === 0) return 0;

  const coupleEntitlement = Number(
    SimulationEngine.calculatePension(totalAssets, otherIncome, true, isHomeowner, year, pensionGrowthRate, rentPaidAnnual, employmentIncome)
  ) || 0;

  return coupleEntitlement * (eligibleCount / 2);
};

const formatRatesVerifiedDate = (isoDate) => {
  if (!isoDate) return 'recently';
  try {
    return new Intl.DateTimeFormat('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(new Date(isoDate));
  } catch {
    return 'recently';
  }
};

const scenarioForOrganicEntry = (article = '') => {
  const slug = article.toLowerCase();
  if (/couples-different-retirement|younger-partner|phased/.test(slug)) return 'couple-phased-retirement';
  if (/retire-at-60|early-retire|true-cost-retirement/.test(slug)) return 'retire-at-60';
  if (/worst-years|sequence|monte-carlo|safe-withdrawal|probability-retirement-fails/.test(slug)) return 'sequence-risk-1969';
  if (/age-pension|deeming|homeowner|gifting|asset-test|taper/.test(slug)) return 'age-pension-couple';
  return 'age-pension-couple';
};

import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, BarChart, Bar, ComposedChart, Line, Legend, Customized } from 'recharts';
import { historicalData, lifeExpectancyData } from './data';

// Move StrategySelect outside to prevent recreation on every render
const StrategySelect = ({ value, onChange, strategyOptions }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const current = strategyOptions.find((o) => o.value === value) || strategyOptions[0];
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);
  
  return (
    <div ref={dropdownRef} className="relative" style={{ overflow: 'visible' }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((s) => !s)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-left focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
      >
        <span className="font-medium">{current.label}</span>
      </button>
      {open && (
        <ul className="absolute z-50 mt-1 w-[28rem] max-h-64 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg right-0" role="listbox" style={{ overflowX: 'visible' }}>
          {strategyOptions.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              aria-disabled={opt.disabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (opt.disabled) {
                  return; // Don't allow selection of disabled options
                }
                setOpen(false);
                setTimeout(() => onChange(opt.value), 0);
              }}
              className={`px-3 py-2 ${
                opt.disabled 
                  ? 'cursor-not-allowed opacity-50 bg-gray-50' 
                  : 'cursor-pointer hover:bg-gray-50'
              } ${
                opt.value === value ? 'bg-gray-100' : ''
              }`}
            >
              <div className="flex items-start gap-2 pr-6">
                <span className={`text-sm flex-1 ${opt.disabled ? 'text-gray-500' : 'text-gray-800'}`}>
                  {opt.label}
                  {opt.disabled && ' (Not available)'}
                </span>
                <InfoTooltip content={opt.tooltip} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CollapsibleInputSection = ({ title, subtitle, tooltip, open, onToggle, children, className = '' }) => (
  <div className={`mt-6 border border-gray-200 rounded-lg bg-white overflow-hidden shadow-sm ${className}`}>
    <button
      type="button"
      onClick={() => onToggle(!open)}
      className="w-full p-4 flex items-center justify-between gap-3 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      aria-expanded={open}
    >
      <div>
        <div className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <span>{title}</span>
          {tooltip && (
            <span onClick={(e) => e.stopPropagation()}>
              <InfoTooltip content={tooltip} />
            </span>
          )}
        </div>
        {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
      <span className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true">▾</span>
    </button>
    {open && (
      <div className="border-t border-gray-200 p-6 bg-gray-50/40">
        {children}
      </div>
    )}
  </div>
);

function LastSavedTicker({ savedAt }) {
  const labelRef = useRef(null);
  useEffect(() => {
    const tick = () => {
      if (labelRef.current) {
        const seconds = Math.floor((Date.now() - savedAt.getTime()) / 1000);
        labelRef.current.textContent = `Last saved: ${seconds} seconds ago`;
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [savedAt]);
  return (
    <div className="text-center text-xs text-gray-500 mt-8 mb-4">
      <span ref={labelRef}>Last saved: 0 seconds ago</span>
    </div>
  );
}

// ============================================================
// FreeRunsEmailGate: shown when anonymous runs are exhausted.
// Capturing an email grants 1 more free run.
// ============================================================
function FreeRunsEmailGate({ onEmailCaptured, onUpgradeClick, onDismiss }) {
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    setError(null);

    const trimmed = email.trim();
    const source = 'free_runs_gate';
    trackLeadCaptureEvent('free_runs_email_capture_attempt', {
      source,
      calculatorTier: 'advanced'
    });

    // Store locally immediately — this is what grants the extra runs
    SubscriptionManager.setAdvancedFreeEmail(trimmed);

    try {
      const { ok, data } = await postCalculatorLeadCapture({
        email: trimmed,
        calculatorType: 'advanced',
        source
      });
      const leadStored = ok && data.leadStored !== false;
      if (leadStored) {
        trackLeadCaptureEvent('free_runs_email_captured', {
          source,
          calculatorTier: 'advanced',
          leadStored: true
        });
      } else {
        trackLeadCaptureEvent('free_runs_email_capture_failed', {
          source,
          calculatorTier: 'advanced',
          leadStored: false,
          error: data.error || data.message || `http_${ok ? 'no_store' : 'error'}`
        });
      }
    } catch (err) {
      trackLeadCaptureEvent('free_runs_email_capture_failed', {
        source,
        calculatorTier: 'advanced',
        leadStored: false,
        error: err?.message || 'network_error'
      });
    }

    setIsSubmitting(false);
    onEmailCaptured(trimmed);
  };

  return (
    <div className="mb-6 rounded-xl border-2 border-amber-200 bg-amber-50 shadow-lg overflow-hidden">
      <div className="bg-amber-500 px-6 py-3 flex items-center justify-between">
        <p className="text-white font-bold text-base">You've used your free run</p>
        <button type="button" onClick={onDismiss} className="text-white/80 hover:text-white text-xl leading-none">×</button>
      </div>
      <div className="px-6 py-5">
        <p className="text-gray-800 font-medium mb-1">Enter your email to unlock 1 more free run — no payment, no commitment.</p>
        <p className="text-gray-600 text-sm mb-4">We'll also email you a <strong>Retirement Readiness Checklist</strong>. Unsubscribe anytime.</p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mb-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            required
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 text-base disabled:bg-gray-100"
          />
          <button
            type="submit"
            disabled={isSubmitting || !email.trim()}
            className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition disabled:opacity-50 text-base whitespace-nowrap"
          >
            {isSubmitting ? 'Unlocking…' : 'Unlock 1 more run'}
          </button>
        </form>
        {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
        <div className="border-t border-amber-200 pt-4">
          <p className="text-gray-600 text-sm mb-2">Or keep testing with Advanced Retirement:</p>
          <button
            type="button"
            onClick={onUpgradeClick}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition text-sm"
          >
            Keep testing this plan — from $14.99/mo
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppAdvancedMinimal() {
  const [showTutorial, setShowTutorial] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [fixedIncomeVariesNote, setFixedIncomeVariesNote] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showInstructionalPopup, setShowInstructionalPopup] = useState(false);
  const [isFromMonteCarloDemo, setIsFromMonteCarloDemo] = useState(false);
  const [ratesVerifiedDate, setRatesVerifiedDate] = useState(null);
  const [entryScenarioNotice, setEntryScenarioNotice] = useState(null);
  const [modalEmail, setModalEmail] = useState('');
  const [isSubmittingModalEmail, setIsSubmittingModalEmail] = useState(false);
  // Free run gating state
  const [showFreeRunsEmailGate, setShowFreeRunsEmailGate] = useState(false);
  const [showPostResultUpsell, setShowPostResultUpsell] = useState(false);
  const [postResultUpsellDismissed, setPostResultUpsellDismissed] = useState(false);
  const [freeRunsStatus, setFreeRunsStatus] = useState(() => SubscriptionManager.canRunAdvancedFree());
  const [savedScenario, setSavedScenario] = useState(() => {
    try {
      const s = localStorage.getItem('adv_saved_scenario');
      return s ? JSON.parse(s) : null;
    } catch (e) { return null; }
  });
  const [saveScenarioName, setSaveScenarioName] = useState('');
  const [saveScenarioFeedback, setSaveScenarioFeedback] = useState('');
  const exportMenuRef = useRef(null);
  const calculateButtonRef = useRef(null);
  const isMobile = useMobileDetection();

  useEffect(() => {
    let cancelled = false;
    fetch('/data/financial-rates.json')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.lastUpdated) {
          setRatesVerifiedDate(formatRatesVerifiedDate(data.lastUpdated));
        }
      })
      .catch(() => {
        // The banner still renders with a conservative fallback if rates metadata is unavailable.
      });
    return () => { cancelled = true; };
  }, []);
  
  // Function to run advanced projection - find button in DOM directly
  const runAdvancedProjection = () => {
    console.log('🎯 runAdvancedProjection called');
    
    let attempts = 0;
    const maxAttempts = 40;
    
    const tryClick = () => {
      attempts++;
      
      // Try ref first
      if (calculateButtonRef.current) {
        console.log('✅ Calculate button found via ref! Clicking now.');
        calculateButtonRef.current.click();
        return;
      }
      
      // Try finding button in DOM by text content
      const buttons = Array.from(document.querySelectorAll('button'));
      
      // DEBUG: Log all button texts on first attempt
      if (attempts === 1) {
        console.log('🔍 DEBUG: Total buttons:', buttons.length);
        buttons.forEach((btn, i) => {
          console.log(`🔍 Button ${i}: "${btn.textContent.trim().substring(0, 80)}"`);
        });
      }
      
      const calcButton = buttons.find(btn => 
        btn.textContent.includes('Run my plan') ||
        btn.textContent.includes('Retirement Analysis')
      );
      
      if (calcButton) {
        console.log('✅ Calculate button found in DOM! Clicking now.');
        calcButton.click();
        return;
      }
      
      if (attempts < maxAttempts) {
        console.log(`⏳ Calculate button not found, retry ${attempts}/${maxAttempts}...`);
        setTimeout(tryClick, 200);
      } else {
        console.error('❌ Calculate button not found after', maxAttempts, 'attempts (8 seconds)');
        // Scroll to where the button should be
        window.scrollTo({ top: 1000, behavior: 'smooth' });
      }
    };
    
    // Wait a bit before starting to ensure component is mounted
    setTimeout(tryClick, 500);
  };
  
  // Initialize push notifications (skip on offline local server — no SW/push infra)
  useEffect(() => {
    if (isOfflineLocalServer()) return;
    PushNotificationService.initialize().then((success) => {
      if (success) {
        PushNotificationService.scheduleReminders();
      }
    });
  }, []);

  // Check for URL parameters (Monte Carlo demo redirect OR scenario parameter)
  useEffect(() => {
    console.log('🔍 App_advanced_min.js: Checking URL params...');
    
    // Parse parameters from BOTH query string AND hash (hash can contain params like #advanced?scenario=xyz)
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash; // e.g., "#advanced?scenario=couple-phased-retirement"
    const hashParams = hash.includes('?') ? new URLSearchParams(hash.split('?')[1]) : new URLSearchParams();
    
    console.log('🔍 URL search:', window.location.search);
    console.log('🔍 Hash:', hash);
    console.log('🔍 demo param:', urlParams.get('demo'));
    console.log('🔍 from param:', urlParams.get('from'));
    console.log('🔍 scenario param (search):', urlParams.get('scenario'));
    console.log('🔍 scenario param (hash):', hashParams.get('scenario'));
    
    // Check for direct scenario parameter (from blog links) - check BOTH locations
    const scenarioParam = hashParams.get('scenario') || urlParams.get('scenario');
    if (scenarioParam) {
      console.log('🎯 Scenario parameter detected:', scenarioParam);
      localStorage.setItem('loadExample', scenarioParam);
      localStorage.setItem('loadExampleEditable', '1');
      console.log('🎯 Set loadExample in localStorage:', scenarioParam);
    }

    const getPrefillParam = (...keys) => {
      for (const key of keys) {
        const value = urlParams.get(key) || hashParams.get(key);
        if (value != null && value !== '') return value;
      }
      return null;
    };
    const prefillPayload = {
      age: getPrefillParam('age', 'your_age'),
      balance: getPrefillParam('balance', 'super', 'your_super'),
      retirementAge: getPrefillParam('retirement_age', 'retire_age'),
      targetIncome: getPrefillParam('target_income', 'income', 'spending'),
      status: getPrefillParam('status', 'relationship'),
      homeowner: getPrefillParam('homeowner', 'is_homeowner'),
      partnerAge: getPrefillParam('partner_age'),
      partnerBalance: getPrefillParam('partner_balance', 'partner_super'),
      partnerRetirementAge: getPrefillParam('partner_retirement_age', 'partner_retire_age'),
      partnerSalary: getPrefillParam('partner_salary'),
      salary: getPrefillParam('salary', 'your_salary'),
      otherAssets: getPrefillParam('other_assets'),
      otherTaxableIncome: getPrefillParam('other_income', 'other_taxable_income', 'other_taxable'),
      startYear: getPrefillParam('start_year', 'start'),
      periodLength: getPrefillParam('period', 'years'),
      phased: getPrefillParam('phased', 'phased_retirement'),
      caseLabel: getPrefillParam('case_label', 'case'),
      pensionGrowthRate: getPrefillParam('pension_growth', 'pension_growth_rate'),
      investmentFees: getPrefillParam('investment_fees', 'investment_fee'),
      investmentFeeCap: getPrefillParam('investment_fee_cap', 'fee_cap'),
      sp500: getPrefillParam('sp500'),
      ausShares: getPrefillParam('aus_shares', 'australian_shares'),
      intlShares: getPrefillParam('intl_shares', 'international_shares'),
      bonds: getPrefillParam('bonds'),
      cash: getPrefillParam('cash'),
      frankingCredits: getPrefillParam('franking_credits', 'franking')
    };
    if (Object.values(prefillPayload).some((value) => value != null && value !== '')) {
      localStorage.setItem('homepageAdvancedPrefill', JSON.stringify(prefillPayload));
    }
    
    // Check for Monte Carlo demo redirect
    if (urlParams.get('demo') === 'advanced' && urlParams.get('from') === 'monte_carlo') {
      console.log('🎯 Monte Carlo demo redirect detected in App_advanced_min.js');
      
      // Set the example to load (this will be picked up by the other useEffect)
      localStorage.setItem('loadExample', 'single-comprehensive');
      console.log('🎯 Set loadExample in localStorage');
      
      setIsFromMonteCarloDemo(true);
      
      // Wait longer to ensure scenario is fully loaded before showing popup
      setTimeout(() => {
        console.log('🎯 Showing instructional popup for Monte Carlo demo');
        trackAdvancedDemoShown({ surface: 'monte_carlo_demo_popup', calculatorTier: 'advanced' });
        setShowInstructionalPopup(true);
      }, 2500); // Increased from 1500 to 2500ms to allow scenario to load
    }
  }, []);

  // Close export menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const {
    relationshipStatus,
    setRelationshipStatus,
    yourGender,
    setYourGender,
    yourAge,
    setYourAge,
    yourSuper,
    setYourSuper,
    yourTbcUsed,
    setYourTbcUsed,
    partnerGender,
    setPartnerGender,
    partnerSuper,
    setPartnerSuper,
    partnerAge,
    setPartnerAge,
    partnerTbcUsed,
    setPartnerTbcUsed,
    otherTaxableIncome,
    setOtherTaxableIncome,
    yourPartTimeEmploymentIncome,
    setYourPartTimeEmploymentIncome,
    partnerPartTimeEmploymentIncome,
    setPartnerPartTimeEmploymentIncome,
    isHomeowner,
    setIsHomeowner,
    rentPaidFortnightly,
    setRentPaidFortnightly,
    otherNonTaxableIncome,
    setOtherNonTaxableIncome,
    otherAssets,
    setOtherAssets,
    includePension,
    setIncludePension,
    startYear,
    setStartYear,
    periodLength,
    setPeriodLength,
    targetFinalBalance,
    setTargetFinalBalance,
    pensionGrowthRate,
    setPensionGrowthRate,
    generalTransferBalanceCap,
    setGeneralTransferBalanceCap,
    hasPrivateHealthInsurance,
    setHasPrivateHealthInsurance,
    partnerHasPrivateHealthInsurance,
    setPartnerHasPrivateHealthInsurance,
    adminFees,
    setAdminFees,
    adminFeePercentage,
    setAdminFeePercentage,
    investmentFees,
    setInvestmentFees,
    investmentFeeCap,
    setInvestmentFeeCap,
    transactionCosts,
    setTransactionCosts,
    insuranceFees,
    setInsuranceFees,
    sp500,
    setSp500,
    ausShares,
    setAusShares,
    intlShares,
    setIntlShares,
    bonds,
    setBonds,
    cash,
    setCash,
    frankingCredits,
    setFrankingCredits,
    lastResultIncome,
    setLastResultIncome,
    lastResultSurvival,
    setLastResultSurvival,
    annuityPurchasePrice,
    setAnnuityPurchasePrice,
    annuityAnnualPayment,
    setAnnuityAnnualPayment,
    annuityIsIndexed,
    setAnnuityIsIndexed,
    definedBenefitPensionAmount,
    setDefinedBenefitPensionAmount,
    definedBenefitIsIndexed,
    setDefinedBenefitIsIndexed,
    withdrawalStrategy,
    setWithdrawalStrategy,
    monteCarloTargetIncome,
    setMonteCarloTargetIncome,
    // Phased Retirement
    phasedRetirement,
    setPhasedRetirement,
    yourRetirementAge,
    setYourRetirementAge,
    partnerRetirementAge,
    setPartnerRetirementAge,
    partnerSalary,
    setPartnerSalary,
    partnerSuperContributions,
    setPartnerSuperContributions,
    yourSalary,
    setYourSalary,
    yourSuperContributions,
    setYourSuperContributions,
    msiStartsWhenEitherRetires,
    setMsiStartsWhenEitherRetires,
    // Super withdrawal age control
    allowWithdrawalsBeforeRetirementAge,
    setAllowWithdrawalsBeforeRetirementAge,
    enableBridgeStrategy,
    setEnableBridgeStrategy,
    yourSuperAccessAge,
    setYourSuperAccessAge,
    partnerSuperAccessAge,
    setPartnerSuperAccessAge,
    bridgeCashBucket,
    setBridgeCashBucket,
    bridgeInvestmentBucket,
    setBridgeInvestmentBucket,
    bridgeInvestmentReturnRate,
    setBridgeInvestmentReturnRate,
    enableMortgageComparison,
    setEnableMortgageComparison,
    mortgageBalance,
    setMortgageBalance,
    mortgageInterestRate,
    setMortgageInterestRate,
    mortgageAnnualRepayment,
    setMortgageAnnualRepayment,
    mortgageStrategy,
    setMortgageStrategy,
    mortgagePayoffSource,
    setMortgagePayoffSource,
    mortgagePayoffScheduleType,
    setMortgagePayoffScheduleType,
    mortgagePayoffScheduleValue,
    setMortgagePayoffScheduleValue,
    mortgagePartialPayoffAmount,
    setMortgagePartialPayoffAmount,
    lumpSumWithdrawals,
    setLumpSumWithdrawals,
    lumpSumContributions,
    setLumpSumContributions,
    // Alternative Assets from SMSF
    transferFromSMSF,
    setTransferFromSMSF,
    residentialPropertyValue,
    setResidentialPropertyValue,
    residentialRentalIncome,
    setResidentialRentalIncome,
    residentialPropertyRentalYield,
    setResidentialPropertyRentalYield,
    residentialPropertyCapitalGrowth,
    setResidentialPropertyCapitalGrowth,
    commercialPropertyValue,
    setCommercialPropertyValue,
    commercialRentalIncome,
    setCommercialRentalIncome,
    commercialPropertyRentalYield,
    setCommercialPropertyRentalYield,
    commercialPropertyCapitalGrowth,
    setCommercialPropertyCapitalGrowth,
    collectiblesValue,
    setCollectiblesValue,
    collectiblesCapitalGrowth,
    setCollectiblesCapitalGrowth,
    otherAlternativesValue,
    setOtherAlternativesValue,
    otherAlternativesCapitalGrowth,
    setOtherAlternativesCapitalGrowth,
    infrastructurePct,
    setInfrastructurePct,
    infrastructureValue,
    setInfrastructureValue,
    infrastructureReturnRate,
    setInfrastructureReturnRate,
    assetDetails,
    setAssetDetails,
    // Earning vs Non-Earning Aggregation from AdvancedState
    earningAssetsValue,
    setEarningAssetsValue,
    nonEarningAssetsValue,
    setNonEarningAssetsValue,
    earningAssetsPct,
    setEarningAssetsPct,
    nonEarningAssetsPct,
    setNonEarningAssetsPct,
  } = useAdvancedState();

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showHowThisWorks, setShowHowThisWorks] = useState(false);
  const [showAnnuity, setShowAnnuity] = useState(false);
  const [showAlternativeAssets, setShowAlternativeAssets] = useState(false);
  const [showFees, setShowFees] = useState(false);
  const [showAssetAllocation, setShowAssetAllocation] = useState(false);
  const [showBridgeStrategy, setShowBridgeStrategy] = useState(false);
  const [showMortgageComparison, setShowMortgageComparison] = useState(false);
  const [showLumpSumWithdrawals, setShowLumpSumWithdrawals] = useState(false);
  const [showLumpSumEntryRow, setShowLumpSumEntryRow] = useState(false);
  const [showLumpSumContributions, setShowLumpSumContributions] = useState(false);
  const [showLumpSumContributionEntryRow, setShowLumpSumContributionEntryRow] = useState(false);
  const lumpSumWithdrawalsRef = useRef(lumpSumWithdrawals);
  const lumpSumContributionsRef = useRef(lumpSumContributions);

  const updateLumpSumWithdrawals = useCallback((updater) => {
    setLumpSumWithdrawals((prev) => {
      const raw = typeof updater === 'function' ? updater(prev) : updater;
      const next = normalizeLumpSumWithdrawalRows(raw);
      lumpSumWithdrawalsRef.current = next;
      return next;
    });
  }, [setLumpSumWithdrawals]);

  const updateLumpSumContributions = useCallback((updater) => {
    setLumpSumContributions((prev) => {
      const raw = typeof updater === 'function' ? updater(prev) : updater;
      const next = normalizeLumpSumContributionRows(raw);
      lumpSumContributionsRef.current = next;
      return next;
    });
  }, [setLumpSumContributions]);

  useEffect(() => {
    lumpSumWithdrawalsRef.current = lumpSumWithdrawals;
  }, [lumpSumWithdrawals]);

  useEffect(() => {
    lumpSumContributionsRef.current = lumpSumContributions;
  }, [lumpSumContributions]);

  useEffect(() => {
    if (hasLumpSumWithdrawals({ lumpSumWithdrawals })) {
      setShowLumpSumWithdrawals(true);
    }
    if (hasLumpSumContributions({ lumpSumContributions })) {
      setShowLumpSumContributions(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAllLumpSumWithdrawals = useCallback(() => {
    const emptyRows = defaultLumpSumWithdrawalRows();
    lumpSumWithdrawalsRef.current = emptyRows;
    setLumpSumWithdrawals(emptyRows);
    setShowLumpSumEntryRow(false);
    setShowLumpSumWithdrawals(false);
  }, [setLumpSumWithdrawals]);

  const clearAllLumpSumContributions = useCallback(() => {
    const emptyRows = defaultLumpSumContributionRows();
    lumpSumContributionsRef.current = emptyRows;
    setLumpSumContributions(emptyRows);
    setShowLumpSumContributionEntryRow(false);
    setShowLumpSumContributions(false);
  }, [setLumpSumContributions]);
  // Demo mode: only for free users who have exhausted their free runs
  const [isDemoMode, setIsDemoMode] = useState(() => {
    if (SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.ADVANCED_CALCULATOR)) return false;
    return !SubscriptionManager.canRunAdvancedFree().canRun;
  });

  const buildAdvancedScenarioStateSetters = () => ({
    setRelationshipStatus,
    setYourGender,
    setYourAge,
    setYourSuper,
    setYourTbcUsed,
    setPartnerGender,
    setPartnerAge,
    setPartnerSuper,
    setPartnerTbcUsed,
    setOtherTaxableIncome,
    setYourPartTimeEmploymentIncome,
    setPartnerPartTimeEmploymentIncome,
    setRentPaidFortnightly,
    setOtherNonTaxableIncome,
    setOtherAssets,
    setIsHomeowner,
    setIncludePension,
    setPensionGrowthRate,
    setStartYear,
    setPeriodLength,
    setTargetFinalBalance,
    setAdminFees,
    setAdminFeePercentage,
    setInvestmentFees,
    setInvestmentFeeCap,
    setTransactionCosts,
    setInsuranceFees,
    setSp500,
    setAusShares,
    setIntlShares,
    setBonds,
    setCash,
    setFrankingCredits,
    setPhasedRetirement,
    setShowAdvanced,
    setYourRetirementAge,
    setYourSalary,
    setYourSuperContributions,
    setPartnerRetirementAge,
    setPartnerSalary,
    setPartnerSuperContributions,
    setAllowWithdrawalsBeforeRetirementAge,
    setWithdrawalStrategy,
    setMonteCarloTargetIncome,
    setAnnuityPurchasePrice,
    setAnnuityAnnualPayment,
    setAnnuityIsIndexed,
    setDefinedBenefitPensionAmount,
    setDefinedBenefitIsIndexed,
    setTransferFromSMSF,
    setResidentialPropertyValue,
    setResidentialRentalIncome,
    setResidentialPropertyRentalYield,
    setResidentialPropertyCapitalGrowth,
    setCommercialPropertyValue,
    setCommercialRentalIncome,
    setCommercialPropertyRentalYield,
    setCommercialPropertyCapitalGrowth,
    setCollectiblesValue,
    setCollectiblesCapitalGrowth,
    setOtherAlternativesValue,
    setOtherAlternativesCapitalGrowth,
    setInfrastructurePct,
    setInfrastructureValue,
    setInfrastructureReturnRate,
    setAssetDetails,
    setShowAnnuity,
    setShowAlternativeAssets,
    setHasPrivateHealthInsurance,
    setPartnerHasPrivateHealthInsurance,
  });

  const handleLoadExampleScenario = (scenario, options = {}) => {
    if (!scenario?.id) return false;
    const hasAccess = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.ADVANCED_CALCULATOR);
    const freeStatus = SubscriptionManager.canRunAdvancedFree();
    // Lock only after free runs are used — samples stay editable while a free run remains.
    const { lockForFreeUsers = !hasAccess && !freeStatus.canRun } = options;
    const ok = loadScenario(scenario.id, buildAdvancedScenarioStateSetters());
    if (ok && lockForFreeUsers && !hasAccess) {
      setIsDemoMode(true);
      setShowHistorical(false);
      setShowMonteCarlo(false);
    } else if (ok) {
      setIsDemoMode(false);
    }
    return ok;
  };
  
    // Lock inputs when free runs exhausted; load a specific example if requested via blog CTA
  useEffect(() => {
    const hasSubscription = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.ADVANCED_CALCULATOR);
    let exampleToLoad = localStorage.getItem('loadExample');
    let editableLoadedExample = localStorage.getItem('loadExampleEditable') === '1';
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    const hashParams = hash.includes('?') ? new URLSearchParams(hash.split('?')[1]) : new URLSearchParams();
    const article = urlParams.get('article') || hashParams.get('article') || '';
    const source = urlParams.get('source') || hashParams.get('source') || '';
    const isAdvancedEntry = hash.startsWith('#advanced');
    const prefillKeys = [
      'age', 'your_age', 'balance', 'super', 'your_super', 'retirement_age', 'retire_age',
      'target_income', 'income', 'spending', 'status', 'relationship', 'homeowner', 'is_homeowner',
      'partner_age', 'partner_balance', 'partner_super', 'partner_retirement_age', 'partner_retire_age',
      'partner_salary', 'salary', 'your_salary', 'other_assets', 'other_income', 'other_taxable_income', 'other_taxable',
      'start_year', 'start', 'period', 'years',
      'phased', 'phased_retirement', 'case_label', 'case', 'pension_growth', 'pension_growth_rate',
      'investment_fees', 'investment_fee', 'sp500', 'aus_shares', 'australian_shares', 'intl_shares',
      'international_shares', 'bonds', 'cash', 'franking_credits', 'franking'
    ];
    const hasExplicitPrefill = prefillKeys.some((key) => urlParams.has(key) || hashParams.has(key));
    const hasSavedAdvancedState = !!localStorage.getItem('advanced_calculator_state');
    let prefillCaseLabel = '';
    try {
      const pendingPrefill = JSON.parse(localStorage.getItem('homepageAdvancedPrefill') || '{}');
      prefillCaseLabel = pendingPrefill.caseLabel || '';
    } catch {
      prefillCaseLabel = '';
    }

    // Lock inputs if this free user has used all their runs
    const freeRunStatus = SubscriptionManager.canRunAdvancedFree();
    if (!hasSubscription && !freeRunStatus.canRun) {
      setIsDemoMode(true);
    }

    if (
      !exampleToLoad &&
      !hasSubscription &&
      isAdvancedEntry &&
      !hasExplicitPrefill &&
      !hasSavedAdvancedState &&
      /blog|calculators_index|app_disambiguation/.test(source)
    ) {
      exampleToLoad = scenarioForOrganicEntry(article);
      editableLoadedExample = true;
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'example_scenario_auto_load', {
          event_category: 'activation',
          source,
          article,
          scenario_id: exampleToLoad,
          calculator_tier: 'advanced'
        });
      }
    }

    // If a specific example was requested (e.g. from a blog CTA), load it
    if (exampleToLoad) {
      const shouldLockLoadedExample = !editableLoadedExample || (!hasSubscription && !freeRunStatus.canRun);
      handleLoadExampleScenario({ id: exampleToLoad }, { lockForFreeUsers: shouldLockLoadedExample });
      if (editableLoadedExample && !shouldLockLoadedExample) {
        const loadedScenario = getScenario(exampleToLoad);
        setEntryScenarioNotice({
          name: prefillCaseLabel || loadedScenario?.name || 'example scenario',
          source: source || 'blog'
        });
      }
      localStorage.removeItem('loadExample');
      localStorage.removeItem('loadExampleEditable');
    }

    const homepagePrefill = localStorage.getItem('homepageAdvancedPrefill');
    if (homepagePrefill) {
      try {
        const parsed = JSON.parse(homepagePrefill);
        const numberOrNull = (value) => {
          const n = Number(value);
          return Number.isFinite(n) ? n : null;
        };
        const boolOrNull = (value) => {
          if (value == null || value === '') return null;
          if (/^(1|true|yes|y)$/i.test(String(value))) return true;
          if (/^(0|false|no|n)$/i.test(String(value))) return false;
          return null;
        };
        const normalizedStatus = String(parsed.status || '').toLowerCase();
        const isCouplePrefill = ['couple', 'partnered', 'married'].includes(normalizedStatus) || !!parsed.partnerAge || !!parsed.partnerBalance;
        const ageValue = numberOrNull(parsed.age);
        const retirementAgeValue = numberOrNull(parsed.retirementAge);
        const balanceValue = numberOrNull(parsed.balance);
        const targetIncomeValue = numberOrNull(parsed.targetIncome);
        const partnerAgeValue = numberOrNull(parsed.partnerAge);
        const partnerBalanceValue = numberOrNull(parsed.partnerBalance);
        const partnerRetirementAgeValue = numberOrNull(parsed.partnerRetirementAge);
        const salaryValue = numberOrNull(parsed.salary);
        const partnerSalaryValue = numberOrNull(parsed.partnerSalary);
        const otherAssetsValue = numberOrNull(parsed.otherAssets);
        const otherTaxableIncomeValue = numberOrNull(parsed.otherTaxableIncome);
        const startYearValue = numberOrNull(parsed.startYear);
        const periodLengthValue = numberOrNull(parsed.periodLength);
        const pensionGrowthRateValue = numberOrNull(parsed.pensionGrowthRate);
        const investmentFeesValue = numberOrNull(parsed.investmentFees);
        const investmentFeeCapValue = numberOrNull(parsed.investmentFeeCap);
        const sp500Value = numberOrNull(parsed.sp500);
        const ausSharesValue = numberOrNull(parsed.ausShares);
        const intlSharesValue = numberOrNull(parsed.intlShares);
        const bondsValue = numberOrNull(parsed.bonds);
        const cashValue = numberOrNull(parsed.cash);
        const frankingCreditsValue = numberOrNull(parsed.frankingCredits);
        const homeownerValue = boolOrNull(parsed.homeowner);
        const phasedValue = boolOrNull(parsed.phased);

        if (ageValue != null) setYourAge(ageValue);
        if (retirementAgeValue != null) setYourRetirementAge(retirementAgeValue);
        if (balanceValue != null) setYourSuper(balanceValue);
        if (targetIncomeValue != null) setMonteCarloTargetIncome(Math.max(0, Math.round(targetIncomeValue)));
        if (salaryValue != null) setYourSalary(salaryValue);
        if (otherAssetsValue != null) setOtherAssets(otherAssetsValue);
        if (otherTaxableIncomeValue != null) setOtherTaxableIncome(otherTaxableIncomeValue);
        if (startYearValue != null) setStartYear(startYearValue);
        if (periodLengthValue != null) setPeriodLength(periodLengthValue);
        if (pensionGrowthRateValue != null) setPensionGrowthRate(pensionGrowthRateValue);
        if (investmentFeesValue != null) setInvestmentFees(investmentFeesValue);
        if (investmentFeeCapValue != null) setInvestmentFeeCap(Math.max(0, investmentFeeCapValue));
        if (sp500Value != null) setSp500(sp500Value);
        if (ausSharesValue != null) setAusShares(ausSharesValue);
        if (intlSharesValue != null) setIntlShares(intlSharesValue);
        if (bondsValue != null) setBonds(bondsValue);
        if (cashValue != null) setCash(cashValue);
        if (frankingCreditsValue != null) setFrankingCredits(frankingCreditsValue);
        if (homeownerValue != null) setIsHomeowner(homeownerValue);
        if (phasedValue != null) setPhasedRetirement(phasedValue);

        if (ageValue != null || retirementAgeValue != null || balanceValue != null || targetIncomeValue != null || parsed.status || parsed.partnerAge || parsed.partnerBalance) {
          setRelationshipStatus(isCouplePrefill ? 'couple' : 'single');
          setPartnerAge('');
          setPartnerSuper('');
          setPartnerRetirementAge('');
          setOtherTaxableIncome('');
          setOtherNonTaxableIncome('');
          setIncludePension(true);
        }
        if (isCouplePrefill) {
          if (partnerAgeValue != null) setPartnerAge(partnerAgeValue);
          if (partnerBalanceValue != null) setPartnerSuper(partnerBalanceValue);
          if (partnerRetirementAgeValue != null) setPartnerRetirementAge(partnerRetirementAgeValue);
          if (partnerSalaryValue != null) setPartnerSalary(partnerSalaryValue);
        }
      } catch (e) {
        console.warn('Failed to apply homepage advanced prefill:', e);
      } finally {
        localStorage.removeItem('homepageAdvancedPrefill');
      }
    }
  }, []);

  // No useEffect needed - we call runAdvancedProjection() directly from popup button
  
  // Recompute non-earning assets % on desktop Advanced from SMSF transfer values
  // The SMSF handoff provided nonEarningAssetsPct relative to productive assets; for the Advanced UI
  // we want non-earning as a share of total (earning + non-earning). Recalculate once data is available.
  useEffect(() => {
    if (!transferFromSMSF) return;
    const earningVal = Number(earningAssetsValue || 0);
    const nonEarningVal = Number(nonEarningAssetsValue || 0);
    const total = earningVal + nonEarningVal;
    if (total <= 0) return;
    const recomputedPct = (nonEarningVal / total) * 100;
    if (Math.abs((Number(nonEarningAssetsPct || 0)) - recomputedPct) > 0.1) {
      setNonEarningAssetsPct(Number(recomputedPct.toFixed(1)));
    }
  }, [transferFromSMSF, earningAssetsValue, nonEarningAssetsValue]);

  // Hydration gate to avoid initial flicker/reset when transfer just occurred
  // Wait briefly for AdvancedState to apply transfer data before rendering full UI
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setIsHydrated(true), 150);
    return () => clearTimeout(t);
  }, [transferFromSMSF]);
  
  // Auto-show Alternative Assets section when SMSF data is transferred
  useEffect(() => {
    if (transferFromSMSF || residentialPropertyValue || commercialPropertyValue || collectiblesValue || otherAlternativesValue || infrastructureValue) {
      setShowAlternativeAssets(true);
    }
  }, [transferFromSMSF, residentialPropertyValue, commercialPropertyValue, collectiblesValue, otherAlternativesValue, infrastructureValue]);

  // Normalize earning allocations to ensure total portfolio (earning + non-earning) = 100
  // Rule: productive (earning) assets are scaled to fill 100% minus non-earning share
  useEffect(() => {
    // When coming from SMSF, portfolio weights are already valid and should NOT be normalized here.
    // SMSF provides sp500/ausShares/intlShares/bonds/cash (productive) and separate non-earning values.
    if (transferFromSMSF) return;
    // Compute non-earning % of total portfolio (property + collectibles + other alternatives)
    const totalSuperBalance = Math.max(0, Number(yourSuper||0) + Number(partnerSuper||0));
    if (totalSuperBalance <= 0) return;
    const nonEarningValue = Number(residentialPropertyValue||0) + Number(commercialPropertyValue||0) + Number(collectiblesValue||0) + Number(otherAlternativesValue||0);
    const nonEarningPct = (nonEarningValue / totalSuperBalance) * 100;
    const targetEarningTotal = Math.max(0, 100 - nonEarningPct);

    const wSp = Number(sp500||0);
    const wAu = Number(ausShares||0);
    const wIn = Number(intlShares||0);
    const wBo = Number(bonds||0);
    const wCa = Number(cash||0);
    const currentEarningTotal = wSp + wAu + wIn + wBo + wCa;
    if (currentEarningTotal <= 0) return;

    // Only normalize if the total is significantly over 100% (not under)
    // This allows users to build up their allocation without interference
    // Only rescale when total exceeds target by more than 5% to avoid interfering with manual entry
    if (currentEarningTotal > targetEarningTotal + 5) {
      const scale = targetEarningTotal / currentEarningTotal;
      setSp500(wSp * scale);
      setAusShares(wAu * scale);
      setIntlShares(wIn * scale);
      setBonds(wBo * scale);
      setCash(wCa * scale);
    }
  }, [
    // totals that influence normalization
    yourSuper, partnerSuper,
    residentialPropertyValue, commercialPropertyValue, collectiblesValue, otherAlternativesValue,
    sp500, ausShares, intlShares, bonds, cash
  ]);
  
  // Ref for the Run button to enable auto-scrolling
  const runButtonRef = useRef(null);
  
  // Helper function to auto-scroll to Run button (mobile-friendly)
  const scrollToRunButton = () => {
    scrollToElement(runButtonRef, { delay: 100, offset: 60 });
  };

  // Check if inputs should be locked:
  // - Always locked in demo mode (free runs exhausted)
  // - Always locked without a subscription AND no free runs remain
  // - Always locked on SMSF transfer (inputs come pre-filled from SMSF)
  const hasAdvancedAccess = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.ADVANCED_CALCULATOR);
  const inputsLocked = isDemoMode || (!hasAdvancedAccess && !SubscriptionManager.canRunAdvancedFree().canRun) || transferFromSMSF;

  // Helper function to create onChange handlers that respect input locking
  const createLockedOnChange = (setter) => {
    return (e) => {
      if (inputsLocked) {
        // Show the email capture modal when user tries to modify inputs but has no runs left
        setShowInstructionalPopup(true);
        // Scroll to top to show the modal
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (typeof e === 'object' && e.target) {
        // Handle event object
        if (e.target.type === 'checkbox') {
          setter(e.target.checked);
        } else {
          setter(e.target.value);
        }
      } else {
        // Handle direct value
        setter(e);
      }
    };
  };

  // Custom handler for strategy changes - prevent scroll jump
  const handleStrategyChange = (newStrategy) => {
    // Blur any focused element to prevent browser from trying to keep it in view
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    
    // Check if trying to select a dynamic strategy when either partner is below preservation age (60)
    const dynamicStrategies = ['DynamicSWR', 'VanguardGuardrails', 'FloorAndCeiling'];
    const isDynamicStrategy = dynamicStrategies.includes(newStrategy);
    
    if (isDynamicStrategy) {
      const isCouple = relationshipStatus === 'couple';
      const yourAgeNum = Number(yourAge) || 0;
      const partnerAgeNum = Number(partnerAge) || 0;
      const preservationAge = 60;
      
      // Check if either partner is below preservation age
      const youBelowPreservation = yourAgeNum < preservationAge;
      const partnerBelowPreservation = isCouple && partnerAgeNum < preservationAge;
      
      if (youBelowPreservation || partnerBelowPreservation) {
        const belowPreservationPartners = [];
        if (youBelowPreservation) belowPreservationPartners.push('You');
        if (partnerBelowPreservation) belowPreservationPartners.push('Your partner');
        
        alert(
          `Dynamic withdrawal strategies (${newStrategy}) can only be used when both partners have reached preservation age (60).\n\n` +
          `${belowPreservationPartners.join(' and ')} ${belowPreservationPartners.length === 1 ? 'is' : 'are'} below preservation age.\n\n` +
          `Please use "Fixed Sustainable Income" strategy until both partners reach age 60.`
        );
        return; // Prevent strategy change
      }
    }
    
    setWithdrawalStrategy(newStrategy);
  };
  const bridgeStrategyApplies = String(withdrawalStrategy || 'fixed').toLowerCase() === 'fixed';

  // Helper function to create onChange handlers with auto-scroll
  const createOnChangeWithScroll = (setter) => {
    return (e) => {
      setter(e.target.value);
      scrollToRunButton();
    };
  };
  const [showHistorical, setShowHistorical] = useState(true);
  const [showMonteCarlo, setShowMonteCarlo] = useState(false);
  
  // B2B Infrastructure State
  const [b2bMode, setB2bMode] = useState(false);
  const [tenantId, setTenantId] = useState('default');
  const [tenantConfig, setTenantConfig] = useState({
    name: 'Advanced Retirement Calculator',
    logo: null,
    primaryColor: '#2563eb',
    secondaryColor: '#10b981',
    customDomain: null,
    features: {
      monteCarlo: true,
      historicalAnalysis: true,
      exportData: true,
      apiAccess: false
    },
    branding: {
      showPoweredBy: true,
      customFooter: null,
      customHeader: null
    }
  });
  
  // User Management State
  const [currentUser, setCurrentUser] = useState({
    id: null,
    email: null,
    role: 'guest',
    tenantId: 'default',
    permissions: {
      view: true,
      calculate: true,
      export: false,
      admin: false
    }
  });
  
  // API State
  const [apiKey, setApiKey] = useState(null);
  const [apiRequests, setApiRequests] = useState(0);
  const [apiLimit, setApiLimit] = useState(100);
  
  const resultsRef = useRef(null);
  const [mcSuccessRate, setMcSuccessRate] = useState(null);
  const [isMonteCarloRunning, setIsMonteCarloRunning] = useState(false);
  const monteCarloRunIdRef = useRef(0);
  const [historicalResults, setHistoricalResults] = useState({
    sustainableIncome: 0,
    finalRealBalance: 0,
  });
  const [historicalSeries, setHistoricalSeries] = useState([]);
  const [mortgageComparisonResults, setMortgageComparisonResults] = useState([]);
  const [rollingMSI, setRollingMSI] = useState([]);
  const currentStrategyMSIRef = useRef(0); // Use ref instead of state to avoid stale closure values
  // Survives Stripe cancel / result wipe — feed last successful headline into paywall copy.
  const paywallIncome =
    (Number(historicalResults?.sustainableIncome) > 0
      ? Number(historicalResults.sustainableIncome)
      : null) ||
    (Number(currentStrategyMSIRef?.current) > 0 ? Number(currentStrategyMSIRef.current) : null) ||
    (Number(lastResultIncome) > 0 ? Number(lastResultIncome) : null);
  const paywallSurvival =
    mcSuccessRate != null
      ? mcSuccessRate
      : lastResultSurvival != null
        ? lastResultSurvival
        : null;

  useEffect(() => {
    if (mcSuccessRate == null) return;
    setLastResultSurvival(mcSuccessRate);
  }, [mcSuccessRate, setLastResultSurvival]);

  const lastRunStrategyRef = useRef('fixed');
  const lastRunInputsRef = useRef(null);
  const lastReturnsByYearRef = useRef(null);
  const computeFloorCeilingSeriesRef = useRef(null); // Store series function for What If access
  const computeVanguardGuardrailsSeriesRef = useRef(null); // Store series function for What If access
  const computeDynamicSWRSeriesRef = useRef(null); // Store DynamicSWR series function for What If access
  const computeFixedSeriesRef = useRef(null); // Store Fixed MSI function for lazy strategy comparison

  const scrollResultsIntoViewInstant = () => {
    // Scroll to the TOP of results (summary cards), not the charts — scrolling to
    // the chart chops off Safe Income / eligibility / timeline above it.
    const target =
      document.getElementById('simulation-results')
      || resultsRef.current
      || document.getElementById('balance-chart-container');
    if (!target) return false;
    try {
      target.scrollIntoView({ block: 'start', behavior: 'auto' });
    } catch (_) {
      /* older browsers */
    }
    const yOffset = window.pageYOffset || document.documentElement.scrollTop || 0;
    const top = Math.max(0, target.getBoundingClientRect().top + yOffset - 80);
    // Direct scrollTop assignment bypasses CSS scroll-behavior: smooth.
    const se = document.scrollingElement || document.documentElement;
    if (se) se.scrollTop = top;
    if (document.body) document.body.scrollTop = top;
    window.scrollTo(0, top);
    return true;
  };

  const scheduleDeferredMonteCarlo = (mcInputs) => {
    if (!mcInputs) {
      setIsMonteCarloRunning(false);
      return;
    }
    const mcRunId = ++monteCarloRunIdRef.current;
    setIsMonteCarloRunning(true);
    // Wait until charts + the rest of the results tree have painted, then run MC.
    // Starting MC as soon as chart curves appear freezes the main thread and leaves
    // the yearly table / rolling / what-if sections unpainted until MC finishes.
    // Note: charts/table/what-if live under `.advanced-calculator-results`, NOT only
    // inside `#simulation-results` (that wrapper closes before the full-width block).
    const waitForResultsPaint = () => new Promise((resolve) => {
      const started = performance.now();
      const tick = () => {
        const root = document.querySelector('.advanced-calculator-results')
          || document.getElementById('simulation-results')?.parentElement
          || document.body;
        const chartPainted = !!document.querySelector(
          '#balance-chart-container .recharts-area-curve, #balance-chart-container .recharts-line-curve, #balance-chart-container path[class*="recharts"]'
        );
        const yearlyHeading = [...document.querySelectorAll('h5')].find((h) => /Detailed Yearly Data/i.test(h.textContent || ''));
        const yearlyRows = yearlyHeading
          ? yearlyHeading.parentElement?.querySelectorAll('table tbody tr').length || 0
          : 0;
        const whatIfEl = [...document.querySelectorAll('h5')].find((h) => /What If/i.test(h.textContent || ''));
        const rollingReady = !!document.getElementById('rolling-period-chart-container');
        const ready = chartPainted && yearlyRows > 0 && (whatIfEl || rollingReady);
        if (ready || performance.now() - started > 1200) {
          // Force layout of the bottom of the results tree, then yield so the
          // browser can paint long yearly tables before MC blocks the main thread.
          try {
            (whatIfEl || document.getElementById('rolling-period-chart-container') || root)?.getBoundingClientRect();
          } catch (_) { /* ignore */ }
          setTimeout(() => {
            const finish = () => requestAnimationFrame(() => requestAnimationFrame(resolve));
            if (typeof requestIdleCallback === 'function') {
              requestIdleCallback(finish, { timeout: 250 });
            } else {
              setTimeout(finish, 80);
            }
          }, 0);
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
    waitForResultsPaint().then(async () => {
      if (monteCarloRunIdRef.current !== mcRunId) return;
      try {
        // Chunked async MC — yields between trial batches so the page can paint
        // and scroll (top and bottom) while survival % is still calculating.
        const mcResults = await runMonteCarloSimulationAsync(mcInputs, {
          shouldCancel: () => monteCarloRunIdRef.current !== mcRunId,
        });
        if (monteCarloRunIdRef.current !== mcRunId) return;
        setMcPercentiles(mcResults.balancePercentiles);
        setMcIncomePercentiles(mcResults.incomePercentiles);
        setMcSuccessRate(mcResults.successRate);
        setMcMedianIncomesSummary(mcResults.medianIncomesSummary);
      } catch (error) {
        if (monteCarloRunIdRef.current !== mcRunId) return;
        console.error('Monte Carlo calculation error:', error);
        console.error('Error stack:', error.stack);
        setMonteCarloResults({
          successRates: {},
          finalBalancePercentiles: {},
          expectedIncomeRanges: {}
        });
      } finally {
        if (monteCarloRunIdRef.current === mcRunId) {
          setIsMonteCarloRunning(false);
        }
      }
    });
  };
  
  
  const [mcPercentiles, setMcPercentiles] = useState(null);
  const [mcIncomePercentiles, setMcIncomePercentiles] = useState(null);
  const [mcMedianIncomesSummary, setMcMedianIncomesSummary] = useState(null);
  const [monteCarloResults, setMonteCarloResults] = useState({
    successRates: {},
    finalBalancePercentiles: {},
    expectedIncomeRanges: {}
  });
  const [historicalAvgIncome, setHistoricalAvgIncome] = useState(0);
  const [lastSaved, setLastSaved] = useState(new Date());
  
  // Update lastSaved whenever any state changes (via localStorage save in AdvancedState)
  useEffect(() => {
    setLastSaved(new Date());
  }, [yourAge, yourSuper, partnerAge, partnerSuper, startYear, periodLength, withdrawalStrategy, monteCarloTargetIncome]);
  
  // Strategy params (controlled)
  const [dynInitPct, setDynInitPct] = useState(4);
  const [vgInitPct, setVgInitPct] = useState(4.5);
  const [vgCutPct, setVgCutPct] = useState(10);
  const [vgIncPct, setVgIncPct] = useState(10);
  const [vgUpperPct, setVgUpperPct] = useState(20);
  const [vgLowerPct, setVgLowerPct] = useState(20);
  // Floor & Ceiling strategy inputs
  const [fcInitPct, setFcInitPct] = useState(5);
  const [fcFloorPct, setFcFloorPct] = useState(4);
  const [fcCeilPct, setFcCeilPct] = useState(6);
  const [whatIfPeriod, setWhatIfPeriod] = useState(0);
  const [whatIfPeriodResult, setWhatIfPeriodResult] = useState(null);
  const [whatIfFeeChangePct, setWhatIfFeeChangePct] = useState('');
  const [whatIfFeeChangeResult, setWhatIfFeeChangeResult] = useState(null);
  const [whatIfSeqRiskPct, setWhatIfSeqRiskPct] = useState('');
  const [whatIfSeqRiskResult, setWhatIfSeqRiskResult] = useState(null);

  // Helper function to validate and sanitize inputs
  const validateInput = (value, min = -Infinity, max = Infinity, defaultValue = 0) => {
    const num = Number(value);
    if (isNaN(num) || num < min || num > max) {
      return defaultValue;
    }
    return num;
  };

  // Helper function to calculate what-if scenarios with error handling
  const calculateWhatIfScenario = async (scenarioName, calculationFn) => {
    try {
      const result = await calculationFn();
      return { success: true, data: result };
    } catch (error) {
      // Error handled silently
      return { success: false, error: error.message };
    }
  };

  // Use the SAME simulation approach as the main calculator
  const calculateMaxSustainableIncomeWithMainCalculatorLogic = (startYear, years, totalSuper, partnerSuper, weights, strategy, customInvestmentFees = null, tolerance = 1000, scenarioType = null) => {
    // Get historical data and calculate returns (same as main calculator)
    let sumR = 0;
    const returnsByYear = [];
    for (let i = 0; i < years; i++) {
      const year = startYear + i;
      const yearData = historicalData[year];
      if (!yearData) {
        // No historical data found
        return 0;
      }
      const portfolio = { 
        sp500: weights.sp500, 
        ausShares: weights.aus, 
        intlShares: weights.intl, 
        bonds: weights.bonds, 
        cash: weights.cash, 
        frankingCredits: weights.frank,
        infrastructurePct: Number(infrastructurePct) || 0,
        infrastructureReturnRate: Number(infrastructureReturnRate) || 0
      };
      const nominalR = Number(SimulationEngine.getPortfolioReturn(yearData, portfolio)) || 0;
      const histInfl = (historicalData[year]?.inflation) || 0;
      const investFee = (customInvestmentFees !== null ? customInvestmentFees : (Number(investmentFees) || 0)) / 100;
      const netR = ((1 + nominalR) / (1 + histInfl)) - 1 - investFee;
      sumR += netR;
      returnsByYear.push({ year, r: netR });
    }

    // Use the same binary search logic as main calculator
    const includeAgePension = !!includePension;
    const homeowner = !!isHomeowner;
    const isCouple = String(relationshipStatus) === 'couple';
    // Rental income is calculated dynamically in each loop iteration at real growth rate
    // DO NOT include it in 'other' - it will be added separately
    const other = (Number(otherTaxableIncome) || 0);
    const otherNonTax = Number(otherNonTaxableIncome) || 0;
    // Include SMSF non-earning assets in assessable assets for Age Pension test when coming from SMSF
    const otherAssetsValue = (() => {
      const base = Number(otherAssets) || 0;
      if (!transferFromSMSF) return base;
      const resProp = Number(residentialPropertyValue) || 0;
      const comProp = Number(commercialPropertyValue) || 0;
      const col = Number(collectiblesValue) || 0;
      const otherAlt = Number(otherAlternativesValue) || 0;
      return base + resProp + comProp + col + otherAlt;
    })();
    const annuityPay = Number(annuityAnnualPayment) || 0;
    const dbPension = Number(definedBenefitPensionAmount) || 0;
    const investFixedFees = (Number(adminFees) || 0) + (Number(insuranceFees) || 0);
    const getYearlyOtherIncome = useCallback(() => {
      try {
        const result = other + otherNonTax + annuityPay + dbPension;
        return result;
      } catch (error) {
        // Error in getYearlyOtherIncome
        return 0;
      }
    }, [other, otherNonTax, annuityPay, dbPension]);

    // Single source of truth for pension calculation - matches basic calculator
    const calculatePensionForYear = useCallback((totalAssets, totalIncome, year = 0) => {
      return calculateAgePensionForHousehold({
        totalAssets,
        otherIncome: totalIncome,
        isCouple,
        isHomeowner: homeowner,
        year,
        pensionGrowthRate,
        ageA: yourAge + year,
        ageB: (partnerAge || yourAge) + year,
        includePension: includeAgePension
      });
    }, [includeAgePension, isCouple, homeowner, pensionGrowthRate, partnerAge, yourAge]);

    // SINGLE SOURCE OF TRUTH: Parameterized function for calculating maximum sustainable income
    const calculateMaxSustainableIncome = (params) => {
      const {
        totalSuper: paramTotalSuper,
        years: paramYears,
        returnsByYear: paramReturnsByYear,
        yourAge: paramYourAge,
        otherAssetsValue: paramOtherAssetsValue,
        otherIncome: paramOtherIncome,
        includeAgePension: paramIncludeAgePension,
        isCouple: paramIsCouple,
        homeowner: paramHomeowner,
        pensionGrowthRate: paramPensionGrowthRate,
        targetFinalBalance: paramTargetFinalBalance,
        adminFees: paramAdminFees,
        adminFeePercentage: paramAdminFeePercentage,
        investmentFees: paramInvestmentFees,
        insuranceFees: paramInsuranceFees,
        partnerAge: paramPartnerAge
      } = params;

      const calculateFees = (balance) => {
        const adminFeesNum = Number(paramAdminFees) || 52;
        const adminFeePercentageNum = Number(paramAdminFeePercentage) || 0.10;
        const investmentFeesNum = Number(paramInvestmentFees) || 0;
        const insuranceFeesNum = Number(paramInsuranceFees) || 0;
        
        // Admin fees: flat fee + percentage
        let adminFeeAmount = adminFeesNum + (balance * adminFeePercentageNum / 100);
        
        // Apply admin fee cap when using AustralianSuper defaults
        if (adminFeesNum === 52 && adminFeePercentageNum === 0.10) {
          adminFeeAmount = Math.min(adminFeeAmount, 350);
        }
        
        // Investment fees: use user's custom rate
        const investmentFeeAmount = balance * (investmentFeesNum / 100);
        
        // Transaction costs: 0.08% (standard) - only apply if fees are not explicitly set to zero
        const transactionCostAmount = (adminFeesNum === 0 && adminFeePercentageNum === 0 && investmentFeesNum === 0 && insuranceFeesNum === 0) 
            ? 0 
            : balance * 0.0008;
        
        return adminFeeAmount + investmentFeeAmount + transactionCostAmount + insuranceFeesNum;
      };

      const isIncomeSustainable = (targetIncome) => {
        // CRITICAL: Separate infrastructure from liquid super (same as main calculation)
        const infrastructureVal = Number(infrastructureValue) || 0;
        const infrastructureRate = Number(infrastructureReturnRate) || 6;
        const liquidSuper = paramTotalSuper - infrastructureVal;
        
        let bal = liquidSuper; // Track liquid super separately
        let infraBal = infrastructureVal; // Track infrastructure separately
        const targetBalance = Number(paramTargetFinalBalance) || 0;
        
        for (let i = 0; i < paramYears; i++) {
          const { year, r } = paramReturnsByYear[i];
          const currentAge = paramYourAge + i;
          
          // Calculate rental income (using fixed 2.5% inflation, same as main calculation)
          const resVal0 = Number(residentialPropertyValue) || 0;
          const resNominalG = (Number(residentialPropertyCapitalGrowth) || 0) / 100;
          const comVal0 = Number(commercialPropertyValue) || 0;
          const comNominalG = (Number(commercialPropertyCapitalGrowth) || 0) / 100;
          const residentialRentalIncomeAnnual = Number(residentialRentalIncome) || 0;
          const commercialRentalIncomeAnnual = Number(commercialRentalIncome) || 0;
          const fixedInflation = 0.025;
          const resRealG = resNominalG - fixedInflation;
          const comRealG = comNominalG - fixedInflation;
          const currentResidentialValue = resVal0 * Math.pow(1 + resRealG, i);
          const currentCommercialValue = comVal0 * Math.pow(1 + comRealG, i);
          const currentResidentialRental = resVal0 > 0 && residentialRentalIncomeAnnual > 0
            ? (residentialRentalIncomeAnnual / resVal0) * currentResidentialValue
            : 0;
          const currentCommercialRental = comVal0 > 0 && commercialRentalIncomeAnnual > 0
            ? (commercialRentalIncomeAnnual / comVal0) * currentCommercialValue
            : 0;
          const rentalIncome = currentResidentialRental + currentCommercialRental;
          
          // Calculate infrastructure dividends
          const infrastructureDividends = infraBal * (infrastructureRate / 100);
          
          const estimatedAssets = paramOtherAssetsValue + Math.max(0, bal) + infraBal;
          const incomeForPension = Math.max(0, paramOtherIncome + rentalIncome + infrastructureDividends);
          const pensionAnnual = calculateAgePensionForHousehold({
            totalAssets: estimatedAssets,
            otherIncome: incomeForPension,
            isCouple: paramIsCouple,
            isHomeowner: paramHomeowner,
            year: i,
            pensionGrowthRate: paramPensionGrowthRate,
            ageA: currentAge,
            ageB: (paramPartnerAge || paramYourAge) + i,
            includePension: paramIncludeAgePension
          });
          const totalOtherIncome = pensionAnnual + paramOtherIncome + rentalIncome + infrastructureDividends;
          const needFromSuper = Math.max(0, targetIncome - totalOtherIncome);
          
          // Check preservation age (60) - can only withdraw if 60+
          const canAccessSuper = currentAge >= 60;
          const totalAvailable = bal + infraBal;
          const actualWithdrawal = canAccessSuper ? Math.min(needFromSuper, totalAvailable) : 0;
          
          // Withdraw from liquid super first, then infrastructure
          let remainingBalance = bal;
          if (actualWithdrawal > 0) {
            if (bal >= actualWithdrawal) {
              remainingBalance = bal - actualWithdrawal;
            } else {
              const fromInfra = actualWithdrawal - bal;
              remainingBalance = 0;
              infraBal = Math.max(0, infraBal - fromInfra);
            }
          }
          
          // Apply market returns ONLY to liquid super
          remainingBalance *= (1 + r);
          
          // Apply fees to total balance (liquid + infrastructure)
          const totalBeforeFees = remainingBalance + infraBal;
          const fees = calculateFees(totalBeforeFees);
          if (totalBeforeFees > 0) {
            const liquidRatio = remainingBalance / totalBeforeFees;
            const infraRatio = infraBal / totalBeforeFees;
            remainingBalance -= fees * liquidRatio;
            infraBal -= fees * infraRatio;
          }
          
          bal = Math.max(0, remainingBalance);
        }
        
        const totalFinalBalance = bal + infraBal;
        return totalFinalBalance >= targetBalance;
      };
      
      // Calculate a more realistic upper bound that includes pension and other income
      const estimatedPension = calculateAgePensionForHousehold({
        totalAssets: paramOtherAssetsValue + paramTotalSuper,
        otherIncome: paramOtherIncome,
        isCouple: paramIsCouple,
        isHomeowner: paramHomeowner,
        year: 0,
        pensionGrowthRate: paramPensionGrowthRate,
        ageA: paramYourAge,
        ageB: paramPartnerAge || paramYourAge,
        includePension: paramIncludeAgePension
      });
      const totalAvailableIncome = estimatedPension + paramOtherIncome;
      const targetBalance = Number(paramTargetFinalBalance) || 0;
      
      let low, high;
      let best = 0;
      if (targetBalance === 0) {
          // For zero balance: find maximum sustainable income (traditional approach)
                        // YOUR STRATEGY: Start from baseline income (outside income + pension entitlement)
                        const baselineIncome = other + otherNonTax + annuityPay + dbPension;
                        const estimatedAssets = otherAssetsValue + totalSuper;
                        const pensionEntitlement = calculateAgePensionForHousehold({
                          totalAssets: estimatedAssets,
                          otherIncome: baselineIncome,
                          isCouple,
                          isHomeowner: homeowner,
                          year: 0,
                          pensionGrowthRate: pensionGrowthRateVal,
                          ageA: yourAge,
                          ageB: partnerAge || yourAge,
                          includePension: includeAgePension
                        });
                        const totalBaselineIncome = baselineIncome + pensionEntitlement;
                        
                        low = Math.max(0, totalBaselineIncome);
          const totalSuperOriginalForBound = (Number(yourSuper) || 0) + (isCoupleVal ? (Number(partnerSuper) || 0) : 0);
          high = baselineIncome + (totalSuperOriginalForBound * 0.30); // Start with 30% of original total super - generous but realistic
          
          let bestIncome = 0;
          const tolerance = 100;
          
          for (let iter = 0; iter < 50; iter++) {
              const mid = (low + high) / 2;
              
              // Use the SAME isIncomeSustainable function that includes infrastructure and rental income
              const sustainable = isIncomeSustainable(mid);
              
              if (sustainable) {
                  bestIncome = mid;
                  low = mid; // Try higher income
              } else {
                  high = mid; // Try lower income
              }
              
              if (Math.abs(high - low) < tolerance) break;
          }
          best = bestIncome;
      } else {
          // For non-zero balance: find income that achieves target balance
          low = 0;
          high = paramTotalSuper + totalAvailableIncome; // Generous upper bound
          
          const tolerance = 100;
          
          for (let iter = 0; iter < 50; iter++) {
              const mid = (low + high) / 2;
              if (isIncomeSustainable(mid)) { 
                  best = mid; 
                  low = mid; 
              } else { 
                  high = mid; 
              }
              if (Math.abs(high - low) < tolerance) break;
          }
      }
      
      return Math.round(best);
    };

    // Use the SAME bounds calculation as the main calculator for consistency
    const otherIncome = other + otherNonTax + annuityPay + dbPension;
    const estimatedPension = calculateAgePensionForHousehold({
      totalAssets: otherAssetsValue + totalSuper,
      otherIncome,
      isCouple,
      isHomeowner: homeowner,
      year: 0,
      pensionGrowthRate,
      ageA: yourAge,
      ageB: partnerAge || yourAge,
      includePension: includeAgePension
    });
    const totalAvailableIncome = estimatedPension + otherIncome;
    // EXACT SAME LOGIC AS MAIN CALCULATOR: Use the same approach for zero target balance
    let low = Math.max(0, totalAvailableIncome);
    let high = totalSuper * 0.15; // Start with 15% of super per year - generous but realistic
    let best = 0;
    const searchTolerance = 100;
    
    // CRITICAL: Use the SAME infrastructure and rental income logic as isIncomeSustainable
    const infrastructureVal = Number(infrastructureValue) || 0;
    const infrastructureRate = Number(infrastructureReturnRate) || 6;
    const liquidSuper = totalSuper - infrastructureVal;
    
    for (let iter = 0; iter < 50; iter++) {
      const mid = (low + high) / 2;
      // Test if this income is sustainable - EXACT same logic as isIncomeSustainable
      let bal = liquidSuper;
      let infraBal = infrastructureVal;
      let sustainable = true;
      
      for (let i = 0; i < years; i++) {
        const { year, r } = returnsByYear[i];
        const currentAge = yourAge + i;
        
        // Calculate rental income (using fixed 2.5% inflation, same as main calculation)
        const resVal0 = Number(residentialPropertyValue) || 0;
        const resNominalG = (Number(residentialPropertyCapitalGrowth) || 0) / 100;
        const comVal0 = Number(commercialPropertyValue) || 0;
        const comNominalG = (Number(commercialPropertyCapitalGrowth) || 0) / 100;
        const residentialRentalIncomeAnnual = Number(residentialRentalIncome) || 0;
        const commercialRentalIncomeAnnual = Number(commercialRentalIncome) || 0;
        const fixedInflation = 0.025;
        const resRealG = resNominalG - fixedInflation;
        const comRealG = comNominalG - fixedInflation;
        const currentResidentialValue = resVal0 * Math.pow(1 + resRealG, i);
        const currentCommercialValue = comVal0 * Math.pow(1 + comRealG, i);
        const currentResidentialRental = resVal0 > 0 && residentialRentalIncomeAnnual > 0
          ? (residentialRentalIncomeAnnual / resVal0) * currentResidentialValue
          : 0;
        const currentCommercialRental = comVal0 > 0 && commercialRentalIncomeAnnual > 0
          ? (commercialRentalIncomeAnnual / comVal0) * currentCommercialValue
          : 0;
        const rentalIncome = currentResidentialRental + currentCommercialRental;
        
        // Calculate infrastructure dividends
        const infrastructureDividends = infraBal * (infrastructureRate / 100);
        
        const estimatedAssets = otherAssetsValue + Math.max(0, bal) + infraBal;
        const incomeForPension = Math.max(0, otherIncome + rentalIncome + infrastructureDividends);
        const pensionAnnual = calculateAgePensionForHousehold({
          totalAssets: estimatedAssets,
          otherIncome: incomeForPension,
          isCouple,
          isHomeowner: homeowner,
          year: i,
          pensionGrowthRate: pensionGrowthRateVal,
          ageA: currentAge,
          ageB: (partnerAge || yourAge) + i,
          includePension: includeAgePension
        });
        const totalOtherIncome = pensionAnnual + otherIncome + rentalIncome + infrastructureDividends;
        const needFromSuper = Math.max(0, mid - totalOtherIncome);
        
        // Check preservation age (60) - can only withdraw if 60+
        const canAccessSuper = currentAge >= 60;
        const totalAvailable = bal + infraBal;
        const actualWithdrawal = canAccessSuper ? Math.min(needFromSuper, totalAvailable) : 0;
        
        // Withdraw from liquid super first, then infrastructure
        if (actualWithdrawal > 0) {
          if (bal >= actualWithdrawal) {
            bal -= actualWithdrawal;
          } else {
            const fromInfra = actualWithdrawal - bal;
            bal = 0;
            infraBal = Math.max(0, infraBal - fromInfra);
          }
        }
        
        // Apply market returns ONLY to liquid super
        bal *= (1 + r);
        
        // Apply fees to total balance (liquid + infrastructure) - use same calculation as isIncomeSustainable
        const totalBeforeFees = bal + infraBal;
        const adminFeesNum = Number(adminFees) || 52;
        const adminFeePercentageNum = Number(adminFeePercentage) || 0.10;
        const investmentFeesNum = Number(investmentFees) || 0;
        const insuranceFeesNum = Number(insuranceFees) || 0;
        let adminFeeAmount = adminFeesNum + (totalBeforeFees * adminFeePercentageNum / 100);
        if (adminFeesNum === 52 && adminFeePercentageNum === 0.10) {
          adminFeeAmount = Math.min(adminFeeAmount, 350);
        }
        const investmentFeeAmount = totalBeforeFees * (investmentFeesNum / 100);
        const transactionCostAmount = (adminFeesNum === 0 && adminFeePercentageNum === 0 && investmentFeesNum === 0 && insuranceFeesNum === 0) 
            ? 0 
            : totalBeforeFees * 0.0008;
        const fees = adminFeeAmount + investmentFeeAmount + transactionCostAmount + insuranceFeesNum;
        if (totalBeforeFees > 0) {
          const liquidRatio = bal / totalBeforeFees;
          const infraRatio = infraBal / totalBeforeFees;
          bal -= fees * liquidRatio;
          infraBal -= fees * infraRatio;
        }
        
        bal = Math.max(0, bal);
        
        // Check if we can maintain this income level
        if (actualWithdrawal < needFromSuper) {
          sustainable = false;
          break;
        }
      }
      if (sustainable) {
        best = mid; 
        low = mid; // Try higher income
      } else { 
        high = mid; // Try lower income
      }
      
      if (Math.abs(high - low) < searchTolerance) break;
    }
    // Add explanation for sequence of returns scenarios
    if (scenarioType === 'sequence_returns') {
    }
    
    return Math.round(best);
  };

  // Helper function to calculate dynamic strategy MSI using binary search for what-if scenarios
  // This mirrors the logic in handleRun but is standalone for use in what-ifs
  // REMOVED: calculateDynamicStrategyBinarySearchMSI
  // What If scenarios now use direct year-by-year simulation with user's input parameters

  // What-If Scenario Handlers
  const handleChangeRetirementPeriod = async () => {
    // Check if main calculation has been run
    if (!currentStrategyMSIRef.current || currentStrategyMSIRef.current === 0) {
      setWhatIfPeriodResult({ success: false, error: 'Please run the main calculation first (click the Run button above)' });
      return;
    }
    
    const validatedPeriod = validateInput(whatIfPeriod, 1, 100, 0);
    if (validatedPeriod <= 0) {
      setWhatIfPeriodResult({ success: false, error: 'Please enter a valid retirement period (1-100 years)' });
      return;
    }

    // Check if the retirement period extends beyond available historical data
    const start = Number(startYear) || 1980;
    const endYear = start + validatedPeriod - 1;
    
    // Dynamically find the last year in the historical data
    const dataYears = Object.keys(historicalData).map(Number);
    const maxDataYear = Math.max(...dataYears);
    
    if (endYear > maxDataYear) {
      const yearsBeyondData = endYear - maxDataYear;
      setWhatIfPeriodResult({ 
        success: false, 
        error: `Warning: This ${validatedPeriod}-year retirement period extends ${yearsBeyondData} years beyond available historical data (ends ${endYear}, data available through ${maxDataYear}). Results may be less reliable.` 
      });
      return;
    }

    const result = await calculateWhatIfScenario('Change Retirement Period', async () => {
      const lastInputs = lastRunInputsRef.current || {};
      const lastReturns = lastReturnsByYearRef.current || [];
      const start = Number(lastInputs.startYear ?? startYear) || 1980;
      const originalPeriod = Math.max(0, Number(lastInputs.periodLength ?? periodLength) || 0);
      const yearsW = validatedPeriod;
      const isCoupleStatus = (lastInputs.relationshipStatus ?? relationshipStatus) === 'couple';
      const partnerSuperForCalc = isCoupleStatus ? (Number(lastInputs.partnerSuper ?? partnerSuper) || 0) : 0;
      const totalSuper = (Number(lastInputs.yourSuper ?? yourSuper) || 0) + partnerSuperForCalc;

      if (yearsW <= 0 || totalSuper <= 0) {
        throw new Error('Invalid input parameters');
      }
      if (!lastInputs.periodLength && !lastReturns.length) {
        throw new Error('Run the main calculation first');
      }

      if (yearsW === originalPeriod) {
        const exact = Math.round(currentStrategyMSIRef.current || 0);
        return {
          avgIncome: exact,
          finalBalance: 0,
          survivalYears: yearsW,
          maxSustainableIncome: exact,
          totalSuperAfterDrop: totalSuper,
          currentMaxSustainableIncome: exact,
          impact: 0,
        };
      }

      const strategy = (String(lastRunStrategyRef.current || withdrawalStrategy) || 'fixed').toLowerCase();
      const baselineInputs = buildBaselineCalcInputs(lastInputs, lastReturns);
      const whatIfInputs = buildWhatIfCalcInputs(lastInputs, lastReturns, { periodLength: yearsW });
      const baselineIncome = computeStrategyMSI(baselineInputs, strategy);
      const newIncome = computeStrategyMSI(whatIfInputs, strategy);

      return {
        avgIncome: newIncome,
        finalBalance: 0,
        survivalYears: yearsW,
        maxSustainableIncome: newIncome,
        totalSuperAfterDrop: totalSuper,
        currentMaxSustainableIncome: baselineIncome,
        impact: newIncome - baselineIncome,
      };
    });
    setWhatIfPeriodResult(result);
  };
  // State to store MSI results for strategy comparison
  const [msiResults, setMsiResults] = useState({});
  // Calculate MSI results for all withdrawal strategies
  const calculateAllStrategyResults = () => {
    return msiResults;
  };
  // Snapshot of last main run inputs and returns for What-If single-source-of-truth reuse
  // Calculate MSI for all 4 strategies (called when Run button is pressed)
  // EACH STRATEGY WILL PRODUCE DIFFERENT RESULTS - that's the point of comparing them
  const calculateAllStrategiesMSI = (totalSuper, returnsByYear, yearsW, currentAge, assessableAssets, otherIncome, includePension, isCouple, isHomeowner, pensionGrowthRate, adminFees, adminFeePercentage, investmentFees, insuranceFees) => {
    const allMSI = {};

    // Note: This is a placeholder that returns the base MSI
    // The actual MSI for each strategy is calculated in the main strategy-specific code
    // and stored via setMsiResults() calls
    // This function just provides the structure
    const baseMSI = SimulationEngine.calculateMaxSustainableIncomeAdvanced(
      totalSuper, returnsByYear, yearsW, currentAge, assessableAssets,
      otherIncome, includePension, isCouple, isHomeowner, pensionGrowthRate,
      adminFees, adminFeePercentage, investmentFees, insuranceFees, 0
    );

    // Return base MSI for all - will be overridden by actual calculations
    allMSI.Fixed = baseMSI;
    allMSI.DynamicSWR = baseMSI;
    allMSI.VanguardGuardrails = baseMSI;
    allMSI.FloorAndCeiling = baseMSI;

    return allMSI;
  };
  const handleChangeAnnualFees = async () => {
    // Check if main calculation has been run
    if (!currentStrategyMSIRef.current || currentStrategyMSIRef.current === 0) {
      setWhatIfFeeChangeResult({ success: false, error: 'Please run the main calculation first (click the Run button above)' });
      return;
    }
    
    const validatedFeeChange = validateInput(whatIfFeeChangePct, -10, 10, 0);
    if (validatedFeeChange === 0 && whatIfFeeChangePct !== '0') {
      setWhatIfFeeChangeResult({ success: false, error: 'Please enter a valid fee change (-10% to +10%)' });
      return;
    }

    const result = await calculateWhatIfScenario('Change Annual Fees', async () => {
      if (validatedFeeChange === 0) {
        const exact = Math.round(currentStrategyMSIRef.current || 0);
        const totalSuperBaseline = (Number(yourSuper) || 0) + (relationshipStatus === 'couple' ? (Number(partnerSuper) || 0) : 0);
        return {
          success: true,
          avgIncome: exact,
          currentMaxSustainableIncome: exact,
          totalSuperAfterDrop: totalSuperBaseline,
          maxSustainableIncome: exact,
          impact: 0,
        };
      }

      const lastInputs = lastRunInputsRef.current || {};
      const lastReturns = lastReturnsByYearRef.current || [];
      const isCoupleStatus = (lastInputs.relationshipStatus ?? relationshipStatus) === 'couple';
      const partnerSuperAmount = isCoupleStatus ? (Number(lastInputs.partnerSuper ?? partnerSuper) || 0) : 0;
      const totalSuper = (Number(lastInputs.yourSuper ?? yourSuper) || 0) + partnerSuperAmount;
      const yearsW = Math.max(0, Number(lastInputs.periodLength ?? periodLength) || 0);
      if (yearsW <= 0 || totalSuper <= 0) {
        throw new Error('Invalid input parameters');
      }
      if (!lastInputs.periodLength && !lastReturns.length) {
        throw new Error('Run the main calculation first');
      }

      const baseInvestmentFees = Number(lastInputs.investmentFees ?? investmentFees) || 0;
      const newInvestmentFees = Math.max(0, baseInvestmentFees + Number(validatedFeeChange));
      const strategy = (String(lastRunStrategyRef.current || withdrawalStrategy) || 'fixed').toLowerCase();
      const baselineInputs = buildBaselineCalcInputs(lastInputs, lastReturns);
      const whatIfInputs = buildWhatIfCalcInputs(lastInputs, lastReturns, { investmentFees: newInvestmentFees });
      const baselineIncome = computeStrategyMSI(baselineInputs, strategy);
      const newIncome = computeStrategyMSI(whatIfInputs, strategy);

      return {
        success: true,
        avgIncome: newIncome,
        currentMaxSustainableIncome: baselineIncome,
        impact: newIncome - baselineIncome,
      };
    });
    setWhatIfFeeChangeResult(result);
  };
  const handleSequenceRisk = async () => {
    // Check if main calculation has been run
    if (!currentStrategyMSIRef.current || currentStrategyMSIRef.current === 0) {
      setWhatIfSeqRiskResult({ success: false, error: 'Please run the main calculation first (click the Run button above)' });
      return;
    }
    
    // More permissive validation - allow partial input like "-"
    if (!whatIfSeqRiskPct || whatIfSeqRiskPct === '' || whatIfSeqRiskPct === '-') {
      setWhatIfSeqRiskResult({ success: false, error: 'Please enter a valid reduction (-95 to 0 percentage points)' });
      return;
    }
    
    // Check for incomplete negative input before validation
    if (whatIfSeqRiskPct === '-' || whatIfSeqRiskPct === '-.') {
      setWhatIfSeqRiskResult({ success: false, error: 'Please enter a valid reduction (-95 to 0 percentage points)' });
      return;
    }
    
    const validatedSeqRisk = validateInput(whatIfSeqRiskPct, -95, 0, 0);
    if (validatedSeqRisk === 0 && whatIfSeqRiskPct !== '0') {
      setWhatIfSeqRiskResult({ success: false, error: 'Please enter a valid reduction (-95 to 0 percentage points)' });
      return;
    }

    const result = await calculateWhatIfScenario('Sequence of Returns Risk', async () => {
      const lastInputs = lastRunInputsRef.current || {};
      const lastReturns = lastReturnsByYearRef.current || [];
      const yearsW = Math.max(0, Number(lastInputs.periodLength ?? periodLength) || 0);
      const isCoupleStatus = (lastInputs.relationshipStatus ?? relationshipStatus) === 'couple';
      const totalSuperBase = (Number(lastInputs.yourSuper ?? yourSuper) || 0)
        + (isCoupleStatus ? (Number(lastInputs.partnerSuper ?? partnerSuper) || 0) : 0);

      if (yearsW <= 0 || totalSuperBase <= 0) {
        throw new Error('Invalid input parameters');
      }
      if (!lastInputs.periodLength && !lastReturns.length) {
        throw new Error('Run the main calculation first');
      }

      const strategy = (String(lastRunStrategyRef.current || withdrawalStrategy) || 'fixed').toLowerCase();
      const baselineReturns = resolveBaselineReturns(lastInputs, lastReturns);
      const seqReturns = applySequenceRiskToReturns(baselineReturns, validatedSeqRisk, 3);
      const baselineInputs = buildBaselineCalcInputs(lastInputs, baselineReturns);
      const whatIfInputs = buildWhatIfCalcInputs(lastInputs, baselineReturns, { providedReturns: seqReturns });
      const baselineIncome = computeStrategyMSI(baselineInputs, strategy);
      const whatIfResult = computeStrategyFullResult(whatIfInputs, strategy);
      const newIncome = Math.round(whatIfResult.avgIncome || 0);
      const totalSuperAfterDrop = whatIfResult.incomeByYear?.[2]?.balance ?? 0;

      const adjustedAge = Number(lastInputs.yourAge ?? yourAge) || 65;
      const partnerAgeVal = Number(lastInputs.partnerAge ?? partnerAge) || adjustedAge;
      const pensionAge = 67;
      const gapYears = Math.max(0, pensionAge - adjustedAge);
      const includePensionSeq = !!(lastInputs.includePension ?? includePension);
      const isUnder67 = adjustedAge < pensionAge;
      const otherIncomeSeq = (Number(lastInputs.otherTaxableIncome ?? otherTaxableIncome) || 0)
        + (Number(lastInputs.otherNonTaxableIncome ?? otherNonTaxableIncome) || 0)
        + (Number(lastInputs.annuityAnnualPayment ?? annuityAnnualPayment) || 0)
        + (Number(lastInputs.definedBenefitPensionAmount ?? definedBenefitPensionAmount) || 0);
      const assessableAssetsSeq = Number(lastInputs.otherAssets ?? otherAssets) || 0;
      const isHomeownerSeq = !!(lastInputs.isHomeowner ?? isHomeowner);
      const originalTotalSuper = totalSuperBase;
      const balanceReductionPct = originalTotalSuper > 0 ? (Math.max(0, totalSuperAfterDrop) / originalTotalSuper) : 1;

      let showYearlyBreakdown = false;
      let incomeBeforePensionAge = newIncome;
      let incomeAfterPensionAge = newIncome;
      let finalMaxSustainableIncome = newIncome;

      if (isUnder67 && includePensionSeq && balanceReductionPct < 0.5) {
        showYearlyBreakdown = true;
        incomeBeforePensionAge = Math.max(otherIncomeSeq, newIncome);
        const pensionAtAge67 = calculateAgePensionForHousehold({
          totalAssets: Math.max(0, totalSuperAfterDrop) + assessableAssetsSeq,
          otherIncome: otherIncomeSeq,
          isCouple: isCoupleStatus,
          isHomeowner: isHomeownerSeq,
          year: gapYears,
          pensionGrowthRate: Number(lastInputs.pensionGrowthRate ?? pensionGrowthRate) || 0,
          ageA: adjustedAge + gapYears,
          ageB: partnerAgeVal + gapYears,
          includePension: includePensionSeq,
        });
        incomeAfterPensionAge = Math.round(Math.max(otherIncomeSeq + pensionAtAge67, newIncome));
        finalMaxSustainableIncome = Math.min(incomeBeforePensionAge, incomeAfterPensionAge);
      }

      return {
        avgIncome: finalMaxSustainableIncome,
        finalBalance: 0,
        survivalYears: yearsW,
        maxSustainableIncome: finalMaxSustainableIncome,
        totalSuperAfterDrop: Math.round(Math.max(0, totalSuperAfterDrop)),
        currentMaxSustainableIncome: baselineIncome,
        impact: finalMaxSustainableIncome - baselineIncome,
        incomeBeforePensionAge,
        incomeAfterPensionAge,
        isUnder67,
        showYearlyBreakdown,
        gapYears,
      };
    });
        setWhatIfSeqRiskResult(result);
  };
  // Life expectancy p50 markers (left chart only)
  const LifeP50 = (props) => {
    try {
      const years = Math.max(0, Number(periodLength) || 0);
      if (!years) return null;
      const { xAxisMap, offset } = props || {};
      const key = xAxisMap ? Object.keys(xAxisMap)[0] : null;
      const xCfg = key ? xAxisMap[key] : null;
      const xScale = xCfg ? xCfg.scale : null;
      const bandwidth = xScale && typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
      if (!xScale) return null;
      const center = (i) => (xScale(i) ?? 0) + (bandwidth ? bandwidth / 2 : 0);
      const baseY = (offset?.top || 0) + (offset?.height || 0) - 14;

      const nearestRow = (gender, age) => {
        const table = gender === 'male' ? lifeExpectancyData.male : lifeExpectancyData.female;
        const keys = Object.keys(table).map(Number);
        let best = keys[0]; let bestD = Infinity;
        for (const k of keys) { const d = Math.abs(k - age); if (d < bestD) { bestD = d; best = k; } }
        return table[best] || { p50: 0 };
      };

      const points = [];
      const pushP50 = (gender, age, yOffset) => {
        const row = nearestRow(gender, age);
        const v = Number(row.p50) || 0;
        const raw = v - age;
        if (raw > years) return; // skip if beyond chart
        const rem = Math.max(0.0001, raw);
        let xi = Math.max(1, Math.ceil(Math.min(years, rem)));
        const cx = center(xi);
        const cy = baseY - yOffset;
        const color = gender === 'male' ? '#2563eb' : '#ec4899';
        points.push({ cx, cy, color, label: '50' });
      };

      if (yourGender === 'male') pushP50('male', Number(yourAge)||0, 6);
      if (yourGender === 'female') pushP50('female', Number(yourAge)||0, 6);
      if (relationshipStatus === 'couple') {
        if (partnerGender === 'male') pushP50('male', Number(partnerAge)||0, 16);
        if (partnerGender === 'female') pushP50('female', Number(partnerAge)||0, 16);
      }

      return (
        <g style={{ pointerEvents: 'none' }}>
          {points.map((p, i) => (
            <g key={`lep50-${i}`}>
              <circle cx={p.cx} cy={p.cy} r={8} fill={p.color} opacity={0.9} />
              <text x={p.cx} y={p.cy + 3} fontSize={9} textAnchor="middle" fill="#fff">{p.label}</text>
            </g>
          ))}
        </g>
      );
    } catch { return null; }
  };

  // Gender bands inside chart (above x-axis)
  const LifeBands = (props) => {
    try {
      const years = Math.max(0, Number(periodLength) || 0);
      if (!years) return null;
      const { xAxisMap, offset } = props || {};
      const key = xAxisMap ? Object.keys(xAxisMap)[0] : null;
      const xCfg = key ? xAxisMap[key] : null;
      const xScale = xCfg ? xCfg.scale : null;
      const bandwidth = xScale && typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
      if (!xScale) return null;
      const center = (i) => (xScale(i) ?? 0) + (bandwidth ? bandwidth / 2 : 0);
      const x1 = center(1);
      const x2 = center(years);
      const baseY = (offset?.top || 0) + (offset?.height || 0) - 14;
      const rowY = (yOffset) => baseY - yOffset - 3; // 6px bar height centered on marker row

      const nodes = [];
      if (yourGender === 'male' || yourGender === 'female') {
        const youColor = yourGender==='male' ? '#2563eb' : '#ec4899';
        const y = rowY(6);
        nodes.push(<rect key="you" x={x1} y={y} width={Math.max(0, x2 - x1)} height={6} fill={youColor} opacity={0.15} rx={2} />);
      }
      if (relationshipStatus === 'couple' && (partnerGender === 'male' || partnerGender === 'female')) {
        nodes.push(
          <rect key="partner" x={x1} y={rowY(16)} width={Math.max(0, x2 - x1)} height={6} fill={partnerGender==='male' ? '#2563eb' : '#ec4899'} opacity={0.15} rx={2} />
        );
      }
      // Caption on lowest band
      nodes.push(
        <text key="le-caption" x={x1 + 6} y={(yourGender ? rowY(6) : rowY(16)) + 5} fontSize={10} fill="#374151">
          Probability of living x number of years
        </text>
      );
      return <g style={{ pointerEvents: 'none' }}>{nodes}</g>;
    } catch { return null; }
  };
  // Life expectancy p20 markers (left chart only)
  const LifeP20 = (props) => {
    try {
      const years = Math.max(0, Number(periodLength) || 0);
      if (!years) return null;
      const { xAxisMap, offset } = props || {};
      const key = xAxisMap ? Object.keys(xAxisMap)[0] : null;
      const xCfg = key ? xAxisMap[key] : null;
      const xScale = xCfg ? xCfg.scale : null;
      const bandwidth = xScale && typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
      if (!xScale) return null;
      const center = (i) => (xScale(i) ?? 0) + (bandwidth ? bandwidth / 2 : 0);
      const baseY = (offset?.top || 0) + (offset?.height || 0) - 14; // align with p50 row

      const nearestRow = (gender, age) => {
        const table = gender === 'male' ? lifeExpectancyData.male : lifeExpectancyData.female;
        const keys = Object.keys(table).map(Number);
        let best = keys[0]; let bestD = Infinity;
        for (const k of keys) { const d = Math.abs(k - age); if (d < bestD) { bestD = d; best = k; } }
        return table[best] || { p25: 0, p10: 0 };
      };

      const computeP20Rem = (row, age) => {
        let v = Number(row.p20);
        if (!Number.isFinite(v)) {
          const p25 = Number(row.p25);
          const p10 = Number(row.p10);
          if (Number.isFinite(p25) && Number.isFinite(p10)) v = p25 - (p25 - p10) / 3;
        }
        if (!Number.isFinite(v)) v = 0;
        const rem = Math.max(0.0001, v - age);
        return Math.min(years, rem);
      };

      const points = [];
      const pushP20 = (gender, age, yExtra) => {
        const row = nearestRow(gender, age);
        let rawRem;
        if (Number.isFinite(Number(row.p20))) {
          rawRem = Number(row.p20) - age;
        } else {
          rawRem = computeP20Rem(row, age); // already rem
        }
        if (rawRem > years) return;
        const rem = Math.max(0.0001, Math.min(years, rawRem));
        let xi = Math.max(1, Math.ceil(rem)); if (xi > years) xi = years;
        const cx = center(xi);
        const cy = baseY - yExtra;
        const color = gender === 'male' ? '#2563eb' : '#ec4899';
        points.push({ cx, cy, color, label: '20' });
      };

      if (yourGender === 'male') pushP20('male', Number(yourAge)||0, 6);
      if (yourGender === 'female') pushP20('female', Number(yourAge)||0, 6);
      if (relationshipStatus === 'couple') {
        if (partnerGender === 'male') pushP20('male', Number(partnerAge)||0, 16);
        if (partnerGender === 'female') pushP20('female', Number(partnerAge)||0, 16);
      }

      return (
        <g style={{ pointerEvents: 'none' }}>
          {points.map((p, i) => (
            <g key={`lep20-${i}`}>
              <circle cx={p.cx} cy={p.cy} r={8} fill={p.color} opacity={0.9} />
              <text x={p.cx} y={p.cy + 3} fontSize={9} textAnchor="middle" fill="#fff">{p.label}</text>
            </g>
          ))}
        </g>
      );
    } catch { return null; }
  };

  // Life expectancy p10 markers (left chart only)
  const LifeP10 = (props) => {
    try {
      const years = Math.max(0, Number(periodLength) || 0);
      if (!years) return null;
      const { xAxisMap, offset } = props || {};
      const key = xAxisMap ? Object.keys(xAxisMap)[0] : null;
      const xCfg = key ? xAxisMap[key] : null;
      const xScale = xCfg ? xCfg.scale : null;
      const bandwidth = xScale && typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
      if (!xScale) return null;
      const center = (i) => (xScale(i) ?? 0) + (bandwidth ? bandwidth / 2 : 0);
      const baseY = (offset?.top || 0) + (offset?.height || 0) - 14; // align with p50 row

      const nearestRow = (gender, age) => {
        const table = gender === 'male' ? lifeExpectancyData.male : lifeExpectancyData.female;
        const keys = Object.keys(table).map(Number);
        let best = keys[0]; let bestD = Infinity;
        for (const k of keys) { const d = Math.abs(k - age); if (d < bestD) { bestD = d; best = k; } }
        return table[best] || { p25: 0, p5: 0 };
      };

      const computeP10Rem = (row, age) => {
        let v = Number(row.p10);
        if (!Number.isFinite(v)) {
          const p25 = Number(row.p25);
          const p5 = Number(row.p5);
          if (Number.isFinite(p25) && Number.isFinite(p5)) v = p5 + (p25 - p5) * ((10 - 5) / (25 - 5));
        }
        if (!Number.isFinite(v)) v = 0;
        const rem = Math.max(0.0001, v - age);
        return Math.min(years, rem);
      };

      const points = [];
      const pushP10 = (gender, age, yExtra) => {
        const row = nearestRow(gender, age);
        let rawRem;
        if (Number.isFinite(Number(row.p10))) {
          rawRem = Number(row.p10) - age;
        } else {
          rawRem = computeP10Rem(row, age); // already rem
        }
        if (rawRem > years) return;
        const rem = Math.max(0.0001, Math.min(years, rawRem));
        let xi = Math.max(1, Math.ceil(rem)); if (xi > years) xi = years;
        const cx = center(xi);
        const cy = baseY - yExtra;
        const color = gender === 'male' ? '#2563eb' : '#ec4899';
        points.push({ cx, cy, color, label: '10' });
      };

      if (yourGender === 'male') pushP10('male', Number(yourAge)||0, 6);
      if (yourGender === 'female') pushP10('female', Number(yourAge)||0, 6);
      if (relationshipStatus === 'couple') {
        if (partnerGender === 'male') pushP10('male', Number(partnerAge)||0, 16);
        if (partnerGender === 'female') pushP10('female', Number(partnerAge)||0, 16);
      }

      return (
        <g style={{ pointerEvents: 'none' }}>
          {points.map((p, i) => (
            <g key={`lep10-${i}`}>
              <circle cx={p.cx} cy={p.cy} r={8} fill={p.color} opacity={0.9} />
              <text x={p.cx} y={p.cy + 3} fontSize={9} textAnchor="middle" fill="#fff">{p.label}</text>
            </g>
          ))}
        </g>
      );
    } catch { return null; }
  };

  // Life expectancy p5 markers (left chart only)
  const LifeP5 = (props) => {
    try {
      const years = Math.max(0, Number(periodLength) || 0);
      if (!years) return null;
      const { xAxisMap, offset } = props || {};
      const key = xAxisMap ? Object.keys(xAxisMap)[0] : null;
      const xCfg = key ? xAxisMap[key] : null;
      const xScale = xCfg ? xCfg.scale : null;
      const bandwidth = xScale && typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
      if (!xScale) return null;
      const center = (i) => (xScale(i) ?? 0) + (bandwidth ? bandwidth / 2 : 0);
      const baseY = (offset?.top || 0) + (offset?.height || 0) - 14; // align with p50 row

      const nearestRow = (gender, age) => {
        const table = gender === 'male' ? lifeExpectancyData.male : lifeExpectancyData.female;
        const keys = Object.keys(table).map(Number);
        let best = keys[0]; let bestD = Infinity;
        for (const k of keys) { const d = Math.abs(k - age); if (d < bestD) { bestD = d; best = k; } }
        return table[best] || { p5: 0 };
      };

      const points = [];
      const pushP5 = (gender, age, yExtra) => {
        const row = nearestRow(gender, age);
        const v = Number(row.p5) || 0;
        const raw = v - age;
        if (raw > years) return;
        const rem = Math.max(0.0001, raw);
        let xi = Math.max(1, Math.ceil(Math.min(years, rem))); if (xi > years) xi = years;
        const cx = center(xi);
        const cy = baseY - yExtra;
        const color = gender === 'male' ? '#2563eb' : '#ec4899';
        points.push({ cx, cy, color, label: '5' });
      };

      if (yourGender === 'male') pushP5('male', Number(yourAge)||0, 6);
      if (yourGender === 'female') pushP5('female', Number(yourAge)||0, 6);
      if (relationshipStatus === 'couple') {
        if (partnerGender === 'male') pushP5('male', Number(partnerAge)||0, 16);
        if (partnerGender === 'female') pushP5('female', Number(partnerAge)||0, 16);
      }

      return (
        <g style={{ pointerEvents: 'none' }}>
          {points.map((p, i) => (
            <g key={`lep5-${i}`}>
              <circle cx={p.cx} cy={p.cy} r={8} fill={p.color} opacity={0.9} />
              <text x={p.cx} y={p.cy + 3} fontSize={9} textAnchor="middle" fill="#fff">{p.label}</text>
            </g>
          ))}
        </g>
      );
    } catch { return null; }
  };

  // Life expectancy markers temporarily removed

  // Helper function to display input values (hide 0)
  const getInputValue = (value) => {
    // Use shared helper for consistent cross-browser behavior
    return formatInputValue(value);
  };

  // Get min and max years from historical data
  const availableYears = Object.keys(historicalData).map(Number);
  const minDataYear = Math.min(...availableYears);
  const maxDataYear = Math.max(...availableYears);

  // InfoTooltip now defined outside component

  // Check if either partner is below preservation age (60)
  const isCouple = relationshipStatus === 'couple';
  const yourAgeNum = Number(yourAge) || 0;
  const partnerAgeNum = Number(partnerAge) || 0;
  const preservationAge = 60;
  const youBelowPreservation = yourAgeNum < preservationAge;
  const partnerBelowPreservation = isCouple && partnerAgeNum < preservationAge;
  const eitherBelowPreservation = youBelowPreservation || partnerBelowPreservation;
  
  const strategyOptions = [
    {
      value: 'fixed',
      label: 'Fixed Sustainable Income',
      tooltip:
        "Targets a constant inflation‑adjusted income each year. Withdrawals are set so the portfolio is expected to last the full period.",
      disabled: false,
    },
    {
      value: 'DynamicSWR',
      label: 'Dynamic SWR',
      tooltip: eitherBelowPreservation
        ? "⚠️ This strategy only applies when both partners reach preservation age (60). Please use 'Fixed Sustainable Income' until then."
        : "Adjusts withdrawals annually using a dynamic safe‑withdrawal rule with guardrails (raises or lowers income when portfolio deviates).",
      disabled: eitherBelowPreservation,
    },
    {
      value: 'VanguardGuardrails',
      label: 'Vanguard Guardrails',
      tooltip: eitherBelowPreservation
        ? "⚠️ This strategy only applies when both partners reach preservation age (60). Please use 'Fixed Sustainable Income' until then."
        : "Applies Vanguard's market‑aware guardrails: income bands respond to returns and valuation so withdrawals adapt while avoiding big swings.",
      disabled: eitherBelowPreservation,
    },
    {
      value: 'FloorAndCeiling',
      label: 'Floor and Ceiling',
      tooltip: eitherBelowPreservation
        ? "⚠️ This strategy only applies when both partners reach preservation age (60). Please use 'Fixed Sustainable Income' until then."
        : "Sets a minimum \"floor\" to protect essential spending and a maximum \"ceiling\" to limit withdrawals after strong returns.",
      disabled: eitherBelowPreservation,
    },
  ];

  // StrategySelect now defined outside component

  // Auto-switch to Fixed strategy if a dynamic strategy is selected and either partner is below preservation age
  useEffect(() => {
    const dynamicStrategies = ['DynamicSWR', 'VanguardGuardrails', 'FloorAndCeiling'];
    const isDynamicStrategy = dynamicStrategies.includes(withdrawalStrategy);
    
    if (isDynamicStrategy && eitherBelowPreservation) {
      // Switch to Fixed strategy automatically
      setWithdrawalStrategy('fixed');
    }
  }, [withdrawalStrategy, eitherBelowPreservation, setWithdrawalStrategy]);

  // Scenario Management Functions
  const getCurrentInputs = () => {
    return {
      relationshipStatus,
      yourGender,
      yourAge,
      yourSuper,
      yourTbcUsed,
      partnerGender,
      partnerSuper,
      partnerAge,
      partnerTbcUsed,
      otherTaxableIncome,
      otherNonTaxableIncome,
      otherAssets,
      isHomeowner,
      includePension,
      pensionGrowthRate,
      hasPrivateHealthInsurance,
      partnerHasPrivateHealthInsurance,
      // annualRent, // not defined in this component
      startYear,
      periodLength,
      withdrawalStrategy,
      monteCarloTargetIncome,
      targetFinalBalance,
      sp500,
      ausShares,
      intlShares,
      bonds,
      cash,
      frankingCredits,
      adminFees,
      investmentFees,
      insuranceFees,
      generalTransferBalanceCap,
      annuityPurchasePrice,
      annuityAnnualPayment,
      annuityIsIndexed,
      definedBenefitPensionAmount,
      definedBenefitIsIndexed,
      lumpSumWithdrawals,
      lumpSumContributions,
    };
  };

  const getCurrentResults = () => {
    if (!showHistorical && !showMonteCarlo) {
      return null;
    }
    
    return {
      sustainableIncome: historicalResults?.sustainableIncome || 0,
      finalBalance: historicalResults?.finalRealBalance || 0,
      mcPercentiles: mcPercentiles,
      mcIncomePercentiles: mcIncomePercentiles,
      mcMedianIncomesSummary: mcMedianIncomesSummary,
      historicalSeries: historicalSeries,
      rollingMSI: rollingMSI
    };
  };

  const loadScenarioInputs = (scenarioInputs) => {
    // Load all the inputs from the scenario
    setRelationshipStatus(scenarioInputs.relationshipStatus || 'single');
    setYourGender(scenarioInputs.yourGender || 'male');
    setYourAge(scenarioInputs.yourAge || 65);
    setYourSuper(scenarioInputs.yourSuper || 0);
    setYourTbcUsed(scenarioInputs.yourTbcUsed || 0);
    setPartnerGender(scenarioInputs.partnerGender || 'female');
    setPartnerSuper(scenarioInputs.partnerSuper || 0);
    setPartnerAge(scenarioInputs.partnerAge || 65);
    setPartnerTbcUsed(scenarioInputs.partnerTbcUsed || 0);
    setOtherTaxableIncome(scenarioInputs.otherTaxableIncome || 0);
    setOtherNonTaxableIncome(scenarioInputs.otherNonTaxableIncome || 0);
    setOtherAssets(scenarioInputs.otherAssets || 0);
    setIsHomeowner(scenarioInputs.isHomeowner || true);
    setIncludePension(scenarioInputs.includePension || true);
    setPensionGrowthRate(scenarioInputs.pensionGrowthRate || 0.3);
    setHasPrivateHealthInsurance(scenarioInputs.hasPrivateHealthInsurance || true);
    setPartnerHasPrivateHealthInsurance(scenarioInputs.partnerHasPrivateHealthInsurance || true);
    // setAnnualRent(scenarioInputs.annualRent || 0); // annualRent not defined in this component
    setStartYear(scenarioInputs.startYear || 1980);
    setPeriodLength(scenarioInputs.periodLength || 30);
    setWithdrawalStrategy(scenarioInputs.withdrawalStrategy || 'fixed');
    // setFixedIncome(scenarioInputs.fixedIncome || 80000); // fixedIncome not defined in advanced calculator
    setMonteCarloTargetIncome(scenarioInputs.monteCarloTargetIncome || 80000);
    setTargetFinalBalance(scenarioInputs.targetFinalBalance || 0);
    setSp500(scenarioInputs.sp500 || 100);
    setAusShares(scenarioInputs.ausShares || 0);
    setIntlShares(scenarioInputs.intlShares || 0);
    setBonds(scenarioInputs.bonds || 0);
    setCash(scenarioInputs.cash || 0);
    setFrankingCredits(scenarioInputs.frankingCredits || 80);
    setAdminFees(scenarioInputs.adminFees || 52);
    setInvestmentFees(scenarioInputs.investmentFees || 0.5);
    setInsuranceFees(scenarioInputs.insuranceFees || 0);
    setGeneralTransferBalanceCap(
      normalizeGeneralTransferBalanceCap(
        scenarioInputs.generalTransferBalanceCap ??
          scenarioInputs.transferBalanceCap ??
          FinancialConfig.TRANSFER_BALANCE_CAP
      )
    );
    setAnnuityPurchasePrice(scenarioInputs.annuityPurchasePrice || 0);
    setAnnuityAnnualPayment(scenarioInputs.annuityAnnualPayment || 0);
    setAnnuityIsIndexed(scenarioInputs.annuityIsIndexed || false);
    setDefinedBenefitPensionAmount(scenarioInputs.definedBenefitPensionAmount || 0);
    setDefinedBenefitIsIndexed(scenarioInputs.definedBenefitIsIndexed || false);
    if (scenarioInputs.lumpSumWithdrawals) {
      setLumpSumWithdrawals(normalizeLumpSumWithdrawalRows(scenarioInputs.lumpSumWithdrawals));
    }
    if (scenarioInputs.lumpSumContributions) {
      setLumpSumContributions(normalizeLumpSumContributionRows(scenarioInputs.lumpSumContributions));
    }
    
    // Reset results
    setShowHistorical(false);
    setShowMonteCarlo(false);
    
    // Note: User will need to manually run simulation after loading scenario
    // This is intentional to avoid automatic recalculation with potentially large datasets
  };
  return (
    <ScenarioWrapper
      calculatorType="advanced"
      getCurrentInputs={getCurrentInputs}
      getCurrentResults={getCurrentResults}
      loadScenarioInputs={loadScenarioInputs}
      hasResults={showHistorical || showMonteCarlo}
    >
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        
        {/* EMAIL GATE — when anon runs exhausted */}
        {!hasAdvancedAccess && !freeRunsStatus.canRun && freeRunsStatus.reason === 'needs_email' && (
          <FreeRunsEmailGate
            onEmailCaptured={(email) => {
              SubscriptionManager.setAdvancedFreeEmail(email);
              const newStatus = SubscriptionManager.canRunAdvancedFree();
              setFreeRunsStatus(newStatus);
              setShowFreeRunsEmailGate(false);
              setIsDemoMode(false);
              setTimeout(() => calculateButtonRef.current?.click(), 100);
            }}
            onUpgradeClick={() => {
              setShowFreeRunsEmailGate(false);
              if (typeof gtag !== 'undefined') {
                gtag('event', 'email_gate_upgrade_click', { event_category: 'conversion', event_label: 'Advanced email gate', value: 1, calculator_tier: 'advanced' });
              }
              openPricingModal({
                source: 'email_gate',
                calculatorTier: 'advanced',
                surface: 'email_gate_upgrade',
              });
            }}
            onDismiss={() => setShowFreeRunsEmailGate(false)}
          />
        )}

        {/* Exhausted gate — after free runs are used (surrounding, not calculator) */}
        {!hasAdvancedAccess && !freeRunsStatus.canRun && freeRunsStatus.reason === 'needs_upgrade' && (
          <div className="mb-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg overflow-hidden">
            <div className="p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="text-4xl">🔒</div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-2">You've used your free Advanced run. Keep testing before you rely on this plan.</h3>
                  <p className="text-blue-100 mb-4">
                    {exhaustedSupportCopy(paywallIncome, paywallSurvival)}
                  </p>
                  {savedScenario && (
                    <div className="mb-4 p-4 bg-white/15 rounded-lg border border-white/30">
                      <p className="font-semibold mb-2">You have a saved scenario: &quot;{savedScenario.name}&quot;</p>
                      <p className="text-sm text-blue-100 mb-3">Subscribe to restore it and run unlimited what-ifs with your own numbers.</p>
                      <button
                        type="button"
                        onClick={() => {
                          if (typeof gtag !== 'undefined') {
                            gtag('event', 'subscribe_to_restore_click', { event_category: 'conversion', event_label: 'Advanced saved scenario soft pricing', value: 1, calculator_tier: 'advanced' });
                          }
                          openPricingModal({
                            source: 'saved_scenario',
                            calculatorTier: 'advanced',
                            surface: 'saved_scenario_restore',
                          });
                        }}
                        className="px-4 py-2 bg-amber-400 text-blue-900 font-bold rounded-lg hover:bg-amber-300 transition"
                      >
                        Subscribe — restore scenario · from $14.99/mo
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof gtag !== 'undefined') {
                        gtag('event', 'demo_banner_cta_click', { event_category: 'conversion', event_label: 'Upgrade CTA Soft pricing - Advanced', value: 1, calculator_tier: 'advanced' });
                      }
                      openPricingModal({
                        source: 'advanced_gate',
                        calculatorTier: 'advanced',
                        surface: 'free_runs_exhausted',
                      });
                    }}
                    className="px-6 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-100 transition shadow-lg"
                  >
                    {subscribeWithTheirNumberLabel(paywallIncome, { survivalPct: paywallSurvival })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Surrounding chrome only — calculator form/results unchanged below */}
        <div className="flex justify-between items-start gap-4 mb-4">
          <div className="flex-1 min-w-0">
            {b2bMode ? (
              <h1 className="text-2xl font-bold text-gray-800 mb-1">
                {B2BManagementSystem.getTenantConfig(tenantId).name}
              </h1>
            ) : null}
            <p className="text-lg sm:text-xl font-semibold text-gray-900">
              Enter your numbers. See if the income survives bad markets.
            </p>
            {!hasAdvancedAccess && freeRunsStatus.canRun ? (
              <p className="text-sm text-gray-600 mt-1">
                {freeRunsStatus.runsLeft === 1
                  ? '1 free Advanced run remaining — no card required.'
                  : `${freeRunsStatus.runsLeft} free Advanced runs remaining — no card required.`}
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => setShowHowThisWorks((v) => !v)}
              className="mt-2 text-sm font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-2"
            >
              {showHowThisWorks ? 'Hide how this works' : 'How this works'}
            </button>
            {entryScenarioNotice && (
              <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
                <p className="font-bold">Loaded: {entryScenarioNotice.name}.</p>
                <p className="mt-1 text-blue-800">Change any field below, then run your own stress test.</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {/* Localhost-only: toggle free user to test run limit and email gate */}
            {shouldShowLocalDevChrome() && (
              <button
                type="button"
                onClick={() => {
                  const isFree = localStorage.getItem('simulate_free_user') === '1';
                  if (isFree) {
                    localStorage.removeItem('simulate_free_user');
                  } else {
                    localStorage.setItem('simulate_free_user', '1');
                    localStorage.removeItem('subscription_tier');
                    localStorage.removeItem('subscription_expiry');
                    localStorage.removeItem('subscription_id');
                    localStorage.removeItem('subscription_start');
                    localStorage.removeItem('adv_anon_runs');
                    localStorage.removeItem('adv_acct_runs');
                  }
                  window.location.reload();
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow border-2 border-amber-400 bg-amber-100 text-amber-900 hover:bg-amber-200 transition"
                title={localStorage.getItem('simulate_free_user') === '1'
                  ? 'Simulating free user (gates active). Click to restore localhost All Access.'
                  : 'Localhost All Access (gates hidden). Click to simulate free user.'}
              >
                {localStorage.getItem('simulate_free_user') === '1' ? '🧪 Dev mode: Free user' : '🧪 Dev mode: All Access'}
              </button>
            )}
            <button
              onClick={() => setShowTutorial(!showTutorial)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition shadow ${
                showTutorial 
                  ? 'bg-blue-700 text-white ring-2 ring-blue-300' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              title={showTutorial ? 'Close tutorial sidebar' : 'Open tutorial sidebar'}
            >
              <span>🎓</span>
              <span>{showTutorial ? 'Hide Tutorial' : 'Tutorial'}</span>
            </button>
            <button
          onClick={() => {
            if (window.confirm('This will clear all saved data. Are you sure?')) {
              localStorage.removeItem('advanced_calculator_state');
              localStorage.removeItem('calculatorTransfer'); // Also clear any SMSF transfer data
              window.location.reload();
            }
          }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition shadow"
              title="Clear all saved data"
            >
              <span>🗑️</span>
              <span>Clear Data</span>
            </button>
          </div>
        </div>

        {showHowThisWorks && (
          <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4">
            <AdvancedWalkthroughVideo
              placement="advanced_how_this_works"
              calculatorTier="advanced"
            />
            {!hasAdvancedAccess && (
              <ExampleScenariosSelector
                calculatorType="advanced"
                scenarioIds={FEATURED_ADVANCED_SCENARIO_IDS}
                onLoadScenario={handleLoadExampleScenario}
              />
            )}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-950">
              <p className="font-bold">
                Engine rules verified with ATO and Services Australia rates: {ratesVerifiedDate || 'recently'}.
              </p>
              <a
                href="/australian-super-age-pension-rates.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 font-semibold text-emerald-800 underline"
              >
                View live thresholds
              </a>
            </div>
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-950">
              <p className="font-bold">Unsolicited coverage in Super Review · referenced by Wealth Collective.</p>
              <div className="mt-2 flex flex-wrap gap-3">
                <a
                  href="https://www.superreview.com.au/does-asfas-retirement-target-fall-short-for-early-retirees/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-indigo-800 underline"
                >
                  Read Super Review
                </a>
                <a
                  href="https://wealthcollective.co/retirement-calculator-australia/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-indigo-800 underline"
                >
                  See Wealth Collective
                </a>
              </div>
            </div>
            <p className="text-xs text-slate-600">
              Latest update Jun 22, 2026 — lump sum withdrawals and contributions with MSI, charts, and exports.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Minimal Inputs Only */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <InfoTooltip content="Comprehensive retirement planning inputs including personal details, financial assets, pension settings, and portfolio configuration.">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Advanced Retirement Inputs</h3>
              </InfoTooltip>
              
              {transferFromSMSF && (
                <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📊</span>
                    <h4 className="font-semibold text-blue-900">Data Transferred from SMSF</h4>
                  </div>
                  <p className="text-sm text-blue-800">
                    Personal details, super balances, and asset allocation have been auto-populated from your SMSF calculator. 
                    These fields are <strong>read-only</strong>. To modify them, return to the SMSF calculator.
                  </p>
                </div>
              )}
              
              <div>
                <h4 className="text-md font-semibold text-gray-800">Personal & Financial</h4>
                <hr className="border-gray-300 my-3" />
              </div>
              <div>
                <InfoTooltip content="Single hides partner inputs; Couple enables partner fields and pension couple tests.">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relationship Status</label>
                </InfoTooltip>
                <select
                  value={relationshipStatus}
                  onChange={(e) => {
                    // Free users with remaining runs can toggle Single/Couple (Phase 1).
                    if (inputsLocked) return;
                    const newStatus = e.target.value;
                    setRelationshipStatus(newStatus);
                    // Automatically clear partner fields when switching to single
                    if (newStatus === 'single') {
                      setPartnerSuper('');
                      setPartnerAge('');
                      setPartnerTbcUsed('');
                      setPartnerSalary('');
                      setPartnerRetirementAge('');
                      setPartnerSuperContributions('');
                    }
                  }}
                  disabled={inputsLocked}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="single">Single</option>
                  <option value="couple">Couple</option>
                </select>
              </div>

              <div className="mt-4">
                <InfoTooltip content="Used for life expectancy overlays and pension calculations.">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Gender</label>
                </InfoTooltip>
                <select
                  value={yourGender}
                  onChange={(e) => {
                    if (inputsLocked) return;
                    setYourGender(e.target.value);
                  }}
                  disabled={inputsLocked}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div className="mt-4">
                <InfoTooltip content="Age affects life expectancy markers and Age Pension eligibility/tests.">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    {relationshipStatus === 'couple' ? (
                      <><span className="px-2 py-0.5 bg-blue-100 text-blue-900 text-xs font-bold rounded">A</span><span>Your Age</span></>
                    ) : (
                      <span>Your Age</span>
                    )}
                  </label>
                </InfoTooltip>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={getInputValue(yourAge)}
                  readOnly={inputsLocked}
                  disabled={inputsLocked}
                  onChange={(e) => {
                    if (inputsLocked) return; // Prevent changes when locked (demo mode, no subscription, or SMSF transfer)
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    const age = parseInt(value) || 0;
                    
                    // Per-partner validation: Allow any age for planning
                    if (age < 0) {
                      alert('Age cannot be negative.');
                      return;
                    }
                    if (age > 120) {
                      alert('Age seems unrealistic.');
                      return;
                    }
                    
                    // No informational alerts during typing - shown via inline indicators
                    setYourAge(Math.max(0, age));
                  }}
                  onBlur={(e) => {
                    const age = parseInt(e.target.value) || 0;
                    if (age > 0 && age < 55) {
                      alert('ℹ️ PRESERVATION AGE INFORMATION:\n\nYou can access superannuation from age 60 (preservation age).\n\nThis calculator will show projections, but super withdrawals will only begin at age 60.\n\nSource: Superannuation Industry (Supervision) Regulations 1994');
                    }
                  }}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {yourAge > 0 && yourAge < 60 && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <span>⏳</span>
                    <span>Super accessible in {60 - yourAge} years (age 60)</span>
                  </p>
                )}
                {yourAge >= 60 && yourAge < 67 && (
                  <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                    <span>✓</span>
                    <span>Super accessible • Pension from age 67</span>
                  </p>
                )}
                {yourAge >= 67 && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <span>✓</span>
                    <span>Super & Age Pension eligible</span>
                  </p>
                )}
              </div>

              <div className="mt-4">
                <InfoTooltip content="The age at which you plan to retire and begin drawing from super.">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    {relationshipStatus === 'couple' ? (
                      <><span className="px-2 py-0.5 bg-blue-100 text-blue-900 text-xs font-bold rounded">A</span><span>Your Retirement Age</span></>
                    ) : (
                      <span>Your Retirement Age</span>
                    )}
                  </label>
                </InfoTooltip>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={yourRetirementAge}
                  readOnly={inputsLocked}
                  disabled={inputsLocked}
                  onChange={(e) => {
                    if (inputsLocked) return;
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setYourRetirementAge(parseInt(value) || 0);
                  }}
                  onBlur={(e) => {
                    if (transferFromSMSF) return;
                    const age = parseInt(e.target.value) || 0;
                    if (age < yourAge) {
                      alert(`Warning: Your retirement age (${age}) cannot be less than your current age (${yourAge}).`);
                    } else if (age > 100) {
                      alert(`Warning: Your retirement age (${age}) seems unrealistic. Please enter a reasonable age.`);
                    }
                  }}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${transferFromSMSF ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {yourRetirementAge > yourAge && (
                  <p className="text-xs text-blue-600 mt-1">Working {yourRetirementAge - yourAge} more years</p>
                )}
                {yourRetirementAge === yourAge && (
                  <p className="text-xs text-green-600 mt-1">✓ Retiring now</p>
                )}
                
                {/* Super Withdrawal Age Control - Only show when under preservation age */}
                {yourAge < 60 && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allowWithdrawalsBeforeRetirementAge}
                        onChange={(e) => setAllowWithdrawalsBeforeRetirementAge(e.target.checked)}
                        disabled={inputsLocked}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        Allow super withdrawals before retirement age
                      </span>
                    </label>
                    <p className="text-xs text-gray-600 mt-1 ml-6">
                      {allowWithdrawalsBeforeRetirementAge 
                        ? `Super can be accessed at preservation age (60)`
                        : `Super can only be accessed at retirement age (${yourRetirementAge})`
                      }
                    </p>
                  </div>
                )}
              </div>


              <div className="mt-4">
                <InfoTooltip content="Your retirement balance at the start of the simulation (today's dollars).">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    {relationshipStatus === 'couple' ? (
                      <><span className="px-2 py-0.5 bg-blue-100 text-blue-900 text-xs font-bold rounded">A</span><span>Your Super ($)</span></>
                    ) : (
                      <span>Your Super ($)</span>
                    )}
                  </label>
                </InfoTooltip>
                <input
                  type="number"
                  value={yourSuper}
                  readOnly={inputsLocked}
                  disabled={inputsLocked}
                  onChange={(e) => {
                    if (inputsLocked) return;
                    const val = parseInt(e.target.value);
                    if (e.target.value === '' || isNaN(val)) {
                      setYourSuper(0);
                    } else {
                      setYourSuper(Math.max(0, val));
                    }
                  }}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${transferFromSMSF ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
              </div>

              <div className="mt-4">
                <InfoTooltip content="Amount of your personal Transfer Balance Cap already used toward retirement phase income streams">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your TBC Used ($)</label>
                </InfoTooltip>
                <input
                  type="number"
                  value={yourTbcUsed === 0 ? '' : yourTbcUsed}
                  placeholder="0"
                  readOnly={inputsLocked}
                  disabled={inputsLocked}
                  onChange={(e) => {
                    if (inputsLocked) return;
                    const val = parseInt(e.target.value);
                    if (e.target.value === '' || isNaN(val)) {
                      setYourTbcUsed(0);
                    } else {
                      setYourTbcUsed(Math.max(0, val));
                    }
                  }}
                  onBlur={(e) => {
                    if (transferFromSMSF) return;
                    const val = parseInt(e.target.value) || 0;
                    const tbcCap = FinancialConfig.TRANSFER_BALANCE_CAP;
                    if (val > tbcCap) {
                      alert(`Warning: Your TBC Used exceeds the Transfer Balance Cap of $${tbcCap.toLocaleString()}. This may have compliance implications.`);
                    }
                  }}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${transferFromSMSF ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {Number(yourTbcUsed) > FinancialConfig.TRANSFER_BALANCE_CAP && (
                  <div className="text-red-600 text-xs mt-1">⚠️ Exceeds Transfer Balance Cap of ${FinancialConfig.TRANSFER_BALANCE_CAP.toLocaleString()}</div>
                )}
              </div>

              {relationshipStatus === 'couple' && (
                <>
                  <div className="mt-6">
                    <h4 className="text-md font-semibold text-gray-800">Partner</h4>
                    <hr className="border-gray-300 my-3" />
                  </div>
                  <div className="mt-2">
                    <InfoTooltip content="Used for life expectancy overlays and pension couple tests.">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Partner's Gender</label>
                    </InfoTooltip>
                    <select
                      value={partnerGender}
                      onChange={(e) => {
                        if (inputsLocked) return;
                        setPartnerGender(e.target.value);
                      }}
                      disabled={inputsLocked}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div className="mt-4">
                    <InfoTooltip content="Used for life expectancy markers and pension couple tests.">
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-900 text-xs font-bold rounded">B</span>
                        <span>Partner's Age</span>
                      </label>
                    </InfoTooltip>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={getInputValue(partnerAge)}
                      readOnly={transferFromSMSF}
                      disabled={transferFromSMSF}
                      onChange={(e) => {
                        if (transferFromSMSF) return;
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        const age = parseInt(value) || 0;
                        
                        // Per-partner validation: Allow any age for planning
                        if (age < 0) {
                          alert('Age cannot be negative.');
                          return;
                        }
                        if (age > 120) {
                          alert('Age seems unrealistic.');
                          return;
                        }
                        
                        // No informational alerts during typing - shown via inline indicators
                        setPartnerAge(Math.max(0, age));
                      }}
                      onBlur={(e) => {
                        if (transferFromSMSF) return;
                        const age = parseInt(e.target.value) || 0;
                        if (age > 0 && age < 55) {
                          alert('ℹ️ PRESERVATION AGE INFORMATION:\n\nYour partner can access superannuation from age 60 (preservation age).\n\nThis calculator will show projections, but super withdrawals will only begin at age 60.');
                        }
                      }}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${transferFromSMSF ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    {partnerAge > 0 && partnerAge < 60 && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <span>⏳</span>
                        <span>Super accessible in {60 - partnerAge} years (age 60)</span>
                      </p>
                    )}
                    {partnerAge >= 60 && partnerAge < 67 && (
                      <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                        <span>✓</span>
                        <span>Super accessible • Pension from age 67</span>
                      </p>
                    )}
                    {partnerAge >= 67 && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <span>✓</span>
                        <span>Super & Age Pension eligible</span>
                      </p>
                    )}
                  </div>
                  
                  <div className="mt-4">
                    <InfoTooltip content="The age at which your partner plans to retire and begin drawing from super.">
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-900 text-xs font-bold rounded">B</span>
                        <span>Partner's Retirement Age</span>
                      </label>
                    </InfoTooltip>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={partnerRetirementAge}
                      readOnly={transferFromSMSF}
                      disabled={transferFromSMSF}
                      onChange={(e) => {
                        if (transferFromSMSF) return;
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setPartnerRetirementAge(parseInt(value) || 0);
                      }}
                      onBlur={(e) => {
                        if (transferFromSMSF) return;
                        const age = parseInt(e.target.value) || 0;
                        if (age < partnerAge) {
                          alert(`Warning: Partner's retirement age (${age}) cannot be less than their current age (${partnerAge}).`);
                        } else if (age > 100) {
                          alert(`Warning: Partner's retirement age (${age}) seems unrealistic. Please enter a reasonable age.`);
                        }
                      }}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${transferFromSMSF ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    {partnerRetirementAge > partnerAge && (
                      <p className="text-xs text-purple-600 mt-1">Working {partnerRetirementAge - partnerAge} more years</p>
                    )}
                    {partnerRetirementAge === partnerAge && (
                      <p className="text-xs text-green-600 mt-1">✓ Retiring now</p>
                    )}
                  </div>
                  
                  <div className="mt-4">
                    <InfoTooltip content="Partner retirement balance (today's dollars).">
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-900 text-xs font-bold rounded">B</span>
                        <span>Partner's Super ($)</span>
                      </label>
                    </InfoTooltip>
                    <input
                      type="number"
                      value={partnerSuper}
                      readOnly={transferFromSMSF}
                      disabled={transferFromSMSF}
                      onChange={(e) => {
                        if (transferFromSMSF) return;
                        const val = parseInt(e.target.value);
                        if (e.target.value === '' || isNaN(val)) {
                          setPartnerSuper(0);
                        } else {
                          setPartnerSuper(Math.max(0, val));
                        }
                      }}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${transferFromSMSF ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                  </div>
                  <div className="mt-4">
                    <InfoTooltip content="Amount of your partner's Transfer Balance Cap already used toward retirement phase income streams">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Partner's TBC Used ($)</label>
                    </InfoTooltip>
                    <input
                      type="number"
                      value={partnerTbcUsed === 0 ? '' : partnerTbcUsed}
                      placeholder="0"
                      readOnly={transferFromSMSF}
                      disabled={transferFromSMSF}
                      onChange={(e) => {
                        if (transferFromSMSF) return;
                        const val = parseInt(e.target.value);
                        if (e.target.value === '' || isNaN(val)) {
                          setPartnerTbcUsed(0);
                        } else {
                          setPartnerTbcUsed(Math.max(0, val));
                        }
                      }}
                      onBlur={(e) => {
                        if (transferFromSMSF) return;
                        const val = parseInt(e.target.value) || 0;
                        const tbcCap = FinancialConfig.TRANSFER_BALANCE_CAP;
                        if (val > tbcCap) {
                          alert(`Warning: Partner's TBC Used exceeds the Transfer Balance Cap of $${tbcCap.toLocaleString()}. This may have compliance implications.`);
                        }
                      }}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${transferFromSMSF ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    {Number(partnerTbcUsed) > FinancialConfig.TRANSFER_BALANCE_CAP && (
                      <div className="text-red-600 text-xs mt-1">⚠️ Exceeds Transfer Balance Cap of ${FinancialConfig.TRANSFER_BALANCE_CAP.toLocaleString()}</div>
                    )}
                  </div>


                </>
              )}

              {/* Income & Home section - always visible */}
              <>
                  <div className="mt-6">
                    <h4 className="text-md font-semibold text-gray-800">Income & Home</h4>
                    <hr className="border-gray-300 my-3" />
                  </div>
                  <div className="mt-2">
                    <InfoTooltip content="Assessable income outside super (affects Age Pension income test).">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Other Taxable Income ($/yr)</label>
                    </InfoTooltip>
                    <input
                      type="number"
                      value={getInputValue(otherTaxableIncome)}
                      onChange={(e) => {
                        if (inputsLocked) return;
                        setOtherTaxableIncome(Math.max(0, parseInt(e.target.value) || 0));
                      }}
                      disabled={inputsLocked}
                      readOnly={inputsLocked}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <InfoTooltip content="Part-time employment/wage income while retired and on Age Pension. Eligible for Work Bonus: first $7,800/year ($300/fortnight) exempt from pension income test.">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {relationshipStatus === 'couple' ? 'Your' : ''} Part-time Employment ($/yr)
                          <span className="text-xs text-green-600 ml-2">✓ Work Bonus</span>
                        </label>
                      </InfoTooltip>
                      <input
                        type="number"
                        value={getInputValue(yourPartTimeEmploymentIncome)}
                        onChange={(e) => {
                          if (inputsLocked) return;
                          setYourPartTimeEmploymentIncome(Math.max(0, parseInt(e.target.value) || 0));
                        }}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        placeholder="e.g., 15000"
                      />
                      {yourPartTimeEmploymentIncome > 0 && (
                        <p className="text-xs text-green-600 mt-1">
                          ${Math.min(yourPartTimeEmploymentIncome, 7800).toLocaleString()} exempt
                        </p>
                      )}
                    </div>
                    {relationshipStatus === 'couple' && (
                      <div>
                        <InfoTooltip content="Partner's part-time employment/wage income while retired and on Age Pension. Each partner gets their own Work Bonus allowance: $7,800/year each.">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Partner's Part-time Employment ($/yr)
                            <span className="text-xs text-green-600 ml-2">✓ Work Bonus</span>
                          </label>
                        </InfoTooltip>
                        <input
                          type="number"
                          value={getInputValue(partnerPartTimeEmploymentIncome)}
                          onChange={(e) => {
                            if (inputsLocked) return;
                            setPartnerPartTimeEmploymentIncome(Math.max(0, parseInt(e.target.value) || 0));
                          }}
                          disabled={inputsLocked}
                          readOnly={inputsLocked}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          placeholder="e.g., 12000"
                        />
                        {partnerPartTimeEmploymentIncome > 0 && (
                          <p className="text-xs text-green-600 mt-1">
                            ${Math.min(partnerPartTimeEmploymentIncome, 7800).toLocaleString()} exempt
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex items-center">
                    <input
                      id="isHomeowner"
                      type="checkbox"
                      checked={isHomeowner}
                      onChange={(e) => {
                        if (inputsLocked) return;
                        setIsHomeowner(e.target.checked);
                      }}
                      disabled={inputsLocked}
                      className="mr-2"
                    />
                    <label htmlFor="isHomeowner" className="text-sm font-medium text-gray-700">Homeowner</label>
                    <span className="ml-3 text-xs text-gray-500">(untick for Non-Homeowner)</span>
                  </div>
                  
                  {/* Rent Paid (only for non-homeowners) - Commonwealth Rent Assistance */}
                  {!isHomeowner && (
                    <div className="mt-4">
                      <label htmlFor="rentPaid" className="block text-sm font-medium text-gray-700 mb-1">
                        Fortnightly Rent Paid ($)
                        <InfoTooltip content="Enter the rent you pay each fortnight. Commonwealth Rent Assistance will be automatically calculated if you're eligible for Age Pension. Maximum assistance (Sept 2025): Singles $212/fortnight, Couples $199.80/fortnight combined (both partners). Note: Singles receive more than couples combined." />
                      </label>
                      <input
                        id="rentPaid"
                        type="number"
                        value={rentPaidFortnightly}
                        onChange={(e) => {
                          if (inputsLocked) return;
                          setRentPaidFortnightly(e.target.value);
                        }}
                        disabled={inputsLocked}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 400"
                        min="0"
                        step="10"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Rent assistance is added to your Age Pension if eligible (receiving base pension + paying rent above threshold). Unlike Age Pension, couples combined get slightly LESS than singles.
                      </p>
                    </div>
                  )}
              </>

              <CollapsibleInputSection
                title="Advanced Settings"
                subtitle="Other assets, pension assumptions, transfer balance cap, and health insurance"
                tooltip="Less common assumptions that affect tax, Age Pension, and retirement-phase calculations: non-taxable income, assets outside super, pension indexation, transfer balance cap, and private health insurance."
                open={showAdvanced}
                onToggle={setShowAdvanced}
              >
                  <div className="space-y-4">
                    {/* Other income/assets/pension toggle */}
                    <div>
                      <InfoTooltip content="Non-assessable income sources (not counted for pension income test).">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Other Non-taxable Income ($/yr)</label>
                      </InfoTooltip>
                      <input
                        type="number"
                        value={getInputValue(otherNonTaxableIncome)}
                        onChange={(e) => {
                          if (inputsLocked) return;
                          setOtherNonTaxableIncome(Math.max(0, parseInt(e.target.value) || 0));
                        }}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      />
                    </div>
                    <div>
                      <InfoTooltip content="Assets outside super considered for Age Pension tests (e.g., savings, investments, investment property).">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Other Assets ($)</label>
                      </InfoTooltip>
                      <input
                        type="number"
                        value={getInputValue(otherAssets)}
                        onChange={(e) => {
                          if (inputsLocked) return;
                          setOtherAssets(Math.max(0, parseInt(e.target.value) || 0));
                        }}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        id="includePension_left"
                        type="checkbox"
                        checked={includePension}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked && yourAge > 0 && yourAge < 67) {
                            const yearsUntilEligible = 67 - yourAge;
                            alert(`ℹ️ AGE PENSION ELIGIBILITY:\n\nYou must be 67 or older to receive the Age Pension.\n\nYour current age: ${yourAge}\nYears until eligible: ${yearsUntilEligible}\n\nThe calculator will include Age Pension only from age 67 onwards.\n\nSource: Social Security Act 1991`);
                          }
                          setIncludePension(checked);
                        }}
                        className="mr-2"
                      />
                      <InfoTooltip content="Include Australian Age Pension in projections, calculated via asset and income tests">
                        <label htmlFor="includePension_left" className="text-sm font-medium text-gray-700">Include Pension?</label>
                      </InfoTooltip>
                    </div>

                    {/* Pension indexation & cap */}
                    <div>
                      <InfoTooltip content="One annual real (above-CPI) indexation rate for future Age Pension projections. It grows maximum pension rates, part-pension cut-offs, supplements, and rent assistance caps. Asset and income free areas and deeming thresholds stay CPI-flat in today's dollars; deeming percentages stay fixed. Default 0.3% is conservative vs ASIC's separate CPI/wage indexation assumptions.">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pension Growth Rate (%/yr)</label>
                      </InfoTooltip>
                      <input
                        type="number"
                        step="0.1"
                        value={pensionGrowthRate}
                        onChange={(e) => {
                          if (inputsLocked) return;
                          setPensionGrowthRate(parseFloat(e.target.value) || 0);
                        }}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <InfoTooltip content="Your personal limit on how much can be transferred to retirement (tax‑free) phase. Exceeding the cap can incur tax.">
                        <label className="block text-sm font-medium text-gray-700 mb-1">General Transfer Balance Cap ($)</label>
                      </InfoTooltip>
                      <input
                        type="number"
                        value={generalTransferBalanceCap}
                        onChange={(e) => {
                          if (inputsLocked) return;
                          setGeneralTransferBalanceCap(Math.max(0, parseInt(e.target.value) || 0));
                        }}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Health toggles */}
                    <div className="flex items-center">
                      <input
                        id="hasPHI_left"
                        type="checkbox"
                        checked={hasPrivateHealthInsurance}
                        onChange={(e) => {
                          if (inputsLocked) return;
                          setHasPrivateHealthInsurance(e.target.checked);
                        }}
                        disabled={inputsLocked}
                        className="mr-2"
                      />
                      <label htmlFor="hasPHI_left" className="text-sm font-medium text-gray-700">Your Private Health Insurance?</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="partnerHasPHI_left"
                        type="checkbox"
                        checked={partnerHasPrivateHealthInsurance}
                        onChange={(e) => {
                          if (inputsLocked) return;
                          setPartnerHasPrivateHealthInsurance(e.target.checked);
                        }}
                        disabled={inputsLocked}
                        className="mr-2"
                      />
                      <label htmlFor="partnerHasPHI_left" className="text-sm font-medium text-gray-700">Partner's Private Health Insurance?</label>
                    </div>
                  </div>
              </CollapsibleInputSection>
            </div>
          </div>
          {/* Center Column - Simulation & Portfolio */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Simulation and Portfolio</h3>
              <div className="space-y-4">
                <div>
                  <InfoTooltip content="One actual period in calendar order (e.g. 1980 for 30 years → 1980–2009). Each year uses that year’s CPI and investment returns from our historical dataset. For many random paths instead of one period, use Monte Carlo.">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Year (Single Scenario)</label>
                  </InfoTooltip>
                  <input
                    type="number"
                    value={startYear}
                    onChange={(e) => {
                      if (inputsLocked) return;
                      setStartYear(parseInt(e.target.value) || 0);
                    }}
                    disabled={inputsLocked}
                    readOnly={inputsLocked}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value) || 1980;
                      if (val < minDataYear) {
                        alert(`Warning: Start Year cannot be before ${minDataYear}. Historical data is only available from ${minDataYear} onwards.`);
                        setStartYear(minDataYear);
                      } else if (val > maxDataYear) {
                        alert(`Warning: Start Year cannot exceed ${maxDataYear}. Historical data is only available up to ${maxDataYear}.`);
                        setStartYear(maxDataYear);
                      } else {
                        setStartYear(val);
                      }
                    }}
                    min={minDataYear}
                    max={maxDataYear}
                    title={`Historical simulation start year (${minDataYear}-${maxDataYear})`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {Number(startYear) < minDataYear && Number(startYear) > 0 && (
                    <div className="text-red-600 text-xs mt-1">⚠️ Start Year must be {minDataYear} or later (historical data limitation)</div>
                  )}
                  {Number(startYear) > maxDataYear && (
                    <div className="text-red-600 text-xs mt-1">⚠️ Start Year cannot exceed {maxDataYear} (historical data ends at {maxDataYear})</div>
                  )}
                </div>
                <div>
                  <InfoTooltip content="Number of years to simulate">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Period Length (years)</label>
                  </InfoTooltip>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={periodLength}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setPeriodLength(Math.max(0, parseInt(value) || 0));
                    }}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      const endYear = Number(startYear) + val;
                      if (val >= 60) {
                        alert('Warning: Period Length of 60+ years is very long. Most retirement planning uses 30-40 years.');
                      }
                      if (endYear > maxDataYear) {
                        alert(`Warning: Start Year (${startYear}) + Period Length (${val}) = ${endYear}, which exceeds available historical data (ends at ${maxDataYear}). Results may be incomplete.`);
                      }
                    }}
                    title="Number of years to simulate"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {Number(periodLength) >= 60 && (
                    <div className="text-orange-600 text-xs mt-1">⚠️ Very long period (60+ years)</div>
                  )}
                  {(Number(startYear) + Number(periodLength)) > maxDataYear && (
                    <div className="text-red-600 text-xs mt-1">⚠️ Simulation extends beyond available data ({maxDataYear})</div>
                  )}
                </div>
                <div>
                  <InfoTooltip content="Desired balance at end of period (today's dollars)">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Final Balance ($)</label>
                  </InfoTooltip>
                  <input
                    type="number"
                    value={targetFinalBalance}
                    onChange={(e) => {
                      if (inputsLocked) return;
                      setTargetFinalBalance(Math.max(0, parseInt(e.target.value) || 0));
                    }}
                    disabled={inputsLocked}
                    readOnly={inputsLocked}
                    title="Desired balance at the end of the period (today's dollars)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {withdrawalStrategy === 'VanguardGuardrails' && targetFinalBalance > 0 && (
                    <div className="text-orange-600 text-xs mt-1">
                      ℹ️ Vanguard Guardrails does not use Target Final Balance. It adjusts withdrawals based on withdrawal rate guardrails only. Use Dynamic SWR or Fixed strategy for legacy planning.
                    </div>
                  )}
                  {withdrawalStrategy === 'FloorAndCeiling' && targetFinalBalance > 0 && (
                    <div className="text-orange-600 text-xs mt-1">
                      ℹ️ Floor & Ceiling does not use Target Final Balance. It uses fixed percentage floors and ceilings. Use Dynamic SWR or Fixed strategy for legacy planning.
                    </div>
                  )}
                </div>
              </div>
              <CollapsibleInputSection
                title="Fees"
                subtitle={`Admin $${Number(adminFees || 0).toLocaleString()}/yr, investment ${Number(investmentFees || 0).toFixed(2)}%, transaction ${Number(transactionCosts ?? 0).toFixed(2)}%`}
                tooltip="These defaults are typical for large superannuation companies, but fees vary by fund, option, and account type. Use your own fund's specific admin, investment, transaction, insurance, and fee-cap details where known. Fees reduce projected balances and sustainable income."
                open={showFees}
                onToggle={setShowFees}
              >
                <div className="space-y-4">
                  <div>
                    <InfoTooltip content="AustralianSuper flat admin fee">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Admin Fee ($/year)</label>
                    </InfoTooltip>
                    <input
                      type="number"
                      value={adminFees}
                      readOnly={transferFromSMSF}
                      disabled={transferFromSMSF}
                      onChange={(e) => {
                        if (transferFromSMSF) return;
                        setAdminFees(Math.max(0, parseInt(e.target.value) || 0));
                      }}
                      title="AustralianSuper flat admin fee"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${transferFromSMSF ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Typical: $52 flat admin fee</p>
                  </div>
                  <div>
                    <InfoTooltip content="AustralianSuper admin fee percentage">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Admin Fee (%/year)</label>
                    </InfoTooltip>
                    <input
                      type="number"
                      step="0.01"
                      value={adminFeePercentage || 0.10}
                      readOnly={transferFromSMSF}
                      disabled={transferFromSMSF}
                      onChange={(e) => {
                        if (transferFromSMSF) return;
                        setAdminFeePercentage(Math.max(0, parseFloat(e.target.value) || 0));
                      }}
                      title="AustralianSuper admin fee percentage"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${transferFromSMSF ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Typical: 0.10% of balance (capped at $350 when using defaults)</p>
                  </div>
                  <div>
                    <InfoTooltip content="AustralianSuper investment fee">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Investment Fee (%/year)</label>
                    </InfoTooltip>
                    <input
                      type="number"
                      step="0.01"
                      value={investmentFees}
                      readOnly={transferFromSMSF}
                      disabled={transferFromSMSF}
                      onChange={(e) => {
                        if (transferFromSMSF) return;
                        setInvestmentFees(Math.max(0, parseFloat(e.target.value) || 0));
                      }}
                      title="AustralianSuper investment fee"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${transferFromSMSF ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Typical: 0.49% of balance</p>
                  </div>
                  <div>
                    <InfoTooltip content="Maximum investment fee per year">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Investment Fee Cap ($/year)</label>
                    </InfoTooltip>
                    <input
                      type="number"
                      value={investmentFeeCap}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (e.target.value === '' || isNaN(val)) {
                          setInvestmentFeeCap(0);
                        } else {
                          setInvestmentFeeCap(Math.max(0, val));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Typical: Maximum $350/year investment fee</p>
                  </div>
                  <div>
                    <InfoTooltip content="Transaction costs as percentage of balance">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Costs (%/year)</label>
                    </InfoTooltip>
                    <input
                      type="number"
                      step="0.01"
                      value={transactionCosts ?? 0.08}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (e.target.value === '' || isNaN(val)) {
                          setTransactionCosts(0);
                        } else {
                          setTransactionCosts(Math.max(0, val));
                        }
                      }}
                      title="Transaction costs as percentage of balance"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Typical: 0.08% of balance</p>
                  </div>
                  <div>
                    <InfoTooltip content="AustralianSuper insurance fees">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Fees ($/year)</label>
                    </InfoTooltip>
                    <input
                      type="number"
                      value={insuranceFees}
                      readOnly={transferFromSMSF}
                      disabled={transferFromSMSF}
                      onChange={(e) => {
                        if (transferFromSMSF) return;
                        setInsuranceFees(Math.max(0, parseInt(e.target.value) || 0));
                      }}
                      title="AustralianSuper insurance fees"
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${transferFromSMSF ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Typical: $0 (separate insurance fees)</p>
                  </div>
                </div>
              </CollapsibleInputSection>

              <CollapsibleInputSection
                title="Asset Allocation (%)"
                subtitle={`Total allocation: ${(Number(sp500||0) + Number(ausShares||0) + Number(intlShares||0) + Number(bonds||0) + Number(cash||0)).toFixed(1)}%`}
                tooltip="Portfolio mix used to calculate historical returns and Monte Carlo outcomes. Productive asset allocations should total 100%; non-earning assets are shown separately when alternatives are entered."
                open={showAssetAllocation}
                onToggle={setShowAssetAllocation}
              >
                {(() => {
                  // Compute productive asset percentages and validation
                  const earningAlloc = Number(sp500||0) + Number(ausShares||0) + Number(intlShares||0) + Number(bonds||0) + Number(cash||0);
                  const infrastructurePctVal = Number(infrastructurePct||0);
                  const productiveTotal = earningAlloc + infrastructurePctVal;
                  const ok = Math.abs(productiveTotal - 100) < 0.5;
                  
                  
                  if (transferFromSMSF) {
                    // Show productive asset breakdown for SMSF transfers
                    return (
                      <div className="mb-4">
                        <div className="text-sm font-semibold text-blue-800 mb-2">📊 Productive Asset Breakdown (from SMSF)</div>
                        <div className="bg-blue-50 p-3 rounded border border-blue-200 text-xs space-y-1">
                          {ausShares > 0 && <div>• Direct Shares (ASX): {Number(ausShares).toFixed(1)}%</div>}
                          {infrastructurePctVal > 0 && <div>• Infrastructure: {infrastructurePctVal.toFixed(1)}%</div>}
                          {cash > 0 && <div>• Cash: {Number(cash).toFixed(1)}%</div>}
                          {sp500 > 0 && <div>• S&P 500: {Number(sp500).toFixed(1)}%</div>}
                          {intlShares > 0 && <div>• Intl Shares: {Number(intlShares).toFixed(1)}%</div>}
                          {bonds > 0 && <div>• Bonds: {Number(bonds).toFixed(1)}%</div>}
                          <div className="pt-2 mt-2 border-t border-blue-300 font-semibold">
                            Total Productive: {productiveTotal.toFixed(1)}% {ok ? '✅' : '⚠️'}
                          </div>
                        </div>
                        {!ok && (
                          <div className="mt-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded p-2">
                            ⚠️ Productive assets must sum to 100%. Current total: {productiveTotal.toFixed(1)}%
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    // Standard validation for non-SMSF transfers
                    return ok ? (
                      <div className="mb-2 text-xs text-gray-600">Total allocation: {earningAlloc.toFixed(1)}%</div>
                    ) : (
                      <div className="mb-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded p-2">
                        Asset allocation must total 100%. Current total: {earningAlloc.toFixed(1)}%
                      </div>
                    );
                  }
                })()}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Non-earning assets (%) are auto-computed and read-only; not part of the 100% earning allocation */}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[2.5rem] flex items-start">
                      <span>Non-earning assets (%)</span>
                      <span className="ml-1 text-blue-600 cursor-help" title="Auto-computed share of non-earning assets (property, collectibles, other alternatives) as a % of total portfolio.">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      value={(() => {
                        const totalSuperBalance = Math.max(0, Number(yourSuper||0) + Number(partnerSuper||0));
                        const nonEarningValue = Number(residentialPropertyValue||0) + Number(commercialPropertyValue||0) + Number(collectiblesValue||0) + Number(otherAlternativesValue||0);
                        const pct = totalSuperBalance > 0 ? (nonEarningValue / totalSuperBalance) * 100 : 0;
                        return Number(pct.toFixed(1));
                      })()}
                      readOnly
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[2.5rem] flex items-start">
                      <span>S&P 500 (%)</span>
                      <span className="ml-1 text-blue-600 cursor-help" title="Allocation to US S&P 500 equities (%)">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      value={getInputValue(sp500)}
                      onChange={(e) => {
                        const v = Math.max(0, parseFloat(e.target.value) || 0);
                        setSp500(v);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[2.5rem] flex items-start">
                      <span>Aus Shares (%)</span>
                      <span className="ml-1 text-blue-600 cursor-help" title="Allocation to Australian equities (%)">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      value={getInputValue(ausShares)}
                      onChange={(e) => {
                        const v = Math.max(0, parseFloat(e.target.value) || 0);
                        setAusShares(v);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[2.5rem] flex items-start">
                      <span>Intl Shares (%)</span>
                      <span className="ml-1 text-blue-600 cursor-help" title="Allocation to international (ex-US/Aus) equities (%)">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      value={getInputValue(intlShares)}
                      onChange={(e) => {
                        const v = Math.max(0, parseFloat(e.target.value) || 0);
                        setIntlShares(v);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[2.5rem] flex items-start">
                      <span>Bonds (%)</span>
                      <span className="ml-1 text-blue-600 cursor-help" title="Allocation to bonds/fixed income (%)">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      value={getInputValue(bonds)}
                      onChange={(e) => {
                        const v = Math.max(0, parseFloat(e.target.value) || 0);
                        setBonds(v);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[2.5rem] flex items-start">
                      <span>Cash (%)</span>
                      <span className="ml-1 text-blue-600 cursor-help" title="Allocation to cash (%)">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      value={getInputValue(cash)}
                      onChange={(e) => {
                        const v = Math.max(0, parseFloat(e.target.value) || 0);
                        setCash(v);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[2.5rem] flex items-start">
                      <span>Franking Credits (%)</span>
                      <span className="ml-1 text-blue-600 cursor-help" title="Portion of Australian dividends that receive franking credit benefit (%)">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={getInputValue(frankingCredits)}
                      onChange={(e) => {
                        if (inputsLocked) return;
                        setFrankingCredits(Math.max(0, parseFloat(e.target.value) || 0));
                      }}
                      disabled={inputsLocked}
                      readOnly={inputsLocked}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </CollapsibleInputSection>

              <CollapsibleInputSection
                title="Alternative Assets"
                subtitle="Property, infrastructure, collectibles, crypto, commodities, and other alternatives"
                tooltip="Optional assets outside the standard listed shares, bonds, and cash mix. These can affect income, capital growth, assessable assets, and the non-earning asset share of the portfolio."
                open={showAlternativeAssets}
                onToggle={setShowAlternativeAssets}
              >
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">🏢 Alternative Assets</h3>
                  <p className="text-sm text-gray-600 mb-4">Assets outside traditional shares/bonds (property, collectibles, etc.)</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Residential Property */}
                    <div className="col-span-2 bg-white p-4 rounded border border-gray-300">
                      <h4 className="font-semibold text-gray-800 mb-3">🏘️ Residential Property</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Value ($)</span></label>
                          <input
                            type="number"
                            value={getInputValue(residentialPropertyValue)}
                            onChange={(e) => setResidentialPropertyValue(Math.max(0, parseFloat(e.target.value) || 0))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Annual Rental Income ($)</span></label>
                          <input
                            type="number"
                            value={getInputValue(residentialRentalIncome)}
                            onChange={(e) => setResidentialRentalIncome(Math.max(0, parseFloat(e.target.value) || 0))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Rental Yield (%)</span></label>
                          <input
                            type="number"
                            step="0.1"
                            value={getInputValue(residentialPropertyRentalYield)}
                            onChange={(e) => setResidentialPropertyRentalYield(Math.max(0, parseFloat(e.target.value) || 0))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Capital Growth (% p.a.)</span></label>
                          <input
                            type="number"
                            step="0.1"
                            value={getInputValue(residentialPropertyCapitalGrowth)}
                            onChange={(e) => setResidentialPropertyCapitalGrowth(parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Commercial Property */}
                    <div className="col-span-2 bg-white p-4 rounded border border-gray-300">
                      <h4 className="font-semibold text-gray-800 mb-3">🏢 Commercial Property</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Value ($)</span></label>
                          <input
                            type="number"
                            value={getInputValue(commercialPropertyValue)}
                            onChange={(e) => setCommercialPropertyValue(Math.max(0, parseFloat(e.target.value) || 0))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Annual Rental Income ($)</span></label>
                          <input
                            type="number"
                            value={getInputValue(commercialRentalIncome)}
                            onChange={(e) => setCommercialRentalIncome(Math.max(0, parseFloat(e.target.value) || 0))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Rental Yield (%)</span></label>
                          <input
                            type="number"
                            step="0.1"
                            value={getInputValue(commercialPropertyRentalYield)}
                            onChange={(e) => setCommercialPropertyRentalYield(Math.max(0, parseFloat(e.target.value) || 0))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Capital Growth (% p.a.)</span></label>
                          <input
                            type="number"
                            step="0.1"
                            value={getInputValue(commercialPropertyCapitalGrowth)}
                            onChange={(e) => setCommercialPropertyCapitalGrowth(parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Infrastructure (Productive Asset) */}
                    {infrastructureValue > 0 && (
                      <div className="col-span-2 bg-green-50 p-4 rounded border border-green-300">
                        <h4 className="font-semibold text-gray-800 mb-3">⚡ Infrastructure (Productive Asset)</h4>
                        <p className="text-xs text-gray-600 mb-3">From SMSF Asset Valuation Manager</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Value ($)</span></label>
                            <input
                              type="number"
                              value={getInputValue(infrastructureValue)}
                              onChange={(e) => setInfrastructureValue(Math.max(0, parseFloat(e.target.value) || 0))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Percentage of Productive Assets (%)</span></label>
                            <input
                              type="number"
                              step="0.1"
                              value={Number(infrastructurePct).toFixed(1)}
                              readOnly
                              disabled
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Assumed Return Rate (% p.a.)</span></label>
                            <input
                              type="number"
                              step="0.1"
                              value={getInputValue(infrastructureReturnRate)}
                              onChange={(e) => setInfrastructureReturnRate(parseFloat(e.target.value) || 0)}
                              placeholder="e.g., 6.0"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Editable return rate for infrastructure investments</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Collectibles */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Collectibles Value ($)</span></label>
                      <input
                        type="number"
                        value={getInputValue(collectiblesValue)}
                        onChange={(e) => setCollectiblesValue(Math.max(0, parseFloat(e.target.value) || 0))}
                        placeholder="Art, antiques, rare items"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Collectibles Capital Growth (% p.a.)</span></label>
                      <input
                        type="number"
                        step="0.1"
                        value={getInputValue(collectiblesCapitalGrowth)}
                        onChange={(e) => setCollectiblesCapitalGrowth(parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Other Alternatives */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Other Alternatives Value ($)</span></label>
                      <input
                        type="number"
                        value={getInputValue(otherAlternativesValue)}
                        onChange={(e) => setOtherAlternativesValue(Math.max(0, parseFloat(e.target.value) || 0))}
                        placeholder="Crypto, commodities, other"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 min-h-[4rem] flex items-start"><span>Other Alternatives Capital Growth (% p.a.)</span></label>
                      <input
                        type="number"
                        step="0.1"
                        value={getInputValue(otherAlternativesCapitalGrowth)}
                        onChange={(e) => setOtherAlternativesCapitalGrowth(parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </CollapsibleInputSection>

              <CollapsibleInputSection
                title="Annuity & Defined Benefits"
                subtitle="Guaranteed annuity payments and defined benefit pension income"
                tooltip="Optional guaranteed income sources. Annuities convert a purchase price into regular payments; defined benefit pensions add a fixed or indexed annual income stream."
                open={showAnnuity}
                onToggle={setShowAnnuity}
              >
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <InfoTooltip content="An annuity exchanges a lump sum for a stream of guaranteed payments, optionally indexed to inflation.">
                    <h3 className="text-lg font-semibold text-blue-800 mb-3">Annuity <a href="https://help.superapp/annuity" target="_blank" rel="noreferrer" className="text-blue-700 text-xs ml-2 underline">Help</a></h3>
                  </InfoTooltip>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Annuity Purchase Price ($)</label>
                      <input
                        type="number"
                        value={annuityPurchasePrice}
                        onChange={(e) => setAnnuityPurchasePrice(Math.max(0, parseInt(e.target.value) || 0))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Annual Annuity Payment ($)</label>
                      <input
                        type="number"
                        value={annuityAnnualPayment}
                        onChange={(e) => setAnnuityAnnualPayment(Math.max(0, parseInt(e.target.value) || 0))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        id="annuityIsIndexed"
                        type="checkbox"
                        checked={annuityIsIndexed}
                        onChange={(e) => setAnnuityIsIndexed(e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="annuityIsIndexed" className="text-sm font-medium text-gray-700">Indexed to Inflation</label>
                    </div>
                  </div>

                  <InfoTooltip content="A defined benefit pension pays a fixed (often indexed) income based on service/salary rather than account balance.">
                    <h3 className="text-lg font-semibold text-blue-800 mt-6 mb-3">Defined Benefit Pension <a href="https://help.superapp/defined-benefit" target="_blank" rel="noreferrer" className="text-blue-700 text-xs ml-2 underline">Help</a></h3>
                  </InfoTooltip>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Annual Pension Amount ($)</label>
                      <input
                        type="number"
                        value={definedBenefitPensionAmount}
                        onChange={(e) => setDefinedBenefitPensionAmount(Math.max(0, parseInt(e.target.value) || 0))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        id="definedBenefitIsIndexed"
                        type="checkbox"
                        checked={definedBenefitIsIndexed}
                        onChange={(e) => setDefinedBenefitIsIndexed(e.target.checked)}
                        className="mr-2"
                      />
                      <label htmlFor="definedBenefitIsIndexed" className="text-sm font-medium text-gray-700">Indexed to Inflation</label>
                    </div>
                  </div>
                </div>
              </CollapsibleInputSection>
              
              {/* B2B Platform Features */}
              {b2bMode && (
                <div className="mt-6 bg-purple-50 border border-purple-200 p-6 rounded-lg">
                  <h4 className="text-md font-semibold text-purple-800 mb-4">B2B Platform Configuration</h4>
                  <div className="space-y-6">
                    
                    {/* Tenant Configuration */}
                    <div className="bg-white p-4 rounded-md border border-purple-200">
                      <h5 className="text-sm font-semibold text-purple-800 mb-3">Tenant Configuration</h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <InfoTooltip content="Select tenant configuration for multi-tenant architecture">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Tenant Type</label>
                          </InfoTooltip>
                          <select
                            value={tenantId}
                            onChange={(e) => {
                              setTenantId(e.target.value);
                              const config = B2BManagementSystem.getTenantConfig(e.target.value);
                              setTenantConfig(config);
                              B2BManagementSystem.applyTenantBranding(config);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="default">Default</option>
                            <option value="smsf-advisor">SMSF Advisor</option>
                            <option value="educational">Educational</option>
                          </select>
                        </div>
                        <div>
                          <InfoTooltip content="Current tenant name and branding">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Tenant Name</label>
                          </InfoTooltip>
                          <input
                            type="text"
                            value={tenantConfig.name}
                            onChange={(e) => setTenantConfig({...tenantConfig, name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* User Management */}
                    <div className="bg-white p-4 rounded-md border border-purple-200">
                      <h5 className="text-sm font-semibold text-purple-800 mb-3">User Management</h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <InfoTooltip content="Current user role and permissions">
                            <label className="block text-xs font-medium text-gray-700 mb-1">User Role</label>
                          </InfoTooltip>
                          <select
                            value={currentUser.role}
                            onChange={(e) => {
                              const role = e.target.value;
                              const roleConfig = B2BManagementSystem.USER_ROLES[role];
                              setCurrentUser({
                                ...currentUser,
                                role: role,
                                permissions: roleConfig.permissions
                              });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="guest">Guest User</option>
                            <option value="user">Registered User</option>
                            <option value="admin">Administrator</option>
                            <option value="api">API User</option>
                          </select>
                        </div>
                        <div>
                          <InfoTooltip content="Current user permissions and access level">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Permissions</label>
                          </InfoTooltip>
                          <div className="space-y-1">
                            {Object.entries(currentUser.permissions).map(([permission, allowed]) => (
                              <div key={permission} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={allowed}
                                  readOnly
                                  className="w-3 h-3 text-purple-600 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-xs text-gray-700 capitalize">{permission}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* API Management */}
                    {tenantConfig.features.apiAccess && (
                      <div className="bg-white p-4 rounded-md border border-purple-200">
                        <h5 className="text-sm font-semibold text-purple-800 mb-3">API Management</h5>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <InfoTooltip content="Generate API key for programmatic access">
                              <label className="block text-xs font-medium text-gray-700 mb-1">API Key</label>
                            </InfoTooltip>
                            <div className="flex">
                              <input
                                type="text"
                                value={apiKey || 'Click Generate to create API key'}
                                readOnly
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-100 text-sm"
                              />
                              <button
                                onClick={() => setApiKey(B2BManagementSystem.generateApiKey(tenantId, currentUser.id || 'user'))}
                                className="px-3 py-2 bg-purple-600 text-white rounded-r-md hover:bg-purple-700 text-xs"
                              >
                                Generate
                              </button>
                            </div>
                          </div>
                          <div>
                            <InfoTooltip content="Current API usage and rate limits">
                              <label className="block text-xs font-medium text-gray-700 mb-1">API Usage</label>
                            </InfoTooltip>
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600">Requests:</span>
                                <span className="font-medium">{apiRequests} / {apiLimit}</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-purple-600 h-2 rounded-full" 
                                  style={{width: `${Math.min(100, (apiRequests / apiLimit) * 100)}%`}}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Data Export */}
                    {tenantConfig.features.exportData && (
                      <div className="bg-white p-4 rounded-md border border-purple-200">
                        <h5 className="text-sm font-semibold text-purple-800 mb-3">Data Export</h5>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <button
                            onClick={() => {
                              const data = historicalSeries || [];
                              const csvData = B2BManagementSystem.exportData(data, 'csv');
                              const blob = new Blob([csvData], { type: 'text/csv' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = 'retirement-scenario.csv';
                              a.click();
                              URL.revokeObjectURL(url);
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                          >
                            Export CSV
                          </button>
                          <button
                            onClick={() => {
                              const data = {
                                inputs: {
                                  yourSuper,
                                  partnerSuper,
                                  withdrawalStrategy,
                                  periodLength,
                                  startYear
                                },
                                results: historicalResults,
                                series: historicalSeries
                              };
                              const jsonData = B2BManagementSystem.exportData(data, 'json');
                              const blob = new Blob([jsonData], { type: 'application/json' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = 'retirement-scenario.json';
                              a.click();
                              URL.revokeObjectURL(url);
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                          >
                            Export JSON
                          </button>
                          <button
                            onClick={() => {
                              // Placeholder for PDF export
                              alert('PDF export functionality would be implemented here');
                            }}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                          >
                            Export PDF
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Feature Configuration */}
                    <div className="bg-white p-4 rounded-md border border-purple-200">
                      <h5 className="text-sm font-semibold text-purple-800 mb-3">Feature Configuration</h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {Object.entries(tenantConfig.features).map(([feature, enabled]) => (
                          <div key={feature} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700 capitalize">{feature.replace(/([A-Z])/g, ' $1').trim()}</span>
                            <input
                              type="checkbox"
                              checked={enabled}
                              onChange={(e) => {
                                setTenantConfig({
                                  ...tenantConfig,
                                  features: {
                                    ...tenantConfig.features,
                                    [feature]: e.target.checked
                                  }
                                });
                              }}
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Phased Retirement Toggle */}
            {relationshipStatus === 'couple' && (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg shadow-md mt-6 border border-blue-200">
                <div className="mb-3 pb-3 border-b border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-purple-900 px-3 py-1 rounded-full text-xs font-bold shadow-sm">UNIQUE FEATURE</span>
                    <span className="text-xs text-gray-600 font-medium">The only Australian calculator we know of that handles couples retiring at different ages</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 p-2 bg-white rounded border border-purple-200">
                    <span className="text-lg">📊</span>
                    <span className="text-xs text-purple-900 font-semibold">75-80% of Australian couples retire at different times — this is the majority scenario, not a niche case.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="phasedRetirement"
                    checked={phasedRetirement}
                    disabled={isDemoMode}
                    onChange={(e) => {
                      setPhasedRetirement(e.target.checked);
                      // Set default retirement ages if enabling
                      if (e.target.checked) {
                        if (yourRetirementAge === yourAge) setYourRetirementAge(Math.max(yourAge, 60));
                        if (partnerRetirementAge === partnerAge) setPartnerRetirementAge(Math.max(partnerAge, 60));
                      }
                    }}
                    className={`mt-1 w-5 h-5 text-blue-600 ${isDemoMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                  />
                  <div className="flex-1">
                    <label htmlFor="phasedRetirement" className="text-lg font-semibold text-gray-800 cursor-pointer flex items-center gap-2">
                      <span>🔄</span>
                      <span>Enable Phased Retirement</span>
                    </label>
                    <p className="text-sm text-gray-600 mt-1">
                      Model realistic retirement where partners retire at different ages. Automatically calculates ongoing super contributions (SG 12%) from employment income for any partner still working. Stops contributions when they retire. More accurate than manually using "Other Taxable Income" which doesn't track super contributions or retirement timing.
                    </p>
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                      <strong>Tax Assumption:</strong> Phased retirement assumes all super balances are in pension phase (tax-free growth). If your super is still in accumulation phase, results may be slightly optimistic compared to non-phased retirement which applies accumulation tax.
                    </div>
                  </div>
                </div>
                
                {phasedRetirement && (
                  <>
                  <div className="mt-4 pt-4 border-t border-blue-200 grid grid-cols-2 gap-4">
                    {/* Partner A Retirement Details */}
                    <div className="bg-white p-4 rounded-lg border border-blue-100">
                      <h4 className="font-semibold text-blue-900 mb-3 text-sm flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-900 text-xs font-bold rounded">A</span>
                        <span>Your Retirement Plan</span>
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Retirement Age</label>
                          <input
                            type="number"
                            value={yourRetirementAge}
                            readOnly
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                          />
                          {yourRetirementAge > yourAge && (
                            <p className="text-xs text-blue-600 mt-1">Working {yourRetirementAge - yourAge} more years</p>
                          )}
                          {yourRetirementAge === yourAge && (
                            <p className="text-xs text-green-600 mt-1">✓ Retired now</p>
                          )}
                        </div>
                        {yourRetirementAge > yourAge && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Salary (while working)</label>
                            <input
                              type="number"
                              value={yourSalary}
                              onChange={(e) => {
                                const salary = parseInt(e.target.value) || 0;
                                setYourSalary(salary);
                                // Auto-calculate SG (12%)
                                setYourSuperContributions(Math.round(salary * FinancialConfig.SUPERANNUATION_GUARANTEE.RATE));
                              }}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                              placeholder="Annual salary"
                            />
                            <p className="text-xs text-gray-500 mt-1">SG ({(FinancialConfig.SUPERANNUATION_GUARANTEE.RATE * 100).toFixed(1)}%): ${(yourSalary * FinancialConfig.SUPERANNUATION_GUARANTEE.RATE).toLocaleString()}/year</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Partner B Retirement Details */}
                    <div className="bg-white p-4 rounded-lg border border-purple-100">
                      <h4 className="font-semibold text-purple-900 mb-3 text-sm flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-900 text-xs font-bold rounded">B</span>
                        <span>Partner's Retirement Plan</span>
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Retirement Age</label>
                          <input
                            type="number"
                            value={partnerRetirementAge}
                            readOnly
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                          />
                          {partnerRetirementAge > partnerAge && (
                            <p className="text-xs text-purple-600 mt-1">Working {partnerRetirementAge - partnerAge} more years</p>
                          )}
                          {partnerRetirementAge === partnerAge && (
                            <p className="text-xs text-green-600 mt-1">✓ Retired now</p>
                          )}
                        </div>
                        {partnerRetirementAge > partnerAge && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Salary (while working)</label>
                            <input
                              type="number"
                              value={partnerSalary}
                              onChange={(e) => {
                                const salary = parseInt(e.target.value) || 0;
                                setPartnerSalary(salary);
                                // Auto-calculate SG (12%)
                                setPartnerSuperContributions(Math.round(salary * FinancialConfig.SUPERANNUATION_GUARANTEE.RATE));
                              }}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                              placeholder="Annual salary"
                            />
                            <p className="text-xs text-gray-500 mt-1">SG ({(FinancialConfig.SUPERANNUATION_GUARANTEE.RATE * 100).toFixed(1)}%): ${(partnerSalary * FinancialConfig.SUPERANNUATION_GUARANTEE.RATE).toLocaleString()}/year</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border-2 border-amber-400" id="msi-drawdown-trigger">
                    <p className="text-sm font-semibold text-amber-950 mb-1">
                      Super drawdown trigger (Fixed MSI)
                    </p>
                    <p className="text-xs text-amber-900 mb-2">
                      When should sustainable super withdrawals start? Change this, then run again.
                    </p>
                    <div className="space-y-2">
                      <label className={`flex items-start gap-3 cursor-pointer p-2 rounded border bg-white ${msiStartsWhenEitherRetires ? 'border-blue-500 ring-1 ring-blue-300' : 'border-gray-200'}`}>
                        <input
                          type="radio"
                          name="msiDrawdownTriggerAdvanced"
                          checked={!!msiStartsWhenEitherRetires}
                          disabled={isDemoMode}
                          onChange={() => setMsiStartsWhenEitherRetires(true)}
                          className={`mt-1 ${isDemoMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                        <span>
                          <span className="text-sm font-medium text-gray-900">When either partner retires</span>
                          <span className="block text-xs text-gray-600 mt-0.5">
                            Start withdrawals when the first partner reaches their retirement age.
                          </span>
                        </span>
                      </label>
                      <label className={`flex items-start gap-3 cursor-pointer p-2 rounded border bg-white ${!msiStartsWhenEitherRetires ? 'border-blue-500 ring-1 ring-blue-300' : 'border-gray-200'}`}>
                        <input
                          type="radio"
                          name="msiDrawdownTriggerAdvanced"
                          checked={!msiStartsWhenEitherRetires}
                          disabled={isDemoMode}
                          onChange={() => setMsiStartsWhenEitherRetires(false)}
                          className={`mt-1 ${isDemoMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                        <span>
                          <span className="text-sm font-medium text-gray-900">Only when you (primary) retire</span>
                          <span className="block text-xs text-gray-600 mt-0.5">
                            Wait until you reach your retirement age, even if your partner has already retired.
                          </span>
                        </span>
                      </label>
                    </div>
                    <p className="text-xs text-amber-900 mt-2">
                      Active: <strong>{msiStartsWhenEitherRetires ? 'either partner' : 'primary only'}</strong>
                      {' '}· You {yourRetirementAge} · Partner {partnerRetirementAge}
                    </p>
                  </div>
                  </>
                )}
              </div>
            )}
            <details
              id="bridge-delayed-super-access"
              className="mt-4 border border-gray-200 rounded-lg bg-white overflow-hidden group"
              open={showBridgeStrategy}
              onToggle={(e) => setShowBridgeStrategy(e.currentTarget.open)}
            >
              <summary className="cursor-pointer list-none flex items-center justify-between gap-2 p-3 hover:bg-gray-50">
                <div>
                  <span className="text-sm font-semibold text-gray-800 inline-flex items-center gap-2">
                    <span>Bridge / Delayed Super Access</span>
                    <span onClick={(e) => e.stopPropagation()}>
                      <InfoTooltip content="Model being retired but deliberately not drawing super yet. The calculator uses bridge cash first, then outside-super investments, then accessible super. If bridge assets run out before super access, yearly rows flag the shortfall." />
                    </span>
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">Use cash and outside-super buckets before drawing super</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {enableBridgeStrategy && (
                    <span className="text-xs font-medium bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full">Enabled</span>
                  )}
                  <span className="text-gray-500 group-open:rotate-180 transition-transform inline-block" aria-hidden="true">▾</span>
                </div>
              </summary>
              <div className="border-t border-gray-200 p-3 bg-amber-50/40">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="enableBridgeStrategy"
                    checked={!!enableBridgeStrategy}
                    disabled={inputsLocked}
                    onChange={(e) => setEnableBridgeStrategy(e.target.checked)}
                    className="mt-0.5 w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-gray-800">Enable bridge strategy</span>
                    <p className="text-xs text-gray-600 mt-1">
                      Useful for early retirement, preservation-age planning, or delaying super drawdowns.
                    </p>
                  </div>
                </label>
                <p className={`text-xs mt-3 border rounded px-3 py-2 ${
                    bridgeStrategyApplies
                      ? 'text-amber-800 bg-amber-50 border-amber-200'
                      : 'text-orange-700 bg-orange-50 border-orange-200'
                  }`}>
                    Bridge / delayed super access is currently modelled in the Fixed Sustainable Income engine only.
                    {!bridgeStrategyApplies && ' Switch Strategy Type to Fixed to use these bucket settings.'}
                  </p>

              {enableBridgeStrategy && (
                <div className="mt-4 pt-4 border-t border-amber-100 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Super Access Age</label>
                      <input
                        type="number"
                        min="60"
                        max="100"
                        value={yourSuperAccessAge}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        onChange={(e) => setYourSuperAccessAge(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Super is not drawn before this age while the bridge model is enabled.</p>
                    </div>
                    {relationshipStatus === 'couple' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Partner Super Access Age</label>
                        <input
                          type="number"
                          min="60"
                          max="100"
                          value={partnerSuperAccessAge}
                          disabled={inputsLocked}
                          readOnly={inputsLocked}
                          onChange={(e) => setPartnerSuperAccessAge(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cash Bucket ($)</label>
                      <input
                        type="number"
                        min="0"
                        value={bridgeCashBucket}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        onChange={(e) => setBridgeCashBucket(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="e.g. 80000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Outside-Super Investment Bucket ($)</label>
                      <input
                        type="number"
                        min="0"
                        value={bridgeInvestmentBucket}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        onChange={(e) => setBridgeInvestmentBucket(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="e.g. 150000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Outside-Super Real Return (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={bridgeInvestmentReturnRate}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        onChange={(e) => setBridgeInvestmentReturnRate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                  </div>
                </div>
              )}
              </div>
            </details>
            <details
              id="mortgage-payoff-strategy-comparison"
              className="mt-4 border border-gray-200 rounded-lg bg-white overflow-hidden group"
              open={showMortgageComparison}
              onToggle={(e) => setShowMortgageComparison(e.currentTarget.open)}
            >
              <summary className="cursor-pointer list-none flex items-center justify-between gap-2 p-3 hover:bg-gray-50">
                <div>
                  <span className="text-sm font-semibold text-gray-800 inline-flex items-center gap-2">
                    <span>Mortgage Payoff Strategy Comparison</span>
                    <span onClick={(e) => e.stopPropagation()}>
                      <InfoTooltip content="Compare whether keeping mortgage repayments, paying off the loan from super or outside cash, or making a partial payoff gives a better Fixed Sustainable Income result. If the payoff source is outside cash, the calculator can also compare contributing that outside cash to super instead." />
                    </span>
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">Compare payoff, repayments, and super-funded lump sum options</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {enableMortgageComparison && (
                    <span className="text-xs font-medium bg-slate-100 text-slate-900 px-2 py-0.5 rounded-full">Enabled</span>
                  )}
                  <span className="text-gray-500 group-open:rotate-180 transition-transform inline-block" aria-hidden="true">▾</span>
                </div>
              </summary>
              <div className="border-t border-gray-200 p-3 bg-slate-50/60">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="enableMortgageComparison"
                    checked={!!enableMortgageComparison}
                    disabled={inputsLocked}
                    onChange={(e) => setEnableMortgageComparison(e.target.checked)}
                    className="mt-0.5 w-5 h-5 text-slate-700 border-gray-300 rounded focus:ring-slate-500"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-gray-800">Enable mortgage comparison</span>
                    <p className="text-xs text-gray-600 mt-1">
                      Compare debt payoff choices against lifestyle MSI, final super balance, mortgage balance, and total mortgage interest.
                    </p>
                  </div>
                </label>
                <p className="text-xs mt-3 border rounded px-3 py-2 text-slate-700 bg-white border-slate-200">
                  Mortgage strategy comparison is currently modelled in the Fixed Sustainable Income engine. Mortgage repayments and payoffs are treated as required outflows, so reported safe income is lifestyle income after mortgage costs.
                </p>

              {enableMortgageComparison && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mortgage Balance ($)</label>
                      <input
                        type="number"
                        min="0"
                        value={mortgageBalance}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        onChange={(e) => setMortgageBalance(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                        placeholder="e.g. 250000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Interest Rate (% p.a.)</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={mortgageInterestRate}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        onChange={(e) => setMortgageInterestRate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Annual Repayments ($)</label>
                      <input
                        type="number"
                        min="0"
                        value={mortgageAnnualRepayment}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        onChange={(e) => setMortgageAnnualRepayment(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                        placeholder="e.g. 24000"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Main Strategy</label>
                      <select
                        value={mortgageStrategy}
                        disabled={inputsLocked}
                        onChange={(e) => setMortgageStrategy(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                      >
                        <option value="keep">Keep paying</option>
                        <option value="immediatePayoff">Pay off immediately</option>
                        <option value="laterPayoff">Pay off later</option>
                        <option value="partialPayoff">Partial payoff</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payoff Funding Source</label>
                      <select
                        value={mortgagePayoffSource}
                        disabled={inputsLocked}
                        onChange={(e) => setMortgagePayoffSource(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                      >
                        <option value="super">Super lump sum withdrawal</option>
                        <option value="general">General retirement cashflow</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Schedule By</label>
                      <select
                        value={mortgagePayoffScheduleType}
                        disabled={inputsLocked}
                        onChange={(e) => setMortgagePayoffScheduleType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                      >
                        <option value="year">Plan year</option>
                        <option value="age">Your age</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payoff Age / Year</label>
                      <input
                        type="number"
                        min="1"
                        value={mortgagePayoffScheduleValue}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        onChange={(e) => setMortgagePayoffScheduleValue(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                        placeholder={mortgagePayoffScheduleType === 'age' ? 'e.g. 70' : 'e.g. 5'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Partial Payoff Amount ($)</label>
                      <input
                        type="number"
                        min="0"
                        value={mortgagePartialPayoffAmount}
                        disabled={inputsLocked}
                        readOnly={inputsLocked}
                        onChange={(e) => setMortgagePartialPayoffAmount(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>
              )}
              </div>
            </details>
            {/* Withdrawal Strategy */}
            <div className="bg-white p-6 rounded-lg shadow-md mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Withdrawal Strategy</h3>
              <div>
                <div className="mb-4">
                  <InfoTooltip content="Choose how withdrawals are set over time: fixed real income, dynamic rules, or guardrails.">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Strategy Type</label>
                  </InfoTooltip>
                  <StrategySelect value={withdrawalStrategy} onChange={handleStrategyChange} strategyOptions={strategyOptions} />
                </div>
                {String(withdrawalStrategy) === 'DynamicSWR' && (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    <div className="text-sm font-semibold text-blue-900 mb-2">Dynamic SWR</div>
                    <label className="block text-xs font-medium text-blue-800 mb-1">Expected Return Assumption (%) <InfoTooltip content="Your assumed real return rate for the annuity calculation. The strategy calculates annual withdrawals using the PMT formula based on current balance, this expected return, and years remaining to age 100. Higher assumptions mean higher initial withdrawals but faster depletion. Australian regulatory minimums always enforced: 4% (age 60-64), 5% (65-74), 6% (75-79), 7% (80-84), 9% (85-89), 11% (90-94), 14% (95+)." /></label>
                    <input 
                      type="number" 
                      step="0.1" 
                      value={dynInitPct} 
                      onChange={(e)=>{
                        const newVal = parseFloat(e.target.value)||0;
                        setDynInitPct(newVal);
                      }} 
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        if (val <= -5) {
                          alert('Error: Expected return assumption of -5% or less is unrealistically pessimistic.');
                          setDynInitPct(4);
                        } else if (val >= 10) {
                          alert('Warning: Expected real return of 10%+ is very optimistic. Typical assumptions are 3-6%.');
                        }
                      }}
                      className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                      autoFocus={false} 
                    />
                    {dynInitPct <= -5 && (
                      <div className="text-red-600 text-xs mt-1">⚠️ Expected return assumption should be greater than -5%</div>
                    )}
                    {dynInitPct >= 10 && (
                      <div className="text-orange-600 text-xs mt-1">⚠️ Very aggressive rate (10%+)</div>
                    )}
                    <div className="mt-2 text-xs text-blue-800">
                      Initial Withdrawal Rate: {(() => {
                        const age = Number(yourAge)||0;
                        const isCoupleStatus = relationshipStatus === 'couple';
                        const partnerSuperForDisplay = isCoupleStatus ? (Number(partnerSuper)||0) : 0;
                        const totalSuper = (Number(yourSuper)||0) + partnerSuperForDisplay;
                        const expectedReturn = (Number(dynInitPct)||4) / 100;
                        const yearsInPeriod = Math.max(1, Number(periodLength) || 35);
                        const __tbIn7 = Number(targetFinalBalance) || 0;
                        const targetBalance = __tbIn7 < 10 ? 10 : __tbIn7;
                        
                        // Calculate PMT to show initial withdrawal rate
                        const pmt = calculateAnnuityPayment(totalSuper, expectedReturn, yearsInPeriod, targetBalance);
                        const pmtRate = totalSuper > 0 ? (pmt / totalSuper) * 100 : 0;
                        
                        // Compare to regulatory minimum
                        const minRate = age < 65 ? 0 : age < 75 ? 5 : age < 80 ? 6 : age < 85 ? 7 : age < 90 ? 9 : age < 95 ? 11 : 14;
                        const effectiveRate = Math.max(pmtRate, minRate);
                        
                        return `${effectiveRate.toFixed(1)}% (PMT: ${pmtRate.toFixed(1)}%, Min: ${minRate}%)`;
                      })()}
                    </div>
                  </div>
                )}
                {String(withdrawalStrategy) === 'VanguardGuardrails' && (
                  <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg">
                    <div className="text-sm font-semibold text-purple-900 mb-2">Vanguard Guardrails</div>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-purple-800 mb-1">Initial Rate (%) <InfoTooltip content="Initial withdrawal rate as a % of current balance (real). Minimum required: 5% for age 65-74." /></label>
                        <input 
                          type="number" 
                          step="0.1" 
                          value={vgInitPct} 
                          onChange={(e)=>setVgInitPct(parseFloat(e.target.value)||0)} 
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            if (val <= 0) {
                              alert('Error: Initial Rate must be greater than 0%.');
                              setVgInitPct(4.5);
                            } else if (val >= 10) {
                              alert('Warning: Initial Rate of 10% or higher is very aggressive. Typical rates are 3-6%.');
                            }
                          }}
                          className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" 
                        />
                        {vgInitPct <= 0 && (
                          <div className="text-red-600 text-xs mt-1">⚠️ Initial Rate must be greater than 0%</div>
                        )}
                        {vgInitPct >= 10 && (
                          <div className="text-orange-600 text-xs mt-1">⚠️ Very aggressive rate (10%+)</div>
                        )}
                        <div className="mt-1 text-[11px] text-purple-800">Eff. Rate: {(() => {
                          const age = Number(yourAge)||0;
                          const userRate = Number(vgInitPct)||0;
                          const minRate = age < 65 ? 0 : age < 75 ? 5 : age < 80 ? 6 : age < 85 ? 7 : age < 90 ? 9 : age < 95 ? 11 : 14;
                          return `${Math.max(userRate, minRate).toFixed(1)}%`;
                        })()}</div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-purple-800 mb-1">Cut Spending by (%) <InfoTooltip content="If the withdrawal rate breaches the upper guardrail, reduce income by this % next year." /></label>
                        <input type="number" step="1" value={vgCutPct}  disabled={inputsLocked} readOnly={inputsLocked} onChange={(e)=>setVgCutPct(parseFloat(e.target.value)||0)}  className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-purple-800 mb-1">Increase Spending by (%) <InfoTooltip content="If the withdrawal rate falls below the lower guardrail, increase income by this % next year." /></label>
                        <input type="number" step="1" value={vgIncPct}  disabled={inputsLocked} readOnly={inputsLocked} onChange={(e)=>setVgIncPct(parseFloat(e.target.value)||0)}  className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-purple-800 mb-1">Upper Guardrail (+%) <InfoTooltip content="Upper withdrawal-rate band: if current income ÷ balance exceeds this %, trigger a cut next year." /></label>
                        <input type="number" step="1" value={vgUpperPct}  disabled={inputsLocked} readOnly={inputsLocked} onChange={(e)=>setVgUpperPct(parseFloat(e.target.value)||0)}  className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-purple-800 mb-1">Lower Guardrail (-%) <InfoTooltip content="Lower withdrawal-rate band: if current income ÷ balance falls below this %, trigger an increase next year." /></label>
                        <input type="number" step="1" value={vgLowerPct}  disabled={inputsLocked} readOnly={inputsLocked} onChange={(e)=>setVgLowerPct(parseFloat(e.target.value)||0)}  className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" />
                      </div>
                    </div>
                  </div>
                )}
                {String(withdrawalStrategy) === 'FloorAndCeiling' && (
                  <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                    <div className="text-sm font-semibold text-green-900 mb-2">Floor and Ceiling</div>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-green-800 mb-1">Initial % <InfoTooltip content="Starting income as a % of starting balance (real terms). Minimum required: 5% for age 65-74." /></label>
                        <input 
                          type="number" 
                          step="0.1" 
                          value={fcInitPct} 
                          onChange={(e)=>setFcInitPct(parseFloat(e.target.value)||0)} 
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            if (val <= 0) {
                              alert('Error: Initial % must be greater than 0%.');
                              setFcInitPct(5);
                            } else if (val >= 10) {
                              alert('Warning: Initial % of 10% or higher is very aggressive. Typical rates are 3-6%.');
                            }
                          }}
                          className="w-full px-3 py-2 border border-green-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" 
                        />
                        {fcInitPct <= 0 && (
                          <div className="text-red-600 text-xs mt-1">⚠️ Initial % must be greater than 0%</div>
                        )}
                        {fcInitPct >= 10 && (
                          <div className="text-orange-600 text-xs mt-1">⚠️ Very aggressive rate (10%+)</div>
                        )}
                        <div className="mt-1 text-[11px] text-green-800">Eff. Rate: {(() => {
                          const age = Number(yourAge)||0;
                          const userRate = Number(fcInitPct)||0;
                          const minRate = age < 65 ? 0 : age < 75 ? 5 : age < 80 ? 6 : age < 85 ? 7 : age < 90 ? 9 : age < 95 ? 11 : 14;
                          return `${Math.max(userRate, minRate).toFixed(1)}%`;
                        })()}</div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-green-800 mb-1">Floor (%) <InfoTooltip content="Minimum withdrawal rate as % of current balance. Income is never set below this." /></label>
                        <input 
                          type="number" 
                          step="0.1" 
                          value={fcFloorPct} 
                          onChange={(e)=>setFcFloorPct(parseFloat(e.target.value)||0)} 
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            if (val > fcInitPct) {
                              alert(`Error: Floor (${val}%) cannot be greater than Initial (${fcInitPct}%).`);
                            }
                            if (val > fcCeilPct) {
                              alert(`Error: Floor (${val}%) cannot be greater than Ceiling (${fcCeilPct}%).`);
                            }
                          }}
                          className="w-full px-3 py-2 border border-green-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" 
                        />
                        {fcFloorPct > fcInitPct && (
                          <div className="text-red-600 text-xs mt-1">⚠️ Floor cannot exceed Initial rate</div>
                        )}
                        {fcFloorPct > fcCeilPct && (
                          <div className="text-red-600 text-xs mt-1">⚠️ Floor cannot exceed Ceiling</div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-green-800 mb-1">Ceiling (%) <InfoTooltip content="Maximum withdrawal rate as % of current balance. Income is never set above this." /></label>
                        <input 
                          type="number" 
                          step="0.1" 
                          value={fcCeilPct} 
                          onChange={(e)=>setFcCeilPct(parseFloat(e.target.value)||0)} 
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            if (val < fcInitPct) {
                              alert(`Error: Ceiling (${val}%) cannot be less than Initial (${fcInitPct}%).`);
                            }
                            if (val < fcFloorPct) {
                              alert(`Error: Ceiling (${val}%) cannot be less than Floor (${fcFloorPct}%).`);
                            }
                          }}
                          className="w-full px-3 py-2 border border-green-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" 
                        />
                        {fcCeilPct < fcInitPct && (
                          <div className="text-red-600 text-xs mt-1">⚠️ Ceiling cannot be less than Initial rate</div>
                        )}
                        {fcCeilPct < fcFloorPct && (
                          <div className="text-red-600 text-xs mt-1">⚠️ Ceiling cannot be less than Floor</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <details
                  id="lump-sum-withdrawals"
                  className="mt-4 border border-gray-200 rounded-lg bg-white overflow-hidden group"
                  open={showLumpSumWithdrawals}
                  onToggle={(e) => setShowLumpSumWithdrawals(e.currentTarget.open)}
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-2 p-3 hover:bg-gray-50">
                    <div>
                      <span className="text-sm font-semibold text-gray-800 inline-flex items-center gap-2">
                        <span>Lump Sum Withdrawals</span>
                        <span onClick={(e) => e.stopPropagation()}>
                          <InfoTooltip content="Optional one-off super withdrawals on top of your regular strategy income, such as a renovation, caravan, car, pool, or helping children. Schedule by plan year or age; the calculator includes them in MSI, yearly results, charts, and exports." />
                        </span>
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">Optional one-off super draws on top of strategy income</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {hasLumpSumWithdrawals({ lumpSumWithdrawals }) && (
                        <span className="text-xs font-medium bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full">
                          {parseLumpSumWithdrawals({ lumpSumWithdrawals }).length} scheduled
                        </span>
                      )}
                      <span className="text-gray-500 group-open:rotate-180 transition-transform inline-block" aria-hidden="true">▾</span>
                    </div>
                  </summary>
                  <div className="border-t border-gray-200 p-3 bg-amber-50/40">
                    {(() => {
                      const normalizedLumpRows = normalizeLumpSumWithdrawalRows(lumpSumWithdrawals);
                      const hasTouchedLumpRows = normalizedLumpRows.some(isLumpSumRowTouched);
                      const shouldShowLumpFields = hasTouchedLumpRows || showLumpSumEntryRow;
                      return (
                        <>
                    <p className="text-xs text-amber-900 mb-3">
                      MSI is recalculated to include these drains. Schedule by age or plan year (year 1 = first year of projection). Couples: pro-rata from accessible super.
                    </p>
                    <div className="flex items-center justify-between gap-3 mb-3">
                      {parseLumpSumWithdrawals({ lumpSumWithdrawals }).length > 0 ? (
                        <ul className="text-xs text-amber-900 list-disc ml-4 space-y-0.5">
                          {parseLumpSumWithdrawals({ lumpSumWithdrawals }).map((row, i) => (
                            <li key={`lump-summary-${i}`}>{formatLumpSumRow(row)}{row.label ? ` — ${row.label}` : ''}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-amber-900">No lump sum withdrawals scheduled.</p>
                      )}
                      <div className="flex items-center gap-2">
                        {!shouldShowLumpFields && (
                          <button
                            type="button"
                            onClick={() => setShowLumpSumEntryRow(true)}
                            disabled={inputsLocked}
                            className="text-xs font-medium px-3 py-1.5 rounded border border-amber-300 text-amber-900 bg-white hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Add lump sum
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={clearAllLumpSumWithdrawals}
                          disabled={inputsLocked || (!hasTouchedLumpRows && !showLumpSumEntryRow)}
                          className="text-xs font-medium px-3 py-1.5 rounded border border-amber-300 text-amber-900 bg-white hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Clear all
                        </button>
                      </div>
                    </div>
                    {shouldShowLumpFields && (
                    <div className="space-y-3">
                      {getVisibleLumpSumRows(lumpSumWithdrawals).map(({ row, idx }) => (
                        <div key={`lump-sum-${idx}`} className="bg-white border border-amber-200 rounded-lg p-3">
                          <div className="flex items-center justify-between gap-2 mb-3">
                            <div className="text-xs font-semibold text-amber-900">Withdrawal {idx + 1}</div>
                            <button
                              type="button"
                              onClick={() => updateLumpSumWithdrawals((prev) => prev.map((r, i) => i === idx ? { scheduleType: 'year', scheduleValue: '', amount: '', label: '' } : r))}
                              disabled={inputsLocked || (String(row.scheduleValue ?? '').trim() === '' && String(row.amount ?? '').trim() === '' && String(row.label ?? '').trim() === '')}
                              className="text-xs px-2 py-1 rounded border border-amber-200 text-amber-900 bg-white hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Clear
                            </button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <label className="block">
                              <span className="block text-xs font-semibold text-amber-900 mb-1">When</span>
                              <select
                                value={row.scheduleType || 'year'}
                                onChange={(e) => updateLumpSumWithdrawals((prev) => prev.map((r, i) => i === idx ? { ...r, scheduleType: e.target.value } : r))}
                                disabled={inputsLocked}
                                className="w-full px-2 py-1.5 border border-amber-200 rounded text-sm bg-white disabled:bg-gray-100"
                              >
                                <option value="age">Age</option>
                                <option value="year">Plan year</option>
                              </select>
                            </label>
                            <label className="block">
                              <span className="block text-xs font-semibold text-amber-900 mb-1">Age / Year</span>
                              <input
                                type="number"
                                min="1"
                                value={row.scheduleValue ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  updateLumpSumWithdrawals((prev) => prev.map((r, i) => {
                                    if (i !== idx) return r;
                                    const scheduleValue = Number(value);
                                    const minCurrentAge = Math.min(Number(yourAge) || Infinity, Number(partnerAge ?? yourAge) || Infinity);
                                    const shouldTreatAsPlanYear = (r.scheduleType || 'year') === 'age'
                                      && Number.isFinite(scheduleValue)
                                      && scheduleValue > 0
                                      && scheduleValue <= Number(periodLength || 0)
                                      && scheduleValue < minCurrentAge;
                                    return {
                                      ...r,
                                      scheduleValue: value,
                                      scheduleType: shouldTreatAsPlanYear ? 'year' : (r.scheduleType || 'year'),
                                    };
                                  }));
                                }}
                                disabled={inputsLocked}
                                className="w-full px-2 py-1.5 border border-amber-200 rounded text-sm disabled:bg-gray-100"
                                placeholder={(row.scheduleType || 'year') === 'year' ? '4' : String(yourAge || 64)}
                              />
                            </label>
                            <label className="block">
                              <span className="block text-xs font-semibold text-amber-900 mb-1">Amount ($)</span>
                              <input
                                type="number"
                                min="0"
                                step="1000"
                                value={row.amount ?? ''}
                                onChange={(e) => updateLumpSumWithdrawals((prev) => prev.map((r, i) => i === idx ? { ...r, amount: e.target.value } : r))}
                                disabled={inputsLocked}
                                className="w-full px-2 py-1.5 border border-amber-200 rounded text-sm disabled:bg-gray-100"
                                placeholder="40000"
                              />
                            </label>
                            <label className="block">
                              <span className="block text-xs font-semibold text-amber-900 mb-1">Label</span>
                              <input
                                type="text"
                                value={row.label ?? ''}
                                onChange={(e) => updateLumpSumWithdrawals((prev) => prev.map((r, i) => i === idx ? { ...r, label: e.target.value } : r))}
                                disabled={inputsLocked}
                                className="w-full px-2 py-1.5 border border-amber-200 rounded text-sm disabled:bg-gray-100"
                                placeholder="Caravan, renovation..."
                              />
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                        </>
                      );
                    })()}
                  </div>
                </details>
                <details
                  id="lump-sum-contributions"
                  className="mt-4 border border-gray-200 rounded-lg bg-white overflow-hidden group"
                  open={showLumpSumContributions}
                  onToggle={(e) => setShowLumpSumContributions(e.currentTarget.open)}
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-2 p-3 hover:bg-gray-50">
                    <div>
                      <span className="text-sm font-semibold text-gray-800 inline-flex items-center gap-2">
                        <span>Lump Sum Contributions</span>
                        <span onClick={(e) => e.stopPropagation()}>
                          <InfoTooltip content="Optional one-off payments into super. Choose the recipient and contribution type; the calculator adds them to member balances, includes them in earnings, counts SG toward concessional caps, and flags likely cap issues." />
                        </span>
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">Optional one-off payments into super with cap warnings</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {hasLumpSumContributions({ lumpSumContributions }) && (
                        <span className="text-xs font-medium bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded-full">
                          {parseLumpSumContributions({ lumpSumContributions }).length} scheduled
                        </span>
                      )}
                      <span className="text-gray-500 group-open:rotate-180 transition-transform inline-block" aria-hidden="true">▾</span>
                    </div>
                  </summary>
                  <div className="border-t border-gray-200 p-3 bg-emerald-50/40">
                    {(() => {
                      const normalizedContributionRows = normalizeLumpSumContributionRows(lumpSumContributions);
                      const hasTouchedContributionRows = normalizedContributionRows.some(isLumpSumContributionRowTouched);
                      const shouldShowContributionFields = hasTouchedContributionRows || showLumpSumContributionEntryRow;
                      const parsedContributions = parseLumpSumContributions({ lumpSumContributions });
                      return (
                        <>
                          <p className="text-xs text-emerald-900 mb-3">
                            Contributions are added to the selected member account before that year&apos;s earnings. SG in phased retirement counts toward concessional cap checks.
                          </p>
                          <div className="flex items-center justify-between gap-3 mb-3">
                            {parsedContributions.length > 0 ? (
                              <ul className="text-xs text-emerald-900 list-disc ml-4 space-y-0.5">
                                {parsedContributions.map((row, i) => (
                                  <li key={`contribution-summary-${i}`}>{formatLumpSumContributionRow(row)}{row.label ? ` — ${row.label}` : ''}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-emerald-900">No lump sum contributions scheduled.</p>
                            )}
                            <div className="flex items-center gap-2">
                              {!shouldShowContributionFields && (
                                <button
                                  type="button"
                                  onClick={() => setShowLumpSumContributionEntryRow(true)}
                                  disabled={inputsLocked}
                                  className="text-xs font-medium px-3 py-1.5 rounded border border-emerald-300 text-emerald-900 bg-white hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  Add contribution
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={clearAllLumpSumContributions}
                                disabled={inputsLocked || (!hasTouchedContributionRows && !showLumpSumContributionEntryRow)}
                                className="text-xs font-medium px-3 py-1.5 rounded border border-emerald-300 text-emerald-900 bg-white hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Clear all
                              </button>
                            </div>
                          </div>
                          {shouldShowContributionFields && (
                            <div className="space-y-3">
                              {getVisibleLumpSumContributionRows(lumpSumContributions).map(({ row, idx }) => (
                                <div key={`lump-contribution-${idx}`} className="bg-white border border-emerald-200 rounded-lg p-3">
                                  <div className="flex items-center justify-between gap-2 mb-3">
                                    <div className="text-xs font-semibold text-emerald-900">Contribution {idx + 1}</div>
                                    <button
                                      type="button"
                                      onClick={() => updateLumpSumContributions((prev) => prev.map((r, i) => i === idx ? { recipient: 'you', contributionType: 'nonConcessional', scheduleType: 'year', scheduleValue: '', amount: '', label: '' } : r))}
                                      disabled={inputsLocked || (String(row.scheduleValue ?? '').trim() === '' && String(row.amount ?? '').trim() === '' && String(row.label ?? '').trim() === '')}
                                      className="text-xs px-2 py-1 rounded border border-emerald-200 text-emerald-900 bg-white hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                      Clear
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <label className="block">
                                      <span className="block text-xs font-semibold text-emerald-900 mb-1">Recipient</span>
                                      <select
                                        value={row.recipient || 'you'}
                                        onChange={(e) => updateLumpSumContributions((prev) => prev.map((r, i) => i === idx ? { ...r, recipient: e.target.value } : r))}
                                        disabled={inputsLocked}
                                        className="w-full px-2 py-1.5 border border-emerald-200 rounded text-sm bg-white disabled:bg-gray-100"
                                      >
                                        <option value="you">You</option>
                                        {relationshipStatus === 'couple' && <option value="partner">Partner</option>}
                                        {relationshipStatus === 'couple' && <option value="split">Split 50/50</option>}
                                      </select>
                                    </label>
                                    <label className="block">
                                      <span className="block text-xs font-semibold text-emerald-900 mb-1">Type</span>
                                      <select
                                        value={row.contributionType || 'nonConcessional'}
                                        onChange={(e) => updateLumpSumContributions((prev) => prev.map((r, i) => i === idx ? { ...r, contributionType: e.target.value } : r))}
                                        disabled={inputsLocked}
                                        className="w-full px-2 py-1.5 border border-emerald-200 rounded text-sm bg-white disabled:bg-gray-100"
                                      >
                                        <option value="nonConcessional">Non-concessional</option>
                                        <option value="concessional">Concessional / personal deductible</option>
                                        <option value="downsizer">Downsizer</option>
                                        <option value="spouse">Spouse contribution</option>
                                      </select>
                                    </label>
                                    <label className="block">
                                      <span className="block text-xs font-semibold text-emerald-900 mb-1">When</span>
                                      <select
                                        value={row.scheduleType || 'year'}
                                        onChange={(e) => updateLumpSumContributions((prev) => prev.map((r, i) => i === idx ? { ...r, scheduleType: e.target.value } : r))}
                                        disabled={inputsLocked}
                                        className="w-full px-2 py-1.5 border border-emerald-200 rounded text-sm bg-white disabled:bg-gray-100"
                                      >
                                        <option value="age">Age</option>
                                        <option value="year">Plan year</option>
                                      </select>
                                    </label>
                                    <label className="block">
                                      <span className="block text-xs font-semibold text-emerald-900 mb-1">Age / Year</span>
                                      <input
                                        type="number"
                                        min="1"
                                        value={row.scheduleValue ?? ''}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          updateLumpSumContributions((prev) => prev.map((r, i) => {
                                            if (i !== idx) return r;
                                            const scheduleValue = Number(value);
                                            const minCurrentAge = Math.min(Number(yourAge) || Infinity, Number(partnerAge ?? yourAge) || Infinity);
                                            const shouldTreatAsPlanYear = (r.scheduleType || 'year') === 'age'
                                              && Number.isFinite(scheduleValue)
                                              && scheduleValue > 0
                                              && scheduleValue <= Number(periodLength || 0)
                                              && scheduleValue < minCurrentAge;
                                            return {
                                              ...r,
                                              scheduleValue: value,
                                              scheduleType: shouldTreatAsPlanYear ? 'year' : (r.scheduleType || 'year'),
                                            };
                                          }));
                                        }}
                                        disabled={inputsLocked}
                                        className="w-full px-2 py-1.5 border border-emerald-200 rounded text-sm disabled:bg-gray-100"
                                        placeholder={(row.scheduleType || 'year') === 'year' ? '4' : String(yourAge || 64)}
                                      />
                                    </label>
                                    <label className="block">
                                      <span className="block text-xs font-semibold text-emerald-900 mb-1">Amount ($)</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="1000"
                                        value={row.amount ?? ''}
                                        onChange={(e) => updateLumpSumContributions((prev) => prev.map((r, i) => i === idx ? { ...r, amount: e.target.value } : r))}
                                        disabled={inputsLocked}
                                        className="w-full px-2 py-1.5 border border-emerald-200 rounded text-sm disabled:bg-gray-100"
                                        placeholder="50000"
                                      />
                                    </label>
                                    <label className="block">
                                      <span className="block text-xs font-semibold text-emerald-900 mb-1">Label</span>
                                      <input
                                        type="text"
                                        value={row.label ?? ''}
                                        onChange={(e) => updateLumpSumContributions((prev) => prev.map((r, i) => i === idx ? { ...r, label: e.target.value } : r))}
                                        disabled={inputsLocked}
                                        className="w-full px-2 py-1.5 border border-emerald-200 rounded text-sm disabled:bg-gray-100"
                                        placeholder="Inheritance, downsizer..."
                                      />
                                    </label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </details>
              </div>
            </div>
          </div>

          {/* Right Column - Monte Carlo */}
          <div className="lg:col-span-1 space-y-6">
            {/* Monte Carlo Target Income */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Monte Carlo Analysis</h3>
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h4 className="text-md font-semibold text-blue-800 mb-2">Target Income <InfoTooltip content="Tests whether this annual income (today’s dollars) can be sustained across 1,000 random market paths. Each path year draws a random historical year; CPI and returns come from that same year." /></h4>
                <p className="text-xs text-blue-700 mb-3">Enter the annual income you want to test. The headline success % uses this fixed target; year-by-year tables follow your withdrawal strategy (e.g. floor and ceiling).</p>
                <input
                  type="number"
                  value={monteCarloTargetIncome}
                            onChange={(e) => setMonteCarloTargetIncome(Math.max(0, parseInt(e.target.value) || 0))}
                  title="Target annual income to test across many market sequences"
                  placeholder="Enter target income (e.g., 80000)"
                  className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <details className="mt-3 group">
                  <summary className="cursor-pointer text-sm font-semibold text-blue-800 list-none flex items-center gap-1">
                    <span className="text-blue-600 group-open:rotate-90 transition-transform inline-block" aria-hidden="true">▸</span>
                    How inflation is modelled
                  </summary>
                  <ul className="mt-2 ml-4 list-disc space-y-1.5 text-xs text-blue-800">
                    <li><strong>Historical run:</strong> actual year-by-year CPI from your chosen start period, in sequence with that period’s returns.</li>
                    <li><strong>Monte Carlo:</strong> each simulated year picks one random historical year; that year’s CPI and portfolio returns are used together to obtain a real return (after fees).</li>
                  </ul>
                  <p className="mt-2 text-xs text-blue-700">
                    <a href="/guides/advanced-calculator-inflation-and-monte-carlo.html" target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-blue-900">Full guide: inflation &amp; Monte Carlo success %</a>
                  </p>
                </details>
              </div>
            </div>
          </div>
          {/* Bottom Action Bar: spans all three columns, sticky so Run button stays visible when scrolling */}
          <div className="lg:col-span-3 sticky bottom-0 z-40 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] mt-6 py-4 -mx-2 px-2 sm:mx-0 sm:px-0">
            <div>
              <button
                ref={(el) => { calculateButtonRef.current = el; runButtonRef.current = el; }}
                type="button"
                onClick={async (e) => {
                  try {
                    // Gate: check free run availability for non-subscribers
                    if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.ADVANCED_CALCULATOR)) {
                      const runCheck = SubscriptionManager.canRunAdvancedFree();
                      if (!runCheck.canRun) {
                        if (runCheck.reason === 'needs_email') {
                          setShowFreeRunsEmailGate(true);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        } else {
                          if (typeof gtag !== 'undefined') {
                            gtag('event', 'free_runs_exhausted_upgrade_prompt', { event_category: 'conversion', event_label: 'Advanced', value: 1, calculator_tier: 'advanced' });
                          }
                          // Soft pricing — browse plans; PDF stays direct checkout elsewhere.
                          openPricingModal({
                            source: 'advanced_gate',
                            calculatorTier: 'advanced',
                            surface: 'free_runs_exhausted_run_button',
                          });
                        }
                        return;
                      }
                      // Free run is recorded only after a successful result (Phase 1).
                    }
                    // Warn when a couple has different retirement ages but phased retirement is off
                    const _isCouple = relationshipStatus === 'couple';
                    const _diffRetAge = _isCouple && !phasedRetirement &&
                        Number(yourRetirementAge) !== Number(partnerRetirementAge);
                    if (_diffRetAge) {
                      const proceed = window.confirm(
                        'Your partners are retiring at different ages. For accurate results, please enable "Phased Retirement" above.\n\nClick OK to run anyway, or Cancel to go back and tick the box.'
                      );
                      if (!proceed) return;
                    }

                    {
                      const hasSub = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.ADVANCED_CALCULATOR);
                      const freeStatus = hasSub ? null : SubscriptionManager.canRunAdvancedFree();
                      trackAdvancedRunClicked({
                        platform: typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop',
                        relationshipStatus,
                        hasSubscription: hasSub,
                        freeRunsRemaining: freeStatus?.remaining,
                      });
                      // Legacy name — prefer advanced_run_clicked for Phase 0 reporting
                      if (typeof gtag !== 'undefined') {
                        gtag('event', 'calculator_started', {
                          event_category: 'funnel',
                          calculator_tier: 'advanced',
                          relationship_status: relationshipStatus,
                          has_subscription: hasSub
                        });
                      }
                    }

                    // Show spinner; invalidate any deferred Monte Carlo from a prior run
                    monteCarloRunIdRef.current += 1;
                    setIsMonteCarloRunning(false);
                    setIsCalculating(true);
                    
                    // Wait for spinner to render (double RAF + timeout)
                    await new Promise(resolve => {
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                          setTimeout(resolve, 100);
                        });
                      });
                    });
                    
                    const start = Number(startYear) || 1980;
                    const years = Math.max(0, Number(periodLength) || 0);

                    const incompleteLumpRows = (lumpSumWithdrawalsRef.current || lumpSumWithdrawals || []).filter((r) => {
                      const hasAmount = Number(r.amount) > 0;
                      const hasSchedule = Number(r.scheduleValue) > 0;
                      const partialAmount = String(r.amount ?? '').trim() !== '' && Number(r.amount) > 0;
                      const partialSchedule = String(r.scheduleValue ?? '').trim() !== '';
                      return (partialAmount || partialSchedule) && !(hasAmount && hasSchedule);
                    });
                    if (incompleteLumpRows.length > 0) {
                      setIsCalculating(false);
                      setShowLumpSumWithdrawals(true);
                      document.getElementById('lump-sum-withdrawals')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      alert('Lump sum withdrawals: each row needs both Age/Plan year # and Amount ($) to be included in the run.');
                      return;
                    }

                    const lumpsForRun = coerceLumpSumRowsForProjection(lumpSumWithdrawalsRef.current || lumpSumWithdrawals, {
                      yourAge,
                      partnerAge,
                      periodLength: years,
                    });
                    const lumpDiagnostics = getLumpSumScheduleDiagnostics(
                      { lumpSumWithdrawals: lumpsForRun },
                      { yourAge, partnerAge, periodLength: years }
                    );
                    if (lumpDiagnostics.parsed.length > 0 && lumpDiagnostics.firingYears.length === 0) {
                      setIsCalculating(false);
                      setShowLumpSumWithdrawals(true);
                      document.getElementById('lump-sum-withdrawals')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      alert(
                        'Your lump sum schedule does not fall within this projection period (no matching age or plan year in the next '
                        + years + ' years). Adjust Age/Plan year or extend Period Length.'
                      );
                      return;
                    }
                    const parsedLumpsForRun = lumpDiagnostics.parsed;

                    const incompleteContributionRows = (lumpSumContributionsRef.current || lumpSumContributions || []).filter((r) => {
                      const hasAmount = Number(r.amount) > 0;
                      const hasSchedule = Number(r.scheduleValue) > 0;
                      const partialAmount = String(r.amount ?? '').trim() !== '' && Number(r.amount) > 0;
                      const partialSchedule = String(r.scheduleValue ?? '').trim() !== '';
                      return (partialAmount || partialSchedule) && !(hasAmount && hasSchedule);
                    });
                    if (incompleteContributionRows.length > 0) {
                      setIsCalculating(false);
                      setShowLumpSumContributions(true);
                      document.getElementById('lump-sum-contributions')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      alert('Lump sum contributions: each row needs both Age/Plan year # and Amount ($) to be included in the run.');
                      return;
                    }

                    const contributionsForRun = coerceLumpSumContributionRowsForProjection(lumpSumContributionsRef.current || lumpSumContributions, {
                      yourAge,
                      partnerAge,
                      periodLength: years,
                    });
                    const contributionDiagnostics = getLumpSumContributionScheduleDiagnostics(
                      { lumpSumContributions: contributionsForRun },
                      { yourAge, partnerAge, periodLength: years }
                    );
                    if (contributionDiagnostics.parsed.length > 0 && contributionDiagnostics.firingYears.length === 0) {
                      setIsCalculating(false);
                      setShowLumpSumContributions(true);
                      document.getElementById('lump-sum-contributions')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      alert(
                        'Your lump sum contribution schedule does not fall within this projection period (no matching age or plan year in the next '
                        + years + ' years). Adjust Age/Plan year or extend Period Length.'
                      );
                      return;
                    }
                    const parsedContributionsForRun = contributionDiagnostics.parsed;

                    let allocSp500 = Number(sp500) || 0;
                    let allocAus = Number(ausShares) || 0;
                    let allocIntl = Number(intlShares) || 0;
                    let allocBonds = Number(bonds) || 0;
                    let allocCash = Number(cash) || 0;
                    let totalAllocNow = allocSp500 + allocAus + allocIntl + allocBonds + allocCash;
                    // All-zero saved weights = unset → apply balanced 100% mix (same as new visitors).
                    if (Math.abs(totalAllocNow) < 0.5) {
                      allocSp500 = 35;
                      allocAus = 15;
                      allocIntl = 20;
                      allocBonds = 20;
                      allocCash = 10;
                      totalAllocNow = 100;
                      setSp500(35);
                      setAusShares(15);
                      setIntlShares(20);
                      setBonds(20);
                      setCash(10);
                    }
                    if (transferFromSMSF) {
                      // For SMSF transfers, productive assets (shares, infrastructure, cash, bonds) must sum to 100%
                      const infrastructurePctVal = Number(infrastructurePct||0);
                      const productiveTotal = totalAllocNow + infrastructurePctVal;
                      if (Math.abs(productiveTotal - 100) > 0.5) {
                        setIsCalculating(false);
                        alert(`Productive assets must sum to 100%.\n\nCurrent breakdown:\n• Direct Shares (ASX): ${allocAus.toFixed(1)}%\n• Infrastructure: ${infrastructurePctVal.toFixed(1)}%\n• Cash: ${allocCash.toFixed(1)}%\n• S&P 500: ${allocSp500.toFixed(1)}%\n• Intl Shares: ${allocIntl.toFixed(1)}%\n• Bonds: ${allocBonds.toFixed(1)}%\n\nTotal: ${productiveTotal.toFixed(1)}%`);
                        return;
                      }
                    } else {
                      if (Math.round(totalAllocNow) !== 100) {
                        setIsCalculating(false);
                        alert(`Asset allocation must total 100%. Current total: ${totalAllocNow}%`);
                        return;
                      }
                    }
                  
                  // PHASED RETIREMENT: Disabled custom engine - will integrate into standard calculation below
                  // TODO: Modify the standard historical calculation to handle phased retirement
                  
                  const isCoupleStatus = relationshipStatus === 'couple';
                  // Force partner super to 0 if single relationship status
                  const partnerSuperForCalc = isCoupleStatus ? (Number(partnerSuper) || 0) : 0;
                  const totalSuper = (Number(yourSuper) || 0) + partnerSuperForCalc;
                  const weights = {
                    sp500: allocSp500,
                    aus: allocAus,
                    intl: allocIntl,
                    bonds: allocBonds,
                    cash: allocCash,
                    frank: Number.isFinite(Number(frankingCredits)) ? Number(frankingCredits) : 0,
                  };
                  if (years <= 0 || totalSuper <= 0) {
                    setHistoricalResults({ sustainableIncome: 0, finalRealBalance: 0 });
                    setShowHistorical(true);
                    setIsCalculating(false);
                    return;
                  }

                  // ONE returns builder — used by main MSI, rolling periods, and dynamic strategies
                  const buildReturnsForWindow = (windowStart, len) => {
                    const wr = [];
                    for (let i = 0; i < len; i++) {
                      const year = windowStart + i;
                      const yearData = historicalData[year];
                      if (!yearData) return null;
                      const portfolio = {
                        sp500: weights.sp500,
                        ausShares: weights.aus,
                        intlShares: weights.intl,
                        bonds: weights.bonds,
                        cash: weights.cash,
                        frankingCredits: weights.frank,
                        infrastructurePct: Number(infrastructurePct) || 0,
                        infrastructureReturnRate: Number(infrastructureReturnRate) || 0,
                      };
                      const nominalR = Number(SimulationEngine.getPortfolioReturn(yearData, portfolio)) || 0;
                      const histInfl = historicalData[year]?.inflation || 0;
                      const netR = ((1 + nominalR) / (1 + histInfl)) - 1;
                      wr.push({ year, r: netR });
                    }
                    return wr;
                  };

                  const returnsByYear = buildReturnsForWindow(start, years);
                  if (!returnsByYear) {
                    setHistoricalResults({ sustainableIncome: 0, finalRealBalance: 0 });
                    setShowHistorical(true);
                    setIsCalculating(false);
                    return;
                  }
                  const sumR = returnsByYear.reduce((acc, { r }) => acc + r, 0);


                  // Snapshot last-run inputs and returns for What-If reuse (single source of truth)
                  try {
                    lastReturnsByYearRef.current = returnsByYear.slice();
                    lastRunInputsRef.current = {
                      relationshipStatus,
                      yourAge,
                      partnerAge,
                      yourSuper,
                      partnerSuper,
                      includePension,
                      isHomeowner,
                      periodLength: years,
                      startYear: start,
                      adminFees,
                      adminFeePercentage,
                      investmentFees,
                      insuranceFees,
                      investmentFeeCap,
                      sp500, ausShares, intlShares, bonds, cash, frankingCredits,
                      phasedRetirement,
                      yourRetirementAge,
                      partnerRetirementAge,
                      enableBridgeStrategy: enableBridgeStrategy && bridgeStrategyApplies,
                      yourSuperAccessAge,
                      partnerSuperAccessAge,
                      bridgeCashBucket,
                      bridgeInvestmentBucket,
                      bridgeInvestmentReturnRate,
                      enableMortgageComparison,
                      mortgageBalance,
                      mortgageInterestRate,
                      mortgageAnnualRepayment,
                      mortgageStrategy,
                      mortgagePayoffSource,
                      mortgagePayoffScheduleType,
                      mortgagePayoffScheduleValue,
                      mortgagePartialPayoffAmount,
                      yourSalary,
                      partnerSalary,
                      msiStartsWhenEitherRetires: phasedRetirement && relationshipStatus === 'couple'
                        ? msiStartsWhenEitherRetires
                        : true,
                      pensionGrowthRate,
                      generalTransferBalanceCap,
                      yourTbcUsed,
                      partnerTbcUsed,
                      targetFinalBalance,
                      monteCarloTargetIncome,
                      otherTaxableIncome,
                      otherNonTaxableIncome,
                      otherAssets,
                      annuityAnnualPayment,
                      definedBenefitPensionAmount,
                      residentialPropertyValue,
                      residentialRentalIncome,
                      residentialPropertyCapitalGrowth,
                      commercialPropertyValue,
                      commercialRentalIncome,
                      commercialPropertyCapitalGrowth,
                      collectiblesValue,
                      collectiblesCapitalGrowth,
                      otherAlternativesValue,
                      otherAlternativesCapitalGrowth,
                      infrastructureValue,
                      infrastructurePct,
                      infrastructureReturnRate,
                      annuityIsIndexed,
                      definedBenefitIsIndexed,
                      rentPaidFortnightly,
                      allowWithdrawalsBeforeRetirementAge,
                      enableBridgeStrategy: enableBridgeStrategy && bridgeStrategyApplies,
                      yourSuperAccessAge,
                      partnerSuperAccessAge,
                      bridgeCashBucket,
                      bridgeInvestmentBucket,
                      bridgeInvestmentReturnRate,
                      enableMortgageComparison,
                      mortgageBalance,
                      mortgageInterestRate,
                      mortgageAnnualRepayment,
                      mortgageStrategy,
                      mortgagePayoffSource,
                      mortgagePayoffScheduleType,
                      mortgagePayoffScheduleValue,
                      mortgagePartialPayoffAmount,
                      lumpSumWithdrawals: lumpsForRun,
                      parsedLumpSumWithdrawals: parsedLumpsForRun,
                      lumpSumContributions: contributionsForRun,
                      parsedLumpSumContributions: parsedContributionsForRun,
                      fcInitPct,
                      fcFloorPct,
                      fcCeilPct,
                      vgInitPct,
                      vgUpperPct,
                      vgLowerPct,
                      dynInitPct,
                    };
                    lastRunStrategyRef.current = String(withdrawalStrategy) || 'fixed';
                  } catch (_snapshotErr) {
                    console.error('What-If snapshot capture failed:', _snapshotErr && _snapshotErr.message);
                  }
                  // Compute income series depending on strategy
                  const includeAgePension = !!includePension;
                  const homeowner = !!isHomeowner;
                  const isCouple = String(relationshipStatus) === 'couple';
                  const other = Number(otherTaxableIncome) || 0;
                  const otherNonTax = Number(otherNonTaxableIncome) || 0;
                  const otherAssetsValue = Number(otherAssets) || 0;
                  const annuityPay = Number(annuityAnnualPayment) || 0;
                  const dbPension = Number(definedBenefitPensionAmount) || 0;
                  const pensionGrowthRateVal = Number(pensionGrowthRate) || 0;
                  // Calculate fees properly to match user's settings
                  const adminFeesNum = Number(adminFees) || 52;
                  const adminFeePercentageNum = Number(adminFeePercentage) || 0.10;
                  const investmentFeesNum = Number(investmentFees) || 0;
                  const insuranceFeesNum = Number(insuranceFees) || 0;
                  
                  const calculateFees = (balance) => {
                    // Admin fees: flat fee + percentage
                    let adminFeeAmount = adminFeesNum + (balance * adminFeePercentageNum / 100);
                    
                    // Apply admin fee cap when using AustralianSuper defaults
                    if (adminFeesNum === 52 && adminFeePercentageNum === 0.10) {
                      adminFeeAmount = Math.min(adminFeeAmount, 350);
                    }
                    
                    // Investment fees: use user's custom rate
                    const investmentFeeAmount = balance * (investmentFeesNum / 100);
                    
                    // Transaction costs: 0.08% (standard) - only apply if fees are not explicitly set to zero
                    const transactionCostAmount = (adminFeesNum === 0 && adminFeePercentageNum === 0 && investmentFeesNum === 0 && insuranceFeesNum === 0) 
                        ? 0 
                        : balance * 0.0008;
                    
                    return adminFeeAmount + investmentFeeAmount + transactionCostAmount + insuranceFeesNum;
                  };
                  const getYearlyOtherIncome = () => {
                    try {
                      const result = other + otherNonTax + annuityPay + dbPension;
                      return result;
                    } catch (error) {
                      // Error in getYearlyOtherIncome
                      return 0;
                    }
                  };
                  const strategy = String(withdrawalStrategy);
                  let sustainable = 0;
                  let balance = 0;
                  let incomeByYear = [];
                  let yearlyDataForGraphs = [];

                  // Helper function to generate detailed yearly data for graphs
                  const generateDetailedYearlyData = (incomeArray, startYear, totalYears) => {
                    return incomeArray.map((income, index) => ({
                      year: startYear + index,
                      yearIndex: index + 1,
                      balance: 0, // Will be calculated in detailed view
                      withdrawalFromSuper: 0, // Will be calculated in detailed view
                      pension: 0, // Will be calculated in detailed view
                      otherTaxableIncome: 0, // Will be calculated in detailed view
                      otherNonTaxableIncome: 0, // Will be calculated in detailed view
                      realReturn: 0, // Will be calculated in detailed view
                      totalIncome: income
                    }));
                  };
                  // Build historical returns for any start year — same formula as main returnsByYear loop
                  const buildSharedStrategyInputs = (overrides = {}) => {
                    const windowStart = overrides.startYear ?? start;
                    const windowLen = overrides.periodLength ?? years;
                    const resolvedReturns = buildReturnsForWindow(windowStart, windowLen);
                    if (!resolvedReturns) return null;
                    const lumpsForWindow = overrides.parsedLumpSumWithdrawals ?? parsedLumpsForRun;
                    return {
                      relationshipStatus,
                      yourAge,
                      partnerAge,
                      yourSuper,
                      partnerSuper,
                      includePension,
                      isHomeowner,
                      periodLength: windowLen,
                      startYear: windowStart,
                      adminFees,
                      adminFeePercentage,
                      investmentFees,
                      insuranceFees,
                      investmentFeeCap,
                      sp500, ausShares, intlShares, bonds, cash, frankingCredits,
                      phasedRetirement,
                      yourRetirementAge,
                      partnerRetirementAge,
                      yourSalary,
                      partnerSalary,
                      yourPartTimeEmploymentIncome,
                      partnerPartTimeEmploymentIncome,
                      msiStartsWhenEitherRetires: phasedRetirement && relationshipStatus === 'couple'
                        ? msiStartsWhenEitherRetires
                        : true,
                      pensionGrowthRate,
                      generalTransferBalanceCap,
                      yourTbcUsed,
                      partnerTbcUsed,
                      targetFinalBalance,
                      monteCarloTargetIncome,
                      otherTaxableIncome,
                      otherNonTaxableIncome,
                      otherAssets,
                      annuityAnnualPayment,
                      annuityIsIndexed,
                      definedBenefitPensionAmount,
                      definedBenefitIsIndexed,
                      residentialPropertyValue,
                      residentialRentalIncome,
                      residentialPropertyCapitalGrowth,
                      commercialPropertyValue,
                      commercialRentalIncome,
                      commercialPropertyCapitalGrowth,
                      collectiblesValue,
                      collectiblesCapitalGrowth,
                      otherAlternativesValue,
                      otherAlternativesCapitalGrowth,
                      infrastructureValue,
                      infrastructurePct,
                      infrastructureReturnRate,
                      providedReturns: resolvedReturns,
                      rentPaidFortnightly,
                      allowWithdrawalsBeforeRetirementAge,
                      enableBridgeStrategy: enableBridgeStrategy && bridgeStrategyApplies,
                      yourSuperAccessAge,
                      partnerSuperAccessAge,
                      bridgeCashBucket,
                      bridgeInvestmentBucket,
                      bridgeInvestmentReturnRate,
                      enableMortgageComparison,
                      mortgageBalance,
                      mortgageInterestRate,
                      mortgageAnnualRepayment,
                      mortgageStrategy,
                      mortgagePayoffSource,
                      mortgagePayoffScheduleType,
                      mortgagePayoffScheduleValue,
                      mortgagePartialPayoffAmount,
                      lumpSumWithdrawals: lumpsForRun,
                      parsedLumpSumWithdrawals: lumpsForWindow,
                      lumpSumContributions: contributionsForRun,
                      parsedLumpSumContributions: parsedContributionsForRun,
                    };
                  };

                  // ONE Fixed MSI engine — main run and every rolling window call this
                  const computeFixedMSIForWindow = (windowStart, windowLen) => {
                    const inputs = buildSharedStrategyInputs({ startYear: windowStart, periodLength: windowLen });
                    if (!inputs) return { avgIncome: 0, incomeByYear: [], finalBalance: 0 };
                    return computeFixedMSIShared(inputs);
                  };

                  const computeFloorCeilingSeries = (windowOverrides = null) => {
                    const baseInputs = windowOverrides
                      ? buildSharedStrategyInputs(windowOverrides)
                      : buildSharedStrategyInputs();
                    if (!baseInputs) return 0;
                    const result = computeFCSeriesShared({
                      ...baseInputs,
                      fcInitPct,
                      fcFloorPct,
                      fcCeilPct
                    });
                    incomeByYear = result.incomeByYear || [];
                    return Math.round(result.avgIncome || 0);
                  };

                  const computeVanguardGuardrailsSeries = (windowOverrides = null) => {
                    const baseInputs = windowOverrides
                      ? buildSharedStrategyInputs(windowOverrides)
                      : buildSharedStrategyInputs();
                    if (!baseInputs) return 0;
                    const result = computeVGSeriesShared({
                      ...baseInputs,
                      vgInitPct,
                      vgUpperPct,
                      vgLowerPct
                    });
                    incomeByYear = result.incomeByYear || [];
                    return Math.round(result.avgIncome || 0);
                  };

                  const computeDynamicSWRSeries = (windowOverrides = null) => {
                    const baseInputs = windowOverrides
                      ? buildSharedStrategyInputs(windowOverrides)
                      : buildSharedStrategyInputs();
                    if (!baseInputs) return 0;
                    const result = computeDynSeriesShared({
                      ...baseInputs,
                      dynInitPct
                    });
                    incomeByYear = result.incomeByYear || [];
                    return Math.round(result.avgIncome || 0);
                  };


                  // STEP 1: ONE rolling loop — main period MSI comes from current entry only
                  const allStrategyMSI = {};
                  let fixedIncomeData, floorCeilingIncomeData, vanguardIncomeData, dynamicIncomeData;

                  setRollingMSI([]);
                  try {
                    const { rolling: rollingPeriodResults, current } = computeRollingPeriodAnalysis({
                      selectedStartYear: start,
                      windowLen: years,
                      totalSuper,
                      computeForWindow: (s, windowLen) => {
                        const windowOverrides = { startYear: s, periodLength: windowLen };
                        if (strategy === 'FloorAndCeiling') {
                          return computeFloorCeilingSeries(windowOverrides);
                        }
                        if (strategy === 'DynamicSWR') {
                          return computeDynamicSWRSeries(windowOverrides);
                        }
                        if (strategy === 'VanguardGuardrails') {
                          return computeVanguardGuardrailsSeries(windowOverrides);
                        }
                        return computeFixedMSIForWindow(s, windowLen);
                      },
                    });

                    if (strategy === 'Fixed' || strategy === 'fixed') {
                      const fixedFromRolling = current?.fullResult || { avgIncome: 0, incomeByYear: [], finalBalance: 0 };
                      allStrategyMSI.Fixed = fixedFromRolling.avgIncome || 0;
                      fixedIncomeData = fixedFromRolling.incomeByYear || [];
                      setFixedIncomeVariesNote(buildFixedIncomeVariesNote(fixedFromRolling, start, years));
                    } else {
                      const fixedOnly = computeFixedMSIForWindow(start, years);
                      allStrategyMSI.Fixed = fixedOnly.avgIncome || 0;
                      fixedIncomeData = fixedOnly.incomeByYear || [];
                      setFixedIncomeVariesNote(null);
                    }

                    setRollingMSI(rollingPeriodResults.map(({ start: periodStart, income }) => ({
                      start: periodStart,
                      income,
                    })));
                  } catch (error) {
                    console.error('Rolling period analysis error:', error);
                    console.error('Error stack:', error.stack);
                  }

                  // Store series functions in refs for What If access AND for lazy
                  // "Compare Strategies" computation (computed on click, not on every Run —
                  // running all 4 strategies here was costing 3 unused full simulations per Run).
                  computeFloorCeilingSeriesRef.current = computeFloorCeilingSeries;
                  computeVanguardGuardrailsSeriesRef.current = computeVanguardGuardrailsSeries;
                  computeDynamicSWRSeriesRef.current = computeDynamicSWRSeries;
                  computeFixedSeriesRef.current = (overrides = null) => {
                    const s = overrides?.startYear ?? start;
                    const l = overrides?.periodLength ?? years;
                    return computeFixedMSIForWindow(s, l).avgIncome || 0;
                  };

                  // PERF: only compute the series for the SELECTED strategy here (needed for the
                  // graphs/table below). The other three strategies are only needed by the
                  // "Compare Strategies" modal, so they're computed lazily on that button click.
                  if (strategy === 'FloorAndCeiling') {
                    allStrategyMSI.FloorAndCeiling = computeFloorCeilingSeries();
                    floorCeilingIncomeData = [...incomeByYear];
                  } else if (strategy === 'VanguardGuardrails') {
                    allStrategyMSI.VanguardGuardrails = computeVanguardGuardrailsSeries();
                    vanguardIncomeData = [...incomeByYear];
                  } else if (strategy === 'DynamicSWR') {
                    allStrategyMSI.DynamicSWR = computeDynamicSWRSeries();
                    dynamicIncomeData = [...incomeByYear];
                  }
                  
                  // AI MSI will be set later from rolling period analysis
                  // (Single source of truth - use the rolling period calculation)
                  
                  // STEP 2: Set display variables based on SELECTED strategy only
                  // For dynamic strategies: use USER INPUT for table/graphs, BINARY SEARCH for MSI display
                  if (strategy === 'Fixed' || strategy === 'fixed') {
                    sustainable = allStrategyMSI.Fixed;
                    balance = sustainable;
                    yearlyDataForGraphs = fixedIncomeData;
                  } else if (strategy === 'FloorAndCeiling') {
                    // Table/graphs use user input, MSI uses series average (no optimization)
                    sustainable = allStrategyMSI.FloorAndCeiling; // Series MSI for display
                    balance = sustainable;
                    yearlyDataForGraphs = floorCeilingIncomeData; // User input for table
                  } else if (strategy === 'VanguardGuardrails') {
                    // Table/graphs use user input, MSI uses series average (no optimization)
                    sustainable = allStrategyMSI.VanguardGuardrails; // Series MSI for display
                    balance = sustainable;
                    yearlyDataForGraphs = vanguardIncomeData; // User input for table
                  } else if (strategy === 'DynamicSWR') {
                    // Table/graphs use user input, MSI uses series average (no optimization)
                    sustainable = allStrategyMSI.DynamicSWR; // Series MSI for display
                    balance = sustainable;
                    yearlyDataForGraphs = dynamicIncomeData; // User input for table
                  } else {
                    // Fallback to fixed strategy for unknown strategies
                    sustainable = allStrategyMSI.Fixed;
                    balance = sustainable;
                    yearlyDataForGraphs = fixedIncomeData;
                  }


                  // isIncomeSustainable function removed - not used in current implementation

                  // Charts/series commit happens once at the end via flushSync (instant scroll needs DOM).
                  
                  // Calculate actual average income from the displayed simulation (yearlyDataForGraphs)
                  // For Fixed strategy: use MINIMAX value (sustainable), not table average
                  // For DynamicSWR and other strategies: use table average
                  const actualAvgIncome = (strategy === 'Fixed' || strategy === 'fixed')
                    ? sustainable  // Fixed strategy uses MINIMAX (minimum income), not average
                    : (yearlyDataForGraphs.length > 0
                    ? yearlyDataForGraphs.reduce((sum, row) => sum + (row.totalIncome || 0), 0) / yearlyDataForGraphs.length
                      : sustainable);
                  
                  // Get actual final balance from the displayed simulation
                  const actualFinalBalance = yearlyDataForGraphs.length > 0
                    ? yearlyDataForGraphs[yearlyDataForGraphs.length - 1].balance || 0
                    : balance;

                  if (enableMortgageComparison && Number(mortgageBalance) > 0) {
                    const baseMortgageInputs = buildSharedStrategyInputs({ startYear: start, periodLength: years });
                    const payoffValue = Math.max(0, Number(mortgagePartialPayoffAmount) || Number(mortgageBalance) || 0);
                    const scheduleValue = mortgageStrategy === 'immediatePayoff'
                      ? 1
                      : (Number(mortgagePayoffScheduleValue) || 1);
                    const variants = [
                      {
                        key: 'keep',
                        label: 'Keep paying mortgage',
                        inputs: { mortgageStrategy: 'keep' },
                      },
                      {
                        key: 'immediatePayoff',
                        label: 'Pay off immediately',
                        inputs: { mortgageStrategy: 'immediatePayoff' },
                      },
                    ];
                    if (Number(mortgagePayoffScheduleValue) > 0) {
                      variants.push({
                        key: 'laterPayoff',
                        label: `Pay off in ${mortgagePayoffScheduleType === 'age' ? `age ${mortgagePayoffScheduleValue}` : `year ${mortgagePayoffScheduleValue}`}`,
                        inputs: {
                          mortgageStrategy: 'laterPayoff',
                          mortgagePayoffScheduleType,
                          mortgagePayoffScheduleValue,
                        },
                      });
                    }
                    if (Number(mortgagePartialPayoffAmount) > 0) {
                      variants.push({
                        key: 'partialPayoff',
                        label: `Partial payoff (${Number(mortgagePartialPayoffAmount).toLocaleString()})`,
                        inputs: {
                          mortgageStrategy: 'partialPayoff',
                          mortgagePayoffScheduleType,
                          mortgagePayoffScheduleValue: scheduleValue,
                          mortgagePartialPayoffAmount,
                        },
                      });
                    }
                    if (payoffValue > 0 && mortgagePayoffSource === 'general') {
                      const contributionRows = defaultLumpSumContributionRows();
                      contributionRows[0] = {
                        recipient: 'you',
                        contributionType: 'nonConcessional',
                        scheduleType: mortgageStrategy === 'immediatePayoff' ? 'year' : mortgagePayoffScheduleType,
                        scheduleValue,
                        amount: payoffValue,
                        label: 'Mortgage comparison: contribute instead',
                      };
                      variants.push({
                        key: 'contributeInstead',
                        label: 'Use outside cash for super instead',
                        inputs: {
                          mortgageStrategy: 'keep',
                          lumpSumContributions: contributionRows,
                          parsedLumpSumContributions: null,
                        },
                      });
                    }
                    const comparison = variants
                      .map((variant) => {
                        const result = computeFixedMSIShared({
                          ...baseMortgageInputs,
                          ...variant.inputs,
                          enableMortgageComparison: true,
                          mortgageBalance,
                          mortgageInterestRate,
                          mortgageAnnualRepayment,
                          mortgagePayoffSource,
                        }, false, true);
                        const rows = result.incomeByYear || [];
                        const lastRow = rows[rows.length - 1] || {};
                        const totalInterest = rows.reduce((sum, row) => sum + (row.mortgageInterest || 0), 0);
                        const totalMortgageOutflow = rows.reduce((sum, row) => sum + (row.mortgageOutflow || 0), 0);
                        return {
                          key: variant.key,
                          label: variant.label,
                          safeIncome: Math.round(result.avgIncome || 0),
                          finalBalance: Math.round(result.finalBalance || 0),
                          finalMortgageBalance: Math.round(lastRow.mortgageBalance || 0),
                          totalInterest: Math.round(totalInterest),
                          totalMortgageOutflow: Math.round(totalMortgageOutflow),
                          mortgagePaidOff: !!lastRow.mortgagePaidOff,
                        };
                      })
                      .sort((a, b) => b.safeIncome - a.safeIncome);
                    setMortgageComparisonResults(comparison);
                  } else {
                    setMortgageComparisonResults([]);
                  }
                  
                  // Use MINIMAX for Fixed strategy, series function return for other strategies
                  setHistoricalAvgIncome(Math.round(actualAvgIncome));
                  const effectiveMonteCarloTargetIncome = Number(monteCarloTargetIncome) || Math.round(actualAvgIncome);
                  // Auto-populate monteCarloTargetIncome with the computed MSI if the user hasn't entered a manual target.
                  // This ensures the red "Balance at Target Income" line always renders on the chart after a run.
                  if (!Number(monteCarloTargetIncome)) {
                    setMonteCarloTargetIncome(effectiveMonteCarloTargetIncome);
                  }
                  // For What If comparisons: use series function return value (matches what What If will calculate)
                  if (strategy === 'Fixed' || strategy === 'fixed') {
                    currentStrategyMSIRef.current = Math.round(actualAvgIncome); // MINIMAX
                  } else if (strategy === 'FloorAndCeiling') {
                    currentStrategyMSIRef.current = Math.round(allStrategyMSI.FloorAndCeiling || actualAvgIncome); // Series return
                  } else if (strategy === 'VanguardGuardrails') {
                    currentStrategyMSIRef.current = Math.round(allStrategyMSI.VanguardGuardrails || actualAvgIncome); // Series return
                  } else if (strategy === 'DynamicSWR') {
                    currentStrategyMSIRef.current = Math.round(allStrategyMSI.DynamicSWR || actualAvgIncome); // Series return
                  } else {
                    currentStrategyMSIRef.current = Math.round(actualAvgIncome); // Fallback
                  }
                  
                  // VERIFICATION: Calculate table sum to verify displayed average
                  const tableSum = yearlyDataForGraphs.reduce((sum, row) => sum + (row.totalIncome || 0), 0);
                  const tableAvg = yearlyDataForGraphs.length > 0 ? tableSum / yearlyDataForGraphs.length : 0;
                  
                  setHistoricalResults({ 
                    sustainableIncome: Math.max(0, Math.round(actualAvgIncome)), 
                    finalRealBalance: Math.max(0, Math.round(actualFinalBalance))
                  });

                  // NOTE: msiResults (all 4 strategies) is no longer computed here — it's only
                  // needed by the "Compare Strategies" modal, so it's computed lazily on that
                  // button's click (see openStrategyComparison) to keep every Run fast.

                  // Prepare Monte Carlo inputs now, but run only after charts mount + scroll
                  let deferredMonteCarlo = null;
                  try {
                    const mcBase = buildSharedStrategyInputs({
                      monteCarloTargetIncome: effectiveMonteCarloTargetIncome
                    });
                    if (mcBase) {
                      const { providedReturns: _mcDrop, ...mcInputs } = mcBase;
                      deferredMonteCarlo = {
                        ...mcInputs,
                        monteCarloTargetIncome: effectiveMonteCarloTargetIncome,
                        withdrawalStrategy,
                        fcInitPct,
                        fcFloorPct,
                        fcCeilPct,
                        vgInitPct,
                        vgUpperPct,
                        vgLowerPct,
                        dynInitPct,
                      };
                    }
                  } catch (error) {
                    console.error('Monte Carlo input preparation error:', error);
                  }
                  setShowHistorical(true);

                  // Record free run only after a successful result (Phase 1 — no burn on alloc/validation fail).
                  {
                    const runPlatform = typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop';
                    const succeededIncome = Math.max(0, Math.round(actualAvgIncome));
                    if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.ADVANCED_CALCULATOR)) {
                      SubscriptionManager.recordAdvancedRun();
                    }
                    if (succeededIncome > 0) {
                      setLastResultIncome(succeededIncome);
                    }
                    trackAdvancedRunSucceeded({
                      platform: runPlatform,
                      relationshipStatus,
                      sustainableIncome: succeededIncome,
                    });
                    trackCalculationComplete({
                      calculatorTier: 'advanced',
                      calculatorType: 'advanced_retirement',
                      platform: runPlatform,
                      extra: {
                        relationship_status: relationshipStatus,
                        sustainable_income: succeededIncome || undefined
                      }
                    });
                    if (typeof gtag !== 'undefined') {
                      gtag('event', 'result_viewed', {
                        event_category: 'funnel',
                        calculator_tier: 'advanced',
                        relationship_status: relationshipStatus
                      });
                    }
                  }

                  if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.ADVANCED_CALCULATOR)) {
                    const newStatus = SubscriptionManager.canRunAdvancedFree();
                    setFreeRunsStatus(newStatus);
                    if (!newStatus.canRun) {
                      setIsDemoMode(true);
                    }
                    setShowPostResultUpsell(true);
                    setPostResultUpsellDismissed(false);
                    if (typeof gtag !== 'undefined') {
                      gtag('event', 'upgrade_prompt_viewed', {
                        event_category: 'funnel',
                        event_label: 'Advanced post-result',
                        calculator_tier: 'advanced',
                        runs_remaining: newStatus.runsLeft
                      });
                      gtag('event', 'free_run_completed', {
                        event_category: 'free_trial',
                        event_label: `runs_remaining_${newStatus.runsLeft}`,
                        value: 1,
                        calculator_tier: 'advanced'
                      });
                    }
                  }
                  
                  // Commit charts to the DOM, instant-scroll to them, then start Monte Carlo.
                  flushSync(() => {
                    setIsCalculating(false);
                    if (Array.isArray(yearlyDataForGraphs)) {
                      setHistoricalSeries([...yearlyDataForGraphs]);
                    }
                  });
                  scrollResultsIntoViewInstant();
                  requestAnimationFrame(() => {
                    scrollResultsIntoViewInstant();
                    scheduleDeferredMonteCarlo(deferredMonteCarlo);
                  });
                  } catch (error) {
                    // Error in retirement analysis
                    // Critical error in retirement analysis - handled silently
                    setIsCalculating(false);
                    setIsMonteCarloRunning(false);
                    alert('An error occurred while running the retirement analysis. Error: ' + error.message + '\n\nCheck console for details.');
                  }
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Run my plan
              </button>
              {/* Save this scenario — subscribers only (restore already gated) */}
              {!inputsLocked && (
                <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Save this scenario:</label>
                    <input
                      type="text"
                      value={saveScenarioName}
                      onChange={(e) => { setSaveScenarioName(e.target.value); setSaveScenarioFeedback(''); }}
                      placeholder="e.g. Couple 60/58, $800k"
                      className="flex-1 min-w-[140px] px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={!hasAdvancedAccess}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) {
                          window.dispatchEvent(new CustomEvent('showPricingModal', {
                            detail: { source: 'save_scenario', calculatorTier: 'advanced', surface: 'advanced_save_scenario' }
                          }));
                          return;
                        }
                        const name = saveScenarioName.trim();
                        if (!name) {
                          setSaveScenarioFeedback('Enter a name');
                          return;
                        }
                        try {
                          const payload = { name, savedAt: new Date().toISOString(), inputs: getCurrentInputs() };
                          localStorage.setItem('adv_saved_scenario', JSON.stringify(payload));
                          setSavedScenario(payload);
                          setSaveScenarioFeedback(`Saved as "${name}"`);
                          if (typeof gtag !== 'undefined') {
                            gtag('event', 'scenario_saved', { event_category: 'engagement', event_label: 'Advanced save scenario', value: 1, calculator_tier: 'advanced' });
                          }
                        } catch (e) {
                          setSaveScenarioFeedback('Save failed');
                        }
                      }}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-semibold rounded-md"
                    >
                      {hasAdvancedAccess ? 'Save scenario' : 'Save — Subscribe'}
                    </button>
                  </div>
                  {!hasAdvancedAccess && (
                    <p className="text-xs text-gray-500 mt-2 mb-0">
                      Free run: explore your result below. Saving and restoring scenarios is included with Advanced Retirement.
                    </p>
                  )}
                  {saveScenarioFeedback && <p className="text-sm mt-2 text-gray-600">{saveScenarioFeedback}</p>}
                  {savedScenario && (
                    <>
                      <p className="text-xs text-gray-500 mt-1">
                        Saved scenario: &quot;{savedScenario.name}&quot;.
                      </p>
                      {hasAdvancedAccess && (
                        <button
                          type="button"
                          onClick={() => {
                            loadScenarioInputs(savedScenario.inputs);
                            setShowHistorical(false);
                            setShowMonteCarlo(false);
                          }}
                          className="mt-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          Restore &quot;{savedScenario.name}&quot;
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Results — outside input grid so sticky Run bar does not fight scroll anchoring */}
        <div className="advanced-calculator-results" style={{ overflowAnchor: 'none' }}>
          <div className="mt-4" id="simulation-results">

            {/* EMAIL GATE: shown when anon runs exhausted, prompts email for 1 more free run */}
            {showFreeRunsEmailGate && !hasAdvancedAccess && (
              <FreeRunsEmailGate
                onEmailCaptured={(email) => {
                  SubscriptionManager.setAdvancedFreeEmail(email);
                  const newStatus = SubscriptionManager.canRunAdvancedFree();
                  setFreeRunsStatus(newStatus);
                  setShowFreeRunsEmailGate(false);
                  setIsDemoMode(false);
                  // Auto-trigger the calculation now that they have more runs
                  setTimeout(() => calculateButtonRef.current?.click(), 100);
                }}
                onUpgradeClick={() => {
                  setShowFreeRunsEmailGate(false);
                  if (typeof gtag !== 'undefined') {
                    gtag('event', 'email_gate_upgrade_click', { event_category: 'conversion', event_label: 'Advanced email gate', value: 1, calculator_tier: 'advanced' });
                  }
                  openPricingModal({
                    source: 'email_gate',
                    calculatorTier: 'advanced',
                    surface: 'email_gate_upgrade_results',
                  });
                }}
                onDismiss={() => setShowFreeRunsEmailGate(false)}
              />
            )}

            {(historicalSeries.length > 0 || mcSuccessRate != null) && (() => {
              const fmt = (value) => `$${Math.round(Number(value || 0)).toLocaleString()}`;
              const currentStartYear = Number(startYear) || 1980;
              const currentRollingRow = (rollingMSI || []).find((row) => row.start === currentStartYear);
              const isFixedStrategy = String(withdrawalStrategy) === 'Fixed' || String(withdrawalStrategy) === 'fixed';
              const sustainableIncome = (isFixedStrategy && currentRollingRow && Number.isFinite(Number(currentRollingRow.income)))
                ? Number(currentRollingRow.income)
                : (historicalResults?.sustainableIncome || historicalSeries[0]?.totalIncome || 0);
              const pensionValues = historicalSeries.map((row) => Number(row.pension || 0)).filter((value) => value > 0);
              const avgPension = pensionValues.length
                ? pensionValues.reduce((sum, value) => sum + value, 0) / pensionValues.length
                : 0;
              const rolling = (rollingMSI || []).filter((row) => Number.isFinite(Number(row.income)));
              const worstRolling = rolling.length
                ? rolling.reduce((worst, row) => Number(row.income) < Number(worst.income) ? row : worst, rolling[0])
                : null;
              const finalBalance = historicalResults?.finalRealBalance ?? historicalSeries[historicalSeries.length - 1]?.balance;
              const keyRisk = worstRolling
                ? `Worst historical start: ${worstRolling.start}`
                : mcSuccessRate != null && mcSuccessRate < 80
                  ? 'Key risk: market sequence and longevity'
                  : 'Key risk: test more scenarios';
              const usedTargetIncome = Number(monteCarloTargetIncome) > 0;
              const msiHitThreshold = usedTargetIncome
                ? Number(monteCarloTargetIncome)
                : Number(sustainableIncome) || 0;
              const msiHitCount = rolling.filter((row) => Number(row.income) >= msiHitThreshold).length;
              const msiHitRatePct = rolling.length
                ? Math.round((msiHitCount / rolling.length) * 100)
                : null;
              const bridgeEnabledForRun = !!enableBridgeStrategy;
              const bridgeAppliedForRun = bridgeEnabledForRun && isFixedStrategy;
              const bridgeDrawnForRun = historicalSeries.some((row) => (row.bridgeWithdrawal || 0) > 0);
              const bridgeWarningForRun = historicalSeries.some((row) => row.bridgeDepletedBeforeAccess || (row.bridgeShortfall || 0) > 0);

              return (
                <div className="mb-6 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-blue-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-blue-700 font-bold">Your first result</p>
                      <h3 className="text-xl font-bold text-gray-900">Can this retirement plan hold up?</h3>
                      <p className="text-sm text-blue-900 mt-1">Your plan stress-tested against market history, inflation, pension rules, and real-life cash events.</p>
                    </div>
                    {!hasAdvancedAccess && (
                      <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
                        {freeRunsStatus.canRun ? `${freeRunsStatus.runsLeft} free run${freeRunsStatus.runsLeft !== 1 ? 's' : ''} left` : 'Subscribe to keep testing'}
                      </span>
                    )}
                  </div>
                  {bridgeEnabledForRun && (
                    <div className={`mx-5 mt-4 rounded-lg border px-4 py-3 text-sm ${
                      bridgeAppliedForRun
                        ? 'border-amber-200 bg-amber-50 text-amber-900'
                        : 'border-orange-200 bg-orange-50 text-orange-900'
                    }`}>
                      <strong>Bridge / delayed super access:</strong>{' '}
                      {bridgeAppliedForRun
                        ? `Applied in this Fixed Sustainable Income run${bridgeDrawnForRun ? '; bridge withdrawals are shown in the income chart and yearly table' : '; no bridge draw was needed in the displayed years'}.`
                        : 'Not applied in this run because bridge buckets are currently modelled only for Fixed Sustainable Income. Switch Strategy Type to Fixed to use these settings.'}
                      {bridgeWarningForRun && (
                        <span className="block mt-1 font-semibold text-orange-800">
                          Bridge assets were depleted or short before super access in at least one projection year.
                        </span>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 p-5">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Safe income</div>
                      <div className="text-2xl font-bold text-gray-900">{fmt(sustainableIncome)}</div>
                      <div className="text-xs text-gray-500">per year, today's dollars</div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Survival Probability</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {isMonteCarloRunning
                          ? 'Calculating…'
                          : mcSuccessRate != null
                            ? `${mcSuccessRate}%`
                            : '—'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {isMonteCarloRunning
                          ? 'Monte Carlo running after historical results'
                          : 'based on 1,000 Monte Carlo trials'}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Historical periods at/above target</div>
                      <div className="text-2xl font-bold text-gray-900">{msiHitRatePct != null ? `${msiHitRatePct}%` : 'n/a'}</div>
                      <div className="text-xs text-gray-500">
                        {msiHitRatePct != null
                          ? `${msiHitCount} of ${rolling.length} rolling periods · ${usedTargetIncome ? `Target ${fmt(msiHitThreshold)}` : `Safe income ${fmt(msiHitThreshold)}`}`
                          : 'Run historical analysis to compare periods'}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Age Pension</div>
                      <div className="text-2xl font-bold text-gray-900">{avgPension ? fmt(avgPension) : includePension ? 'Low/none' : 'Off'}</div>
                      <div className="text-xs text-gray-500">average when payable</div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">End balance</div>
                      <div className="text-2xl font-bold text-gray-900">{Number.isFinite(Number(finalBalance)) ? fmt(finalBalance) : 'n/a'}</div>
                      <div className="text-xs text-gray-500">end of projection</div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Worst historical start</div>
                      <div className="text-sm font-bold text-gray-900 mt-1">{keyRisk}</div>
                      <div className="text-xs text-gray-500 mt-1">Compare scenarios before relying on one run</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {mortgageComparisonResults.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg shadow-md p-5 mb-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Mortgage Payoff Strategy Comparison</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Fixed Sustainable Income comparison. Safe income is lifestyle income after mortgage repayments and payoff cashflows.
                    </p>
                  </div>
                  <div className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                    Best: {mortgageComparisonResults[0].label}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {mortgageComparisonResults.map((item, idx) => (
                    <div
                      key={item.key}
                      className={`rounded-lg border p-4 ${idx === 0 ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}
                    >
                      <div className="text-sm font-semibold text-gray-900">{item.label}</div>
                      <div className="mt-2 text-2xl font-bold text-gray-900">${item.safeIncome.toLocaleString()}</div>
                      <div className="text-xs text-gray-600">safe lifestyle income / yr</div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-700">
                        <div>
                          <div className="font-semibold">${item.finalBalance.toLocaleString()}</div>
                          <div>final super/assets</div>
                        </div>
                        <div>
                          <div className="font-semibold">${item.finalMortgageBalance.toLocaleString()}</div>
                          <div>final mortgage</div>
                        </div>
                        <div>
                          <div className="font-semibold">${item.totalInterest.toLocaleString()}</div>
                          <div>total interest</div>
                        </div>
                        <div>
                          <div className="font-semibold">${item.totalMortgageOutflow.toLocaleString()}</div>
                          <div>mortgage cashflow</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Surrounding CTAs only — calculator result cards above stay intact */}
            {showPostResultUpsell && !hasAdvancedAccess && (historicalResults || mcPercentiles) && freeRunsStatus.canRun && (
              <div className="mb-6 rounded-xl border border-gray-200 bg-white px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-sm">
                <p className="text-sm text-gray-700 m-0">
                  Change an input and run again — you have {freeRunsStatus.runsLeft} free run{freeRunsStatus.runsLeft !== 1 ? 's' : ''} left.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowPostResultUpsell(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition shadow text-sm whitespace-nowrap"
                >
                  Run again ({freeRunsStatus.runsLeft} left)
                </button>
              </div>
            )}

            {showPostResultUpsell && !hasAdvancedAccess && (historicalResults || mcPercentiles) && !freeRunsStatus.canRun && (
              <div className="mb-6 rounded-xl overflow-hidden shadow-lg border-2 border-blue-200">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {(() => {
                        const income = paywallIncome;
                        const survivalStr = paywallSurvival != null
                          ? `${Math.round(Number(paywallSurvival))}% Monte Carlo survival`
                            : historicalResults
                            ? `survived most historical periods since 1928`
                            : null;
                        return (
                          <>
                            <p className="text-lg font-bold mb-1">
                              {income
                                ? `This run supports ~$${Math.round(income / 1000)}k/yr.`
                                : 'You just saw your retirement survival odds.'}
                              {survivalStr ? ` ${survivalStr}.` : ''}
                            </p>
                            <p className="text-blue-100 text-sm">
                              {exhaustedSupportCopy(income, paywallSurvival)}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPostResultUpsell(false);
                        setPostResultUpsellDismissed(true);
                      }}
                      className="text-white/70 hover:text-white text-xl leading-none shrink-0 mt-0.5"
                      aria-label="Dismiss"
                    >×</button>
                  </div>
                </div>
                <div className="bg-white px-6 py-5">
                  <p className="text-sm font-semibold text-gray-800 mb-2">Your free run is used. Advanced Retirement unlocks:</p>
                  <ul className="text-sm text-gray-700 space-y-1 mb-4">
                    <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">&#x25BA;</span><span>Stress 10+ different retirement start years on this plan</span></li>
                    <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">&#x25BA;</span><span>Export a PDF for your partner or adviser</span></li>
                    <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">&#x25BA;</span><span>Save this plan and come back later</span></li>
                    <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">&#x25BA;</span><span>Change retirement age or spending and re-run</span></li>
                  </ul>
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof gtag !== 'undefined') {
                        gtag('event', 'post_result_upsell_upgrade_click', { event_category: 'conversion', event_label: 'Advanced post-result soft pricing', value: 1, calculator_tier: 'advanced' });
                      }
                      openPricingModal({
                        source: 'post_result',
                        calculatorTier: 'advanced',
                        surface: 'free_runs_exhausted_post_result',
                      });
                    }}
                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition shadow text-sm"
                  >
                    {subscribeWithTheirNumberLabel(paywallIncome, { survivalPct: paywallSurvival })}
                  </button>
                </div>
              </div>
            )}

            {postResultUpsellDismissed && !showPostResultUpsell && !hasAdvancedAccess && !freeRunsStatus.canRun && (historicalResults || mcPercentiles || lastResultIncome) && (
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                <p className="text-sm text-blue-900 m-0">
                  {exhaustedSupportCopy(paywallIncome, paywallSurvival)}
                </p>
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition text-sm whitespace-nowrap shrink-0"
                  onClick={() => {
                    if (typeof gtag !== 'undefined') {
                      gtag('event', 'post_result_see_pricing_click', { event_category: 'conversion', event_label: 'Advanced post-result dismissed banner', value: 1, calculator_tier: 'advanced' });
                    }
                    openPricingModal({
                      source: 'post_result_dismissed',
                      calculatorTier: 'advanced',
                      surface: 'post_result_dismissed_banner',
                    });
                  }}
                >
                  {subscribeWithTheirNumberLabel(paywallIncome, { survivalPct: paywallSurvival })}
                </button>
              </div>
            )}

            {!hasAdvancedAccess && (historicalResults || mcPercentiles) && (
              <div className="mb-6">
                <ExampleScenariosSelector
                  calculatorType="advanced"
                  scenarioIds={FEATURED_ADVANCED_SCENARIO_IDS}
                  onLoadScenario={handleLoadExampleScenario}
                />
              </div>
            )}
            {/* Per-Partner Eligibility Timeline - For Couples */}
            {historicalResults && relationshipStatus === 'couple' && (
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200 mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span>👥</span>
                  <span>Per-Partner Eligibility Status</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* You / Partner A */}
                  <div className="bg-white p-3 rounded-lg border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-2 text-sm">You (Partner A)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Age:</span>
                        <span className="font-bold text-blue-600">{yourAge}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Gender:</span>
                        <span className="font-medium">{yourGender}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Super:</span>
                        <span className="font-bold">${yourSuper.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Life Exp:</span>
                        <span className="text-xs">{(() => {
                          const data = lifeExpectancyData[yourGender];
                          const ages = Object.keys(data || {}).map(Number);
                          const closest = ages.reduce((prev, curr) => Math.abs(curr - yourAge) < Math.abs(prev - yourAge) ? curr : prev, ages[0]);
                          return Math.round(data?.[closest]?.p50 || 85);
                        })()} yrs (median)</span>
                      </div>
                      <div className="border-t border-gray-200 pt-2 mt-2">
                        <div className="flex items-center gap-2 mb-1">
                          {yourAge >= 60 ? (
                            <><span className="text-green-500">✓</span><span className="text-xs text-green-700 font-medium">Super Access: NOW</span></>
                          ) : (
                            <><span className="text-amber-500">⏳</span><span className="text-xs text-amber-700 font-medium">Super Access: Year {60 - yourAge}</span></>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {yourAge >= 67 ? (
                            <><span className="text-green-500">✓</span><span className="text-xs text-green-700 font-medium">Age Pension: NOW</span></>
                          ) : (
                            <><span className="text-amber-500">⏳</span><span className="text-xs text-amber-700 font-medium">Age Pension: Year {67 - yourAge}</span></>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Partner / Partner B */}
                  <div className="bg-white p-3 rounded-lg border border-purple-100">
                    <h4 className="font-semibold text-purple-900 mb-2 text-sm">Partner (Partner B)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Age:</span>
                        <span className="font-bold text-purple-600">{partnerAge || 0}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Gender:</span>
                        <span className="font-medium">{partnerGender}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Super:</span>
                        <span className="font-bold">${partnerSuper.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Life Exp:</span>
                        <span className="text-xs">{(() => {
                          const data = lifeExpectancyData[partnerGender];
                          const ages = Object.keys(data || {}).map(Number);
                          const closest = ages.reduce((prev, curr) => Math.abs(curr - (partnerAge || 67)) < Math.abs(prev - (partnerAge || 67)) ? curr : prev, ages[0]);
                          return Math.round(data?.[closest]?.p50 || 88);
                        })()} yrs (median)</span>
                      </div>
                      <div className="border-t border-gray-200 pt-2 mt-2">
                        <div className="flex items-center gap-2 mb-1">
                          {(partnerAge || 0) >= 60 ? (
                            <><span className="text-green-500">✓</span><span className="text-xs text-green-700 font-medium">Super Access: NOW</span></>
                          ) : (
                            <><span className="text-amber-500">⏳</span><span className="text-xs text-amber-700 font-medium">Super Access: Year {60 - (partnerAge || 0)}</span></>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {(partnerAge || 0) >= 67 ? (
                            <><span className="text-green-500">✓</span><span className="text-xs text-green-700 font-medium">Age Pension: NOW</span></>
                          ) : (
                            <><span className="text-amber-500">⏳</span><span className="text-xs text-amber-700 font-medium">Age Pension: Year {67 - (partnerAge || 0)}</span></>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Combined Eligibility Summary - ONLY for non-phased retirement */}
                {!phasedRetirement && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-2">
                      <span>🔄</span>
                      <span>Combined Eligibility</span>
                    </h4>
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex items-center gap-2">
                        {yourAge >= 60 && (partnerAge || 0) >= 60 ? (
                          <><span className="text-green-500 font-bold">✓</span><span><strong>Super Withdrawals:</strong> Both partners eligible - withdrawals active</span></>
                        ) : (
                          <><span className="text-amber-500 font-bold">⏳</span><span><strong>Super Withdrawals:</strong> Start in year {Math.max(60 - yourAge, 60 - (partnerAge || 0))} when youngest partner reaches 60</span></>
                        )}
                      </div>
                      {includePension && (
                        <div className="flex items-center gap-2">
                          {yourAge >= 67 && (partnerAge || 0) >= 67 ? (
                            <><span className="text-green-500 font-bold">✓</span><span><strong>Couple Age Pension:</strong> Both partners eligible - pension active</span></>
                          ) : (
                            <><span className="text-amber-500 font-bold">⏳</span><span><strong>Couple Age Pension:</strong> Start in year {Math.max(67 - yourAge, 67 - (partnerAge || 0))} when youngest partner reaches 67</span></>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Phased Retirement Eligibility Summary - ONLY when phased retirement enabled */}
                {phasedRetirement && (
                  <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-300">
                    <h4 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-2">
                      <span>🔄</span>
                      <span>Phased Retirement Eligibility</span>
                    </h4>
                    <div className="space-y-2 text-xs text-gray-700">
                      <div className="p-2 bg-white rounded border-l-4 border-blue-500">
                        <div className="font-semibold text-blue-900 mb-1">Partner A (You) - Age {yourAge}</div>
                        <div className="flex items-center gap-2">
                          {yourAge >= 60 ? (
                            <><span className="text-green-500 font-bold">✓</span><span>Super access NOW (age 60+)</span></>
                          ) : (
                            <><span className="text-amber-500 font-bold">⏳</span><span>Super access in {60 - yourAge} years (age 60)</span></>
                          )}
                        </div>
                        {includePension && (
                          <div className="flex items-center gap-2 mt-1">
                            {yourAge >= 67 ? (
                              <><span className="text-green-500 font-bold">✓</span><span>Single Age Pension NOW (age 67+)</span></>
                            ) : (
                              <><span className="text-amber-500 font-bold">⏳</span><span>Single Age Pension in {67 - yourAge} years (age 67)</span></>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="p-2 bg-white rounded border-l-4 border-purple-500">
                        <div className="font-semibold text-purple-900 mb-1">Partner B - Age {partnerAge || 0}</div>
                        <div className="flex items-center gap-2">
                          {(partnerAge || 0) >= 60 ? (
                            <><span className="text-green-500 font-bold">✓</span><span>Super access NOW (age 60+)</span></>
                          ) : (
                            <><span className="text-amber-500 font-bold">⏳</span><span>Super access in {60 - (partnerAge || 0)} years (age 60)</span></>
                          )}
                        </div>
                        {includePension && (
                          <div className="flex items-center gap-2 mt-1">
                            {(partnerAge || 0) >= 67 ? (
                              <><span className="text-green-500 font-bold">✓</span><span>Single Age Pension NOW (age 67+)</span></>
                            ) : (
                              <><span className="text-amber-500 font-bold">⏳</span><span>Single Age Pension in {67 - (partnerAge || 0)} years (age 67)</span></>
                            )}
                          </div>
                        )}
                      </div>
                      {includePension && (
                        <div className="p-2 bg-gradient-to-r from-green-50 to-blue-50 rounded border border-green-300">
                          <div className="font-semibold text-green-900 mb-1 flex items-center gap-1">
                            <span>👥</span>
                            <span>Couple Age Pension</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {yourAge >= 67 && (partnerAge || 0) >= 67 ? (
                              <><span className="text-green-500 font-bold">✓</span><span>Active NOW (both age 67+)</span></>
                            ) : (
                              <><span className="text-amber-500 font-bold">⏳</span><span>Starts in year {Math.max(67 - yourAge, 67 - (partnerAge || 0))} when both reach 67</span></>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Visual Timeline */}
                <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm flex items-center gap-2">
                    <span>📅</span>
                    <span>Retirement Milestones Timeline</span>
                  </h4>
                  
                  {/* Timeline for Partner A */}
                  <div className="mb-3">
                    <p className="text-xs font-medium text-blue-900 mb-2">You (Partner A)</p>
                    <div className="relative h-10 bg-gray-100 rounded-lg overflow-hidden">
                      {yourAge < 60 && (
                        <div className="absolute top-0 bottom-0 bg-amber-200 border-r-2 border-amber-500" 
                             style={{left: '0%', width: `${Math.min(100, ((60 - yourAge) / (periodLength || 30)) * 100)}%`}}>
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-amber-800">
                            Age 60 ↓
                          </span>
                        </div>
                      )}
                      {yourAge < 67 && (
                        <div className="absolute top-0 bottom-0 border-r-2 border-blue-500" 
                             style={{left: `${Math.min(100, ((67 - yourAge) / (periodLength || 30)) * 100)}%`}}>
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-blue-800 whitespace-nowrap">
                            ↓ Age 67
                          </span>
                        </div>
                      )}
                      <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center">
                        <span className="text-xs font-semibold text-gray-700">
                          {yourAge >= 67 ? '✓ Fully Eligible' : yourAge >= 60 ? '✓ Super Only' : `${60 - yourAge}yr to Super`}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Timeline for Partner B */}
                  <div>
                    <p className="text-xs font-medium text-purple-900 mb-2">Partner (Partner B)</p>
                    <div className="relative h-10 bg-gray-100 rounded-lg overflow-hidden">
                      {(partnerAge || 0) < 60 && (
                        <div className="absolute top-0 bottom-0 bg-amber-200 border-r-2 border-amber-500" 
                             style={{left: '0%', width: `${Math.min(100, ((60 - (partnerAge || 0)) / (periodLength || 30)) * 100)}%`}}>
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-amber-800">
                            Age 60 ↓
                          </span>
                        </div>
                      )}
                      {(partnerAge || 0) < 67 && (
                        <div className="absolute top-0 bottom-0 border-r-2 border-purple-500" 
                             style={{left: `${Math.min(100, ((67 - (partnerAge || 0)) / (periodLength || 30)) * 100)}%`}}>
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-purple-800 whitespace-nowrap">
                            ↓ Age 67
                          </span>
                        </div>
                      )}
                      <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center">
                        <span className="text-xs font-semibold text-gray-700">
                          {(partnerAge || 0) >= 67 ? '✓ Fully Eligible' : (partnerAge || 0) >= 60 ? '✓ Super Only' : `${60 - (partnerAge || 0)}yr to Super`}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Legend */}
                  <div className="mt-3 pt-3 border-t border-gray-200 flex gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-amber-200 border border-amber-500 rounded"></div>
                      <span className="text-gray-600">Waiting Period</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></div>
                      <span className="text-gray-600">Eligible Period</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={resultsRef} className="flex justify-between items-start mb-4">
              <div className="grid grid-cols-1 gap-4 flex-1 lg:grid-cols-2">
                <div className="bg-white border border-gray-200 rounded-md p-3 cursor-pointer" onClick={() => { setShowHistorical(true); setShowMonteCarlo(false); }}>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Historical Simulation</div>
                  <h4 className="text-sm font-semibold text-gray-800">
                    Single Period Analysis: {startYear}–{Math.max(startYear, startYear + Math.max(0, parseInt(periodLength) || 0) - 1)}
                  </h4>
                </div>
                <div className="bg-white border border-gray-200 rounded-md p-3 cursor-pointer" onClick={() => { setShowMonteCarlo(true); setShowHistorical(false); }}>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Monte Carlo Analysis</div>
                  <h4 className="text-sm font-semibold text-gray-800">Monte Carlo Simulation (1000 trials)</h4>
                </div>
              </div>
              
              {/* PDF Export and Compare Buttons */}
              {(showHistorical || showMonteCarlo) && (
                <div className="ml-4 flex gap-2">
                  <button
                    onClick={async () => {
                      if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                        startPdfUnlockCheckout({
                          calculatorTier: 'advanced',
                          surface: 'advanced_pdf_export',
                        }).catch(alertCheckoutFailure);
                        return;
                      }
                      try {
                        setIsExporting(true);
                        
                        // Wait for spinner to render
                        await new Promise(resolve => {
                          requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                              setTimeout(resolve, 100);
                            });
                          });
                        });
                        
                        // Calculate rolling period statistics
                      const rollingStats = (() => {
                        const incomes = (rollingMSI || []).map((d) => d.income).filter((n) => Number.isFinite(n)).sort((a,b)=>a-b);
                        if (incomes.length === 0) return null;
                        const worst = incomes[0];
                        const best = incomes[incomes.length-1];
                        const mid = Math.floor(incomes.length/2);
                        const median = incomes.length % 2 === 1 ? incomes[mid] : Math.round((incomes[mid-1] + incomes[mid]) / 2);
                        return { worst, median, best, count: incomes.length };
                      })();

                      const exportInputs = {
                        relationshipStatus,
                        yourAge,
                        yourSuper,
                        partnerAge,
                        partnerSuper,
                        startYear,
                        periodLength,
                        withdrawalStrategy,
                        includePension,
                        sp500,
                        ausShares,
                        intlShares,
                        bonds,
                        cash,
                        frankingCredits,
                        adminFees,
                        adminFeePercentage,
                        investmentFees,
                        investmentFeeCap,
                        transactionCosts,
                        insuranceFees,
                        targetIncome: monteCarloTargetIncome,
                        swrRate: dynInitPct,
                        vanguardInitialRate: vgInitPct,
                        floorCeilingInitialRate: fcInitPct,
                        floorCeilingFloorRate: fcFloorPct,
                        floorCeilingCeilingRate: fcCeilPct,
                        lumpSumWithdrawals,
                        lumpSumContributions,
                      };
                      // Capture charts as images - SVG DIRECT METHOD
                      const chartImages = {};
                      
                      // Helper to convert SVG to image
                      const svgToImage = (svgElement, width, height) => {
                        return new Promise((resolve) => {
                          try {
                            // Clone the SVG
                            const clonedSvg = svgElement.cloneNode(true);
                            
                            // Set explicit dimensions
                            clonedSvg.setAttribute('width', width);
                            clonedSvg.setAttribute('height', height);
                            
                            // Serialize SVG
                            const svgData = new XMLSerializer().serializeToString(clonedSvg);
                            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                            const url = URL.createObjectURL(svgBlob);
                            
                            // Create image from SVG
                            const img = new Image();
                            img.onload = () => {
                              // Create canvas and draw image
                              const canvas = document.createElement('canvas');
                              canvas.width = width;
                              canvas.height = height;
                              const ctx = canvas.getContext('2d');
                              ctx.fillStyle = '#ffffff';
                              ctx.fillRect(0, 0, width, height);
                              ctx.drawImage(img, 0, 0);
                              
                              const imageData = canvas.toDataURL('image/png');
                              URL.revokeObjectURL(url);
                              
                              resolve({
                                data: imageData,
                                width: width,
                                height: height
                              });
                            };
                            img.onerror = () => {
                              URL.revokeObjectURL(url);
                              resolve(null);
                            };
                            img.src = url;
                          } catch (err) {
                            console.error('[PDF] SVG conversion error:', err);
                            resolve(null);
                          }
                        });
                      };
                      
                      // Helper function to capture chart
                      const captureChart = async (containerId, chartName) => {
                        const container = document.getElementById(containerId);
                        if (!container) {
                          console.warn(`[PDF] ${chartName} container not found: ${containerId}`);
                          return null;
                        }
                        
                        // Scroll into view
                        container.scrollIntoView({ behavior: 'instant', block: 'center' });
                        await new Promise(resolve => setTimeout(resolve, 500));
                        
                        // Hide tooltips
                        const tooltips = container.querySelectorAll('.recharts-tooltip-wrapper, [role="tooltip"]');
                        tooltips.forEach(t => t.style.display = 'none');
                        await new Promise(resolve => setTimeout(resolve, 200));
                        
                        // Try to find SVG element (Recharts uses SVG)
                        const svg = container.querySelector('svg');
                        if (svg) {
                          console.log(`[PDF] Found SVG for ${chartName}, converting...`);
                          const svgRect = svg.getBoundingClientRect();
                          const width = Math.max(svgRect.width || 800, 800);
                          const height = Math.max(svgRect.height || 300, 300);
                          
                          const result = await svgToImage(svg, width, height);
                          if (result) {
                            console.log(`[PDF] ${chartName} captured via SVG: ${result.width}x${result.height}`);
                            tooltips.forEach(t => t.style.display = '');
                            return result;
                          }
                        }
                        
                        // Fallback to html2canvas if SVG method fails
                        if (window.html2canvas) {
                          try {
                            console.log(`[PDF] Falling back to html2canvas for ${chartName}...`);
                            const canvas = await window.html2canvas(container, {
                              scale: 2,
                              backgroundColor: '#ffffff',
                              logging: true,
                              useCORS: true,
                              allowTaint: true
                            });
                            
                            const imageData = canvas.toDataURL('image/png');
                            console.log(`[PDF] ${chartName} captured via html2canvas: ${canvas.width}x${canvas.height}`);
                            
                            tooltips.forEach(t => t.style.display = '');
                            
                            return {
                              data: imageData,
                              width: canvas.width,
                              height: canvas.height
                            };
                          } catch (err) {
                            console.error(`[PDF] html2canvas error for ${chartName}:`, err);
                            tooltips.forEach(t => t.style.display = '');
                            return null;
                          }
                        }
                        
                        tooltips.forEach(t => t.style.display = '');
                        return null;
                      };
                      
                      // Wait for charts to render
                      await new Promise(resolve => setTimeout(resolve, 1500));
                      
                      // Capture all charts
                      console.log('[PDF] Starting chart capture...');
                      chartImages.balanceChart = await captureChart('balance-chart-container', 'Balance Chart');
                      chartImages.incomeChart = await captureChart('income-chart-container', 'Income Chart');
                      chartImages.rollingChart = await captureChart('rolling-period-chart-container', 'Rolling Period Chart');
                      
                      const capturedCount = Object.keys(chartImages).filter(k => chartImages[k] !== null).length;
                      console.log(`[PDF] Chart capture complete: ${capturedCount} of 3 charts captured`);
                      if (capturedCount === 0) {
                        console.error('[PDF] WARNING: NO CHARTS CAPTURED! Check console for errors.');
                      }
                      
                      
                      PDFExportManager.exportAdvancedCalculator(
                        exportInputs,
                        {
                          ...historicalResults,
                          sustainableIncome: historicalResults?.sustainableIncome || 0,
                          finalRealBalance: historicalResults?.finalRealBalance || 0,
                          yearlyData: historicalSeries,
                          rollingPeriodStats: rollingStats
                        },
                        { 
                          simulations: mcPercentiles, 
                          successRate: mcSuccessRate || 0, 
                          medianFinalBalance: mcPercentiles?.p50 || 0, 
                          percentile10: mcPercentiles?.p10 || 0, 
                          percentile90: mcPercentiles?.p90 || 0,
                          simulationCount: 1000
                        },
                        withdrawalStrategy,
                        chartImages
                      );
                      
                      setIsExporting(false);
                      
                      } catch (error) {
                        console.error('[PDF Export] ERROR:', error);
                        setIsExporting(false);
                        alert('Error generating PDF: ' + error.message + '\n\nPlease check the console (F12) for details.');
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors shadow-lg inline-flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download PDF
                  </button>

                  <button
                    onClick={() => {
                      if (!hasAdvancedAccess) {
                        window.dispatchEvent(new CustomEvent('showPricingModal', {
                          detail: { source: 'compare_strategies', calculatorTier: 'advanced', surface: 'advanced_compare_strategies' }
                        }));
                        return;
                      }
                      if (eitherBelowPreservation) {
                        alert(
                          "Strategy comparison is only available when both partners have reached preservation age (60).\n\n" +
                          "Dynamic strategies (Dynamic SWR, Vanguard Guardrails, Floor and Ceiling) can only be used when both partners are 60+.\n\n" +
                          "Please wait until both partners reach age 60 to compare strategies."
                        );
                        return;
                      }
                      // Lazily compute all 4 strategies' MSI here (on click) rather than on every
                      // Run — this modal is opened occasionally, so it's fine to pay the cost here.
                      try {
                        setMsiResults({
                          Fixed: Math.round(computeFixedSeriesRef.current ? computeFixedSeriesRef.current() : 0),
                          FloorAndCeiling: Math.round(computeFloorCeilingSeriesRef.current ? computeFloorCeilingSeriesRef.current() : 0),
                          VanguardGuardrails: Math.round(computeVanguardGuardrailsSeriesRef.current ? computeVanguardGuardrailsSeriesRef.current() : 0),
                          DynamicSWR: Math.round(computeDynamicSWRSeriesRef.current ? computeDynamicSWRSeriesRef.current() : 0),
                        });
                      } catch (error) {
                        console.error('Strategy comparison MSI error:', error);
                      }
                      setShowComparison(true);
                    }}
                    disabled={hasAdvancedAccess && eitherBelowPreservation}
                    className={`font-bold py-3 px-6 rounded-lg transition-colors shadow-lg inline-flex items-center gap-2 ${
                      hasAdvancedAccess && eitherBelowPreservation
                        ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                    }`}
                    title={
                      !hasAdvancedAccess
                        ? 'Subscribe to compare withdrawal strategies'
                        : eitherBelowPreservation
                          ? "Strategy comparison requires both partners to be 60+"
                          : "Compare all withdrawal strategies"
                    }
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2m0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    {hasAdvancedAccess ? 'Compare Strategies' : 'Compare — Subscribe'}
                  </button>

                  <div ref={exportMenuRef} className="relative">
                    <button
                      onClick={() => setShowExportMenu(!showExportMenu)}
                      className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition-colors shadow-lg inline-flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {showExportMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                        <button
                          onClick={async () => {
                            setShowExportMenu(false);
                            if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                              startPdfUnlockCheckout({
                                calculatorTier: 'advanced',
                                surface: 'advanced_export_menu_pdf',
                              }).catch(alertCheckoutFailure);
                              return;
                            }
                            try {
                              const chartImages = await captureCharts();
                              const exportInputs = buildExportInputs();
                              
                              PDFExportManager.exportAdvancedCalculator(
                                exportInputs,
                                {
                                  ...historicalResults,
                                  sustainableIncome: historicalResults?.sustainableIncome || 0,
                                  finalRealBalance: historicalResults?.finalRealBalance || 0,
                                  yearlyData: historicalSeries,
                                  rollingPeriodStats: rollingStats
                                },
                                { 
                                  simulations: mcPercentiles, 
                                  successRate: mcSuccessRate || 0, 
                                  medianFinalBalance: mcPercentiles?.p50 || 0, 
                                  percentile10: mcPercentiles?.p10 || 0, 
                                  percentile90: mcPercentiles?.p90 || 0,
                                  simulationCount: 1000
                                },
                                withdrawalStrategy,
                                chartImages
                              );
                            } catch (error) {
                              console.error('Error generating PDF:', error);
                              alert('Error generating PDF: ' + error.message);
                            }
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 text-gray-700 font-semibold flex items-center gap-2 rounded-t-lg"
                        >
                          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          Export PDF
                        </button>
                        <button
                          onClick={() => {
                            setShowExportMenu(false);
                            PDFExportManager.exportAdvancedToCSV(historicalSeries, { startYear, yourAge });
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 text-gray-700 font-semibold flex items-center gap-2 rounded-b-lg border-t border-gray-100"
                        >
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Export CSV
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Full-width Historical Results */}
          {showHistorical && (
            <div className="lg:col-span-3 mt-3">
              {rollingMSI && rollingMSI.length > 0 && (
                (() => {
                  const incomes = (rollingMSI || []).map((d) => d.income).filter((n) => Number.isFinite(n)).sort((a,b)=>a-b);
                  const lowest = incomes.length ? incomes[0] : 0;
                  const highest = incomes.length ? incomes[incomes.length-1] : 0;
                  
                  // Find the specific period result for the current analysis period
                  const currentStartYear = Number(startYear) || 1980;
                  const currentPeriodResult = (rollingMSI || []).find(d => d.start === currentStartYear);
                  const periodDurationYears = Math.max(0, parseInt(periodLength) || 0);
                  const periodLabel = `${currentStartYear}–${Math.max(currentStartYear, currentStartYear + periodDurationYears - 1)}`;
                  
                  // MAIN BOX VALUE: same rolling array entry as the chart bar for this start year
                  let currentPeriodIncome = 0;
                  if (currentPeriodResult && Number.isFinite(Number(currentPeriodResult.income))) {
                    currentPeriodIncome = Number(currentPeriodResult.income);
                  } else if (String(withdrawalStrategy) === 'Fixed' || String(withdrawalStrategy) === 'fixed') {
                    currentPeriodIncome = historicalResults?.sustainableIncome || 0;
                  } else {
                    currentPeriodIncome = currentStrategyMSIRef.current || 0;
                  }
                  
                  // Calculate "Median Real Income" box value (median to avoid outlier distortion)
                  let median = 0;
                  if (incomes.length > 0) {
                    // Use median for ALL strategies to avoid outlier distortion
                    const mid = Math.floor(incomes.length/2);
                    median = incomes.length % 2 === 1 ? incomes[mid] : Math.round((incomes[mid-1] + incomes[mid]) / 2);
                  }
                  
                  const fmt = (v) => `$${(v||0).toLocaleString()}`;
                  return (
                    <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                      <div className="bg-green-50 border border-green-200 rounded-md p-4 text-center">
                        <div className="text-[10px] uppercase tracking-wide text-green-700">Lowest Real Income</div>
                        <div className="text-lg font-semibold text-green-900">{fmt(lowest)}</div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-md p-4 text-center">
                        <div className="text-[10px] uppercase tracking-wide text-green-700">Median Real Income</div>
                        <div className="text-lg font-semibold text-green-900">{fmt(median)}</div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-md p-4 text-center">
                        <div className="text-[10px] uppercase tracking-wide text-green-700">Highest Real Income</div>
                        <div className="text-lg font-semibold text-green-900">{fmt(highest)}</div>
                      </div>
                      <div className="bg-indigo-50 border border-indigo-200 rounded-md p-4 text-center">
                          <div className="text-[10px] uppercase tracking-wide text-indigo-700">Income for {periodLabel}</div>
                        <div className="text-lg font-semibold text-indigo-900">{fmt(currentPeriodIncome)}</div>
                        {phasedRetirement && relationshipStatus === 'couple' && (
                          <div className="mt-2 text-[9px] text-indigo-600 italic">
                            Assumes all super in pension phase (tax-free)
                          </div>
                        )}
                        {!phasedRetirement && relationshipStatus === 'couple' && (
                          <div className="mt-2 text-[9px] text-indigo-600 italic">
                            Applies accumulation tax to accumulation-phase balances
                          </div>
                        )}
                      </div>
                    </div>
                    </>
                  );
                })()
              )}
              {historicalSeries.length > 0 && (
                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-white border border-gray-200 rounded-md p-3" id="balance-chart-container">
                    <div className="text-sm font-semibold text-gray-800 mb-2">Real Super Balance Over Time</div>
                    
                    <div style={{ width: '100%', height: 260 }}>
                      <ResponsiveContainer>
                        <AreaChart data={historicalSeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="balanceColor" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="yearIndex" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <RechartsTooltip 
                            formatter={(v) => `$${Number(v).toLocaleString()}`} 
                            labelFormatter={(l) => `Year ${l}`}
                          />
                          <Legend />
                          <Area type="monotone" dataKey="balance" name={Number(monteCarloTargetIncome) > 0 ? "Balance at MSI (Max Sustainable Income)" : "Super Balance"} stroke="#2563eb" fillOpacity={1} fill="url(#balanceColor)" isAnimationActive={false} />
                          {Number(monteCarloTargetIncome) > 0 && (
                            <Area type="monotone" dataKey="balanceWithTargetIncome" name="Balance at Target Income" stroke="#dc2626" fillOpacity={0} strokeWidth={2} dot={false} isAnimationActive={false} />
                          )}
                          <Customized component={<LifeBands />} />
                          <Customized component={<LifeP50 />} />
                          <Customized component={<LifeP20 />} />
                          <Customized component={<LifeP10 />} />
                          <Customized component={<LifeP5 />} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-3 text-sm font-semibold text-gray-700">Detailed Yearly Data</div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-md p-3" id="income-chart-container">
                    <div className="text-sm font-semibold text-gray-800 mb-1">Real Annual Income by Source</div>
                    <div className="text-xs text-gray-600 mb-2">All income amounts are inflation-adjusted (today's dollars).</div>
                    
                    <div style={{ width: '100%', height: 260 }}>
                      <ResponsiveContainer>
                        <ComposedChart data={historicalSeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="yearIndex" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Legend />
                          <RechartsTooltip 
                            formatter={(value, name, props) => {
                              const data = props.payload;
                              const total = (data.otherIncome || 0) + (data.bridgeWithdrawal || 0) + (data.withdrawalFromSuper || 0) + (data.lumpSumWithdrawal || 0) + (data.pension || 0);
                              return [
                                `$${Number(value).toLocaleString()}`,
                                name
                              ];
                            }}
                            labelFormatter={(label) => `Year ${label}`}
                            content={({ active, payload, label }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                const taxable = data.otherTaxableIncome || 0;
                                const nonTaxable = data.otherNonTaxableIncome || 0;
                                const annuity = data.annuityIncome || 0;
                                const dbPensionVal = data.definedBenefitPension || 0;
                                const bridgeWithdrawal = data.bridgeWithdrawal || 0;
                                const superWithdrawal = data.withdrawalFromSuper || 0;
                                const lumpSumWithdrawal = data.lumpSumWithdrawal || 0;
                                const lumpSumContribution = data.lumpSumContribution || 0;
                                const pension = data.pension || 0;
                                const mortgageOutflow = data.mortgageOutflow || 0;
                                const mortgagePayoffFromSuper = data.mortgagePayoffFromSuper || 0;
                                const total = Number.isFinite(data.totalIncome)
                                  ? data.totalIncome
                                  : taxable + nonTaxable + annuity + dbPensionVal + bridgeWithdrawal + superWithdrawal + lumpSumWithdrawal + pension - mortgageOutflow;
                                
                                return (
                                  <div className="bg-white p-3 border border-gray-200 rounded-md shadow-lg">
                                    <p className="font-semibold text-gray-800">Year {label}</p>
                                    <div className="space-y-1 text-sm">
                                      {taxable > 0 && <div className="flex justify-between gap-4">
                                        <span style={{color:'#f97316'}}>Employment &amp; Taxable:</span>
                                        <span className="font-medium">${taxable.toLocaleString()}</span>
                                      </div>}
                                      {nonTaxable > 0 && <div className="flex justify-between gap-4">
                                        <span style={{color:'#a855f7'}}>Non-Taxable Income:</span>
                                        <span className="font-medium">${nonTaxable.toLocaleString()}</span>
                                      </div>}
                                      {annuity > 0 && <div className="flex justify-between gap-4">
                                        <span style={{color:'#06b6d4'}}>Annuity (Tax-Free):</span>
                                        <span className="font-medium">${annuity.toLocaleString()}</span>
                                      </div>}
                                      {dbPensionVal > 0 && <div className="flex justify-between gap-4">
                                        <span style={{color:'#f43f5e'}}>Defined Benefit Pension:</span>
                                        <span className="font-medium">${dbPensionVal.toLocaleString()}</span>
                                      </div>}
                                      {bridgeWithdrawal > 0 && <div className="flex justify-between gap-4">
                                        <span style={{color:'#d97706'}}>Bridge Bucket:</span>
                                        <span className="font-medium">${bridgeWithdrawal.toLocaleString()}</span>
                                      </div>}
                                      <div className="flex justify-between gap-4">
                                        <span style={{color:'#3b82f6'}}>Super Withdrawal:</span>
                                        <span className="font-medium">${superWithdrawal.toLocaleString()}</span>
                                      </div>
                                      {lumpSumWithdrawal > 0 && <div className="flex justify-between gap-4">
                                        <span style={{color:'#f59e0b'}}>Lump Sum Withdrawal:</span>
                                        <span className="font-medium">${lumpSumWithdrawal.toLocaleString()}</span>
                                      </div>}
                                      {lumpSumContribution > 0 && <div className="flex justify-between gap-4">
                                        <span style={{color:'#059669'}}>Super Contribution:</span>
                                        <span className="font-medium">${lumpSumContribution.toLocaleString()}</span>
                                      </div>}
                                      {mortgageOutflow > 0 && <div className="flex justify-between gap-4">
                                        <span style={{color:'#475569'}}>Mortgage costs:</span>
                                        <span className="font-medium">-${mortgageOutflow.toLocaleString()}</span>
                                      </div>}
                                      {mortgagePayoffFromSuper > 0 && <div className="flex justify-between gap-4 text-xs">
                                        <span style={{color:'#475569'}}>Mortgage payoff funded from super:</span>
                                        <span className="font-medium">${mortgagePayoffFromSuper.toLocaleString()}</span>
                                      </div>}
                                      <div className="flex justify-between gap-4">
                                        <span style={{color:'#10b981'}}>Age Pension:</span>
                                        <span className="font-medium">${pension.toLocaleString()}</span>
                                      </div>
                                      <hr className="my-1" />
                                      <div className="flex justify-between font-semibold">
                                        <span className="text-gray-800">Total Income (today's dollars):</span>
                                        <span className="text-gray-800">${total.toLocaleString()}</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="otherTaxableIncome" stackId="income" name="Employment & Taxable" fill="#f97316" isAnimationActive={false} />
                          <Bar dataKey="otherNonTaxableIncome" stackId="income" name="Non-Taxable Income" fill="#a855f7" isAnimationActive={false} />
                          <Bar dataKey="annuityIncome" stackId="income" name="Annuity (Tax-Free)" fill="#06b6d4" isAnimationActive={false} />
                          <Bar dataKey="definedBenefitPension" stackId="income" name="Defined Benefit Pension" fill="#f43f5e" isAnimationActive={false} />
                          <Bar dataKey="bridgeWithdrawal" stackId="income" name="Bridge Bucket" fill="#d97706" isAnimationActive={false} />
                          <Bar dataKey="withdrawalFromSuper" stackId="income" name="Super Withdrawal" fill="#3b82f6" isAnimationActive={false} />
                          <Bar dataKey="lumpSumWithdrawal" stackId="income" name="Lump Sum Withdrawal" fill="#f59e0b" isAnimationActive={false} />
                          <Bar dataKey="pension" stackId="income" name="Age Pension" fill="#10b981" isAnimationActive={false} />
                          {historicalSeries.some((row) => (row.lumpSumContribution || 0) > 0) && (
                            <Line type="monotone" dataKey="lumpSumContribution" name="Super Contribution" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                          )}
                          {/* For Fixed strategy: show average total income across all years */}
                          {(() => {
                            if (withdrawalStrategy !== 'Fixed' && withdrawalStrategy !== 'fixed') return null;
                            
                            const rowTotal = (row) => Number.isFinite(row.totalIncome)
                              ? row.totalIncome
                              : (row.otherTaxableIncome || 0) + (row.otherNonTaxableIncome || 0) + (row.annuityIncome || 0) + (row.definedBenefitPension || 0) + (row.bridgeWithdrawal || 0) + (row.withdrawalFromSuper || 0) + (row.lumpSumWithdrawal || 0) + (row.pension || 0) - (row.mortgageOutflow || 0);
                            const allIncomes = historicalSeries.map(rowTotal);
                            if (allIncomes.length === 0) return null;
                            const avgIncomeAllYears = allIncomes.reduce((sum, inc) => sum + inc, 0) / allIncomes.length;
                            return (
                              <Line 
                                type="monotone" 
                                dataKey={() => avgIncomeAllYears}
                                stroke="#DC2626" 
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                                name={`ALL-YEARS average (${allIncomes.length} yrs)`}
                                isAnimationActive={false}
                              />
                            );
                          })()}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                    {(withdrawalStrategy === 'Fixed' || withdrawalStrategy === 'fixed') && historicalSeries.length > 0 && (() => {
                      const rowTotalB = (row) => Number.isFinite(row.totalIncome)
                        ? row.totalIncome
                        : (row.otherTaxableIncome || 0) + (row.otherNonTaxableIncome || 0) + (row.annuityIncome || 0) + (row.definedBenefitPension || 0) + (row.bridgeWithdrawal || 0) + (row.withdrawalFromSuper || 0) + (row.lumpSumWithdrawal || 0) + (row.pension || 0) - (row.mortgageOutflow || 0);
                      const allIncomes = historicalSeries.map(rowTotalB);
                      const avgIncomeAllYears = allIncomes.reduce((sum, inc) => sum + inc, 0) / allIncomes.length;
                      return (
                        <p className="text-xs text-gray-600 mt-2 italic px-3">
                          <strong>Why isn't Fixed income perfectly flat?</strong> There can be up to three different income levels in phased retirement scenarios:
                          Phase 1 (both partners under 60, no super access), Phase 2 (one partner 60+, one under 60), and Phase 3 (both partners 60+).
                          Australian law also requires minimum annual withdrawals from super based on your age (4% under 65, 5% at 65–74, 6% at 75–79, then higher) once you're eligible to access it — that minimum is a floor under Fixed MSI, and it still applies while you're working under phased retirement.
                          Two things push income above the steady-state MSI you see in later years: (1) in a strong market year, the mandatory minimum (a % of the retired partner’s balance) can exceed MSI; or (2) under phased retirement, the retired partner’s minimum is withdrawn <em>on top of</em> the still-working partner’s salary — the working partner’s super is not drawn and does not take a minimum until they retire.
                          You must withdraw the higher amount to stay compliant, even if you don't need the income for lifestyle. The red dotted line is the ALL-YEARS average across all {allIncomes.length} years (not only the spike years): <strong>${Math.round(avgIncomeAllYears).toLocaleString()}/year</strong>.
                          Age Pension changes can also cause variations.
                        </p>
                      );
                    })()}
                  </div>
                </div>
              )}
              {historicalSeries.length > 0 && (() => {
                const showLumpSumColumn = historicalSeries.some((r) => (r.lumpSumWithdrawal || 0) > 0);
                const showContributionColumn = historicalSeries.some((r) => (r.lumpSumContribution || 0) > 0);
                const showBridgeColumn = historicalSeries.some((r) => (r.bridgeWithdrawal || 0) > 0 || (r.bridgeShortfall || 0) > 0 || r.bridgeDepletedBeforeAccess);
                const showMortgageColumn = historicalSeries.some((r) => (r.mortgageOpeningBalance || 0) > 0 || (r.mortgageOutflow || 0) > 0);
                const showContributionFlags = historicalSeries.some((r) => Array.isArray(r.contributionFlags) && r.contributionFlags.length > 0);
                return (
                <div className="mt-6 lg:col-span-3">
                  <h5 className="text-sm font-semibold text-gray-800 mb-2">Detailed Yearly Data</h5>
                  <div className="overflow-x-auto bg-white border border-gray-200 rounded-md">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Year</th>
                          <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Super Balance (year-end)</th>
                          {showBridgeColumn && (
                            <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Bridge Balance</th>
                          )}
                          {showBridgeColumn && (
                            <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Bridge Draw</th>
                          )}
                          {showMortgageColumn && (
                            <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Mortgage Balance</th>
                          )}
                          {showMortgageColumn && (
                            <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Mortgage Cost</th>
                          )}
                          <th className="text-left px-2 py-2 text-gray-600 text-[10px]" title="Withdrawal % shown is of the balance at the START of that year (i.e. the PRIOR row's year-end balance), not the year-end balance shown in this row.">Super Withdrawal (% of start-of-year balance)</th>
                          {showLumpSumColumn && (
                            <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Lump Sum</th>
                          )}
                          {showContributionColumn && (
                            <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Contrib.</th>
                          )}
                          <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Pension</th>
                          <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Other Income</th>
                          <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Total Income (today's $)</th>
                          <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Return</th>
                          {showContributionFlags && (
                            <th className="text-left px-2 py-2 text-gray-600 text-[10px]">Contribution Flags</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {historicalSeries.map((row) => {
                          const totalIncome = Number.isFinite(row.totalIncome)
                            ? row.totalIncome
                            : (row.bridgeWithdrawal || 0) + (row.withdrawalFromSuper || 0) + (row.lumpSumWithdrawal || 0) + (row.pension || 0) + (row.otherTaxableIncome || 0) + (row.otherNonTaxableIncome || 0) + (row.annuityIncome || 0) + (row.definedBenefitPension || 0);
                          const bridgeBalance = (row.bridgeCashBalance || 0) + (row.bridgeInvestmentBalance || 0);
                          return (
                            <tr key={`yr-${row.yearIndex}`} className="border-b border-gray-100">
                              <td className="px-2 py-2 text-gray-800 text-[10px]">{row.yearIndex}</td>
                              <td className="px-2 py-2 text-gray-800 font-semibold text-[10px]">${(row.balance||0).toLocaleString()}</td>
                              {showBridgeColumn && (
                                <td className="px-2 py-2 text-amber-800 font-semibold text-[10px]">${bridgeBalance.toLocaleString()}</td>
                              )}
                              {showBridgeColumn && (
                                <td className="px-2 py-2 text-amber-700 text-[10px]">
                                  ${(row.bridgeWithdrawal||0).toLocaleString()}
                                  {(row.bridgeShortfall || 0) > 0 && (
                                    <span className="block text-red-600">Short ${(row.bridgeShortfall || 0).toLocaleString()}</span>
                                  )}
                                  {row.bridgeDepletedBeforeAccess && (
                                    <span className="block text-orange-600">Depleted before super access</span>
                                  )}
                                </td>
                              )}
                              {showMortgageColumn && (
                                <td className="px-2 py-2 text-slate-800 font-semibold text-[10px]">${(row.mortgageBalance||0).toLocaleString()}</td>
                              )}
                              {showMortgageColumn && (
                                <td className="px-2 py-2 text-slate-700 text-[10px]">
                                  ${(row.mortgageOutflow||0).toLocaleString()}
                                  {(row.mortgagePayoff || 0) > 0 && (
                                    <span className="block text-slate-600">Payoff ${(row.mortgagePayoff || 0).toLocaleString()}</span>
                                  )}
                                  {(row.mortgageInterest || 0) > 0 && (
                                    <span className="block text-slate-500">Interest ${(row.mortgageInterest || 0).toLocaleString()}</span>
                                  )}
                                </td>
                              )}
                              <td className="px-2 py-2 text-gray-700 text-[10px]">
                                ${(row.withdrawalFromSuper||0).toLocaleString()}
                                {(row.openingBalance || 0) > 0 && (
                                  <span className="block text-gray-400">
                                    {(100 * (row.withdrawalFromSuper || 0) / row.openingBalance).toFixed(1)}% of ${Math.round(row.openingBalance).toLocaleString()} opening
                                  </span>
                                )}
                              </td>
                              {showLumpSumColumn && (
                                <td className="px-2 py-2 text-gray-700 text-[10px]">${(row.lumpSumWithdrawal||0).toLocaleString()}</td>
                              )}
                              {showContributionColumn && (
                                <td className="px-2 py-2 text-emerald-700 text-[10px]">${(row.lumpSumContribution||0).toLocaleString()}</td>
                              )}
                              <td className="px-2 py-2 text-gray-700 text-[10px]">${(row.pension||0).toLocaleString()}</td>
                              <td className="px-2 py-2 text-gray-700 text-[10px]">${((row.otherTaxableIncome||0) + (row.otherNonTaxableIncome||0) + (row.annuityIncome||0) + (row.definedBenefitPension||0)).toLocaleString()}</td>
                              <td className="px-2 py-2 font-bold text-gray-900 text-[10px]">${totalIncome.toLocaleString()}</td>
                              <td className="px-2 py-2 text-gray-800 text-[10px]">{(Math.round((row.realReturn||0)*1000)/10).toFixed(1)}%</td>
                              {showContributionFlags && (
                                <td className="px-2 py-2 text-orange-700 text-[10px]">
                                  {(row.contributionFlags || []).map((flag) => flag.message).join(' ')}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                );
              })()}
              {historicalSeries.length > 0 && (
                <div className="mt-6 lg:col-span-3">
                  <h5 className="text-sm font-semibold text-gray-800">Rolling Period Analysis</h5>
                  <p className="text-xs text-gray-600 mt-1">This analysis shows the MSI (Maximum Sustainable Income) for every possible {(Number(periodLength)||0)}-year period in the historical data.</p>

                  {String(withdrawalStrategy) === 'FloorAndCeiling' && (
                    (() => {
                      const incomes = (rollingMSI || []).map((d) => d.income).filter((n) => Number.isFinite(n)).sort((a,b)=>a-b);
                      const worst = incomes.length ? incomes[0] : 0;
                      const best = incomes.length ? incomes[incomes.length-1] : 0;
                      
                      // For floor and ceiling, use the actual average from the current period's yearly data
                      let median = 0;
                      if (historicalSeries && historicalSeries.length > 0) {
                        const totalIncome = historicalSeries.reduce((sum, year) => {
                          // Use year.totalIncome if available (Fixed strategy), otherwise sum components
                          const yearTotalIncome = year.totalIncome || ((year.withdrawalFromSuper || 0) + (year.pension || 0) + (year.otherTaxableIncome || 0) + (year.otherNonTaxableIncome || 0) + (year.annuityIncome || 0) + (year.definedBenefitPension || 0));
                          return sum + yearTotalIncome;
                        }, 0);
                        median = totalIncome / historicalSeries.length;
                      } else if (incomes.length) {
                        const mid = Math.floor(incomes.length/2);
                        median = incomes.length % 2 === 1 ? incomes[mid] : Math.round((incomes[mid-1] + incomes[mid]) / 2);
                      }
                      // Boxes removed - redundant with main boxes above
                      return null;
                    })()
                  )}

                  <div className="mt-3 relative">
                    {!hasAdvancedAccess && (
                      <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
                        <p className="text-sm text-indigo-900 m-0">
                          Free run shows how this start year holds up above. <strong>Stress more start years</strong> with Advanced Retirement.
                        </p>
                        <button
                          type="button"
                          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md whitespace-nowrap shrink-0"
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('showPricingModal', {
                              detail: { source: 'rolling_history', calculatorTier: 'advanced', surface: 'advanced_rolling_period_gate' }
                            }));
                          }}
                        >
                          Unlock full history
                        </button>
                      </div>
                    )}
                    <div className={`bg-white border border-gray-200 rounded-md p-3 ${!hasAdvancedAccess ? 'opacity-60 pointer-events-none select-none' : ''}`} id="rolling-period-chart-container">
                      <div className="text-sm font-semibold text-gray-800 mb-2">{(String(withdrawalStrategy) === 'FloorAndCeiling' || String(withdrawalStrategy) === 'VanguardGuardrails' || String(withdrawalStrategy) === 'DynamicSWR') ? 'Median Real Income per Period' : 'Max Sustainable Income per Period'}</div>
                      <div style={{ width: '100%', height: 220 }}>
                        <ResponsiveContainer>
                          <BarChart data={hasAdvancedAccess ? (rollingMSI || []) : (rollingMSI || []).slice(0, 8)} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="start" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <RechartsTooltip formatter={(v) => `$${Number(v).toLocaleString()}`} labelFormatter={(l) => `Start ${l}`} />
                            <Bar dataKey="income" name="Max Sustainable Income" fill="#6366f1" isAnimationActive={false} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {historicalSeries.length > 0 && (
                <div className="mt-8 lg:col-span-3">
                  <h5 className="text-sm font-semibold text-gray-800">"What If?" Scenarios (Historical Simulation)</h5>
                  <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Top-left: Change Retirement Period */}
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                      <div className="text-sm font-semibold text-gray-800">Change Retirement Period</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {String(withdrawalStrategy) === 'FloorAndCeiling' ? 'Calculates a new sustainable income.' : 'Tests if your portfolio survives for a different retirement period. If it fails, it calculates a sustainable income for that period.'}
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={whatIfPeriod || ''}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            setWhatIfPeriod(Math.max(0, parseInt(value) || 0));
                          }}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Years"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (!hasAdvancedAccess) {
                              window.dispatchEvent(new CustomEvent('showPricingModal', {
                                detail: { source: 'what_if', calculatorTier: 'advanced', surface: 'advanced_what_if_period' }
                              }));
                              return;
                            }
                            handleChangeRetirementPeriod();
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
                        >
                          {hasAdvancedAccess ? 'Run' : 'Subscribe'}
                        </button>
                      </div>
                      {whatIfPeriodResult && (
                        <div className="mt-3 text-xs text-gray-700 space-y-1">
                          {whatIfPeriodResult.success === false ? (
                            <div className="text-red-600 font-medium">Error: {whatIfPeriodResult.error}</div>
                          ) : whatIfPeriodResult.success === true && whatIfPeriodResult.data ? (
                            <div>
                              <div>New sustainable income: <span className="font-semibold">${(whatIfPeriodResult.data.avgIncome||0).toLocaleString()}</span>/yr</div>
                              {(() => {
                                // Use the base case MSI from the What If result (already uses MINIMAX for Fixed strategy)
                                const currentIncome = whatIfPeriodResult.data.currentMaxSustainableIncome || 0;
                                const newIncome = whatIfPeriodResult.data.avgIncome || 0;
                                const incomeChange = newIncome - currentIncome;
                                const isIncrease = incomeChange > 0;
                                return (
                                  <div>
                                    Income change: <span className={`font-semibold ${isIncrease ? 'text-blue-600' : 'text-red-600'}`}>
                                      {isIncrease ? '+' : ''}${incomeChange.toLocaleString()}
                                    </span>/yr
                                  </div>
                                );
                              })()}
                              <div>Survival years: <span className="font-semibold">{whatIfPeriodResult.data.survivalYears}</span></div>
                              <div className="text-xs text-blue-600 mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                <strong>Explanation:</strong> This shows your sustainable income over a {whatIfPeriod}-year retirement period using the exact same calculation logic as the main calculator. {(Number(periodLength)||35) !== whatIfPeriod ? `Your current ${periodLength}-year plan yields $${(whatIfPeriodResult.data.currentMaxSustainableIncome || currentStrategyMSIRef.current || 0).toLocaleString()}/yr.` : ''}
                              </div>
                            </div>
                          ) : (
                            <div className="text-gray-500">No result available</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                      <div className="text-sm font-semibold text-gray-800">Change Annual Fees</div>
                      <div className="text-xs text-gray-600 mt-1">Enter the % change in investment fees. If it fails, it calculates what's needed to succeed with the new fees.</div>
                      <div className="mt-3 flex items-center gap-2">
                        <input
                          type="number"
                          step="0.1"
                          value={whatIfFeeChangePct}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || (!isNaN(Number(value)) && Number(value) >= -10 && Number(value) <= 10)) {
                              setWhatIfFeeChangePct(value);
                            }
                          }}
                          className="w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Δ Fees (% pts)"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (!hasAdvancedAccess) {
                              window.dispatchEvent(new CustomEvent('showPricingModal', {
                                detail: { source: 'what_if', calculatorTier: 'advanced', surface: 'advanced_what_if_fees' }
                              }));
                              return;
                            }
                            handleChangeAnnualFees();
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
                        >
                          {hasAdvancedAccess ? 'Run' : 'Subscribe'}
                        </button>
                      </div>
                      {whatIfFeeChangeResult && (
                        <div className="mt-3 text-xs text-gray-700 space-y-1">
                          {whatIfFeeChangeResult.success === false ? (
                            <div className="text-red-600 font-medium">Error: {whatIfFeeChangeResult.error}</div>
                          ) : whatIfFeeChangeResult.success === true && whatIfFeeChangeResult.data ? (
                            <div>
                              {!whatIfFeeChangeResult.data.success ? (
                            <>
                              <div className="text-red-700">Plan fails</div>
                                  <div>Required starting capital: <span className="font-semibold">${(whatIfFeeChangeResult.data.requiredCapital||0).toLocaleString()}</span></div>
                                  <div>Portfolio would last: <span className="font-semibold">{whatIfFeeChangeResult.data.survivalYears}</span> years</div>
                            </>
                              ) : (
                            <>
                              <div>New sustainable income: <span className="font-semibold">${(whatIfFeeChangeResult.data.avgIncome||0).toLocaleString()}</span>/yr</div>
                              {(() => {
                                // Use the base case MSI from the What If result (already uses MINIMAX for Fixed strategy)
                                const currentIncome = whatIfFeeChangeResult.data.currentMaxSustainableIncome || 0;
                                const newIncome = whatIfFeeChangeResult.data.avgIncome || 0;
                                const incomeChange = newIncome - currentIncome;
                                const isIncrease = incomeChange > 0;
                                return (
                                  <div>
                                    Income change: <span className={`font-semibold ${isIncrease ? 'text-blue-600' : 'text-red-600'}`}>
                                      {isIncrease ? '+' : ''}${incomeChange.toLocaleString()}
                                    </span>/yr
                                  </div>
                                );
                              })()}
                              <div className="text-xs text-blue-600 mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                <strong>Explanation:</strong> Compares MSI at current fees vs. with an extra {(Number(whatIfFeeChangePct)||0)}% investment fee. Higher fees reduce sustainable income.
                              </div>
                            </>
                              )}
                            </div>
                          ) : (
                            <div className="text-gray-500">No result available</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                      <InfoTooltip content="Models the impact of experiencing specific returns for the first 3 years of retirement. Enter a negative number (e.g., -20) to see the effect of a market crash, or 0 to see baseline historical returns.">
                        <div className="text-sm font-semibold text-gray-800">Sequence of Returns Risk</div>
                      </InfoTooltip>
                      <div className="text-xs text-gray-600 mt-1">% return for first 3 years (e.g., -20 = market crash, 0 = zero growth)</div>
                      <div className="mt-3 flex items-center gap-2">
                        <input
                          type="text"
                          value={whatIfSeqRiskPct}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow typing by being more permissive - allow empty, negative sign, and numbers
                            if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
                              setWhatIfSeqRiskPct(value);
                            }
                          }}
                          className="w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="-20 (negative = crash)"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (!hasAdvancedAccess) {
                              window.dispatchEvent(new CustomEvent('showPricingModal', {
                                detail: { source: 'what_if', calculatorTier: 'advanced', surface: 'advanced_what_if_sequence' }
                              }));
                              return;
                            }
                            handleSequenceRisk();
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
                        >
                          {hasAdvancedAccess ? 'Run' : 'Subscribe'}
                        </button>
                      </div>
                      {whatIfSeqRiskResult && (
                        <div className="mt-3 text-sm">
                          {whatIfSeqRiskResult.success === false ? (
                            <div className="text-red-600 font-medium">Error: {whatIfSeqRiskResult.error}</div>
                          ) : whatIfSeqRiskResult.success === true && whatIfSeqRiskResult.data ? (
                            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                              <div className="text-sm font-bold text-red-900 mb-1">⚠️ What if returns are {whatIfSeqRiskPct >= 0 ? '+' : ''}{whatIfSeqRiskPct}% pts for 3 years?</div>
                              <div className="text-xs text-red-700">
                                Balance after 3 years: ${(whatIfSeqRiskResult.data.totalSuperAfterDrop||0).toLocaleString()}<br/>
                                {whatIfSeqRiskResult.data.showYearlyBreakdown ? (
                                  <>
                                    Income (Years 1-{whatIfSeqRiskResult.data.gapYears || 3}): ${(whatIfSeqRiskResult.data.incomeBeforePensionAge||0).toLocaleString()}/year<br/>
                                    Income (Years {(whatIfSeqRiskResult.data.gapYears || 3) + 1}+): ${(whatIfSeqRiskResult.data.incomeAfterPensionAge||0).toLocaleString()}/year (with Age Pension)<br/>
                                  </>
                                ) : (
                                  <>
                                    Income would be: ${(whatIfSeqRiskResult.data.maxSustainableIncome||0).toLocaleString()}/year<br/>
                                  </>
                                )}
                                Impact: {(whatIfSeqRiskResult.data.impact||0) >= 0 ? '+' : ''}${(whatIfSeqRiskResult.data.impact||0).toLocaleString()}/year
                              </div>
                            </div>
                          ) : (
                            <div className="text-gray-500">No result available</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {historicalSeries.length > 0 && (
                <div className="mt-6 lg:col-span-3">
                  <CalculationTransparency
                    kind="advanced"
                    inputs={lastRunInputsRef.current || {
                      relationshipStatus,
                      yourAge,
                      partnerAge,
                      yourSuper,
                      partnerSuper,
                      periodLength,
                      startYear,
                      withdrawalStrategy,
                      phasedRetirement,
                      yourRetirementAge,
                      partnerRetirementAge,
                      yourSalary,
                      partnerSalary,
                      includePension,
                      isHomeowner,
                      sp500,
                      ausShares,
                      intlShares,
                      bonds,
                      cash,
                      adminFees,
                      adminFeePercentage,
                      investmentFees,
                      insuranceFees,
                      targetFinalBalance,
                    }}
                    results={{ incomeByYear: historicalSeries, finalBalance: historicalResults?.finalRealBalance }}
                    chartData={historicalSeries}
                  />
                </div>
              )}
              
            </div>
          )}
          {/* Monte Carlo page */}
          {showMonteCarlo && mcPercentiles && (
            <div className="lg:col-span-3 mt-6">
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
                <strong>Monte Carlo note:</strong> uses the same strategy engines as Historical Analysis (TBC split, accumulation tax, Age Pension, phased retirement). Each trial draws random historical years for returns and inflation.
              </div>
              {mcMedianIncomesSummary && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3">
                  <div className="text-sm font-semibold text-green-900">Expected Real Income Range</div>
                  <div className="text-xs text-green-800">This shows the range of median incomes from across all 1000 simulations.</div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="bg-white/70 border border-green-200 rounded p-3 text-center">
                        <div className="text-[10px] uppercase tracking-wide text-green-700">Worst Case Median</div>
                        <div className="text-lg font-semibold text-green-900">${(mcMedianIncomesSummary?.worst||0).toLocaleString()}</div>
                        <div className="text-[9px] text-green-600 mt-1">Lowest median income across simulations</div>
                        {mcIncomePercentiles?.p0 === 0 && (
                          <div className="text-[9px] text-red-600 mt-1">Target ${(Number(monteCarloTargetIncome)||0).toLocaleString()} fails in some simulations</div>
                        )}
                      </div>
                    <div className="bg-white/70 border border-green-200 rounded p-3 text-center">
                      <div className="text-[10px] uppercase tracking-wide text-green-700">Median of Medians</div>
                      <div className="text-lg font-semibold text-green-900">${(mcMedianIncomesSummary.median||0).toLocaleString()}</div>
                    </div>
                    <div className="bg-white/70 border border-green-200 rounded p-3 text-center">
                      <div className="text-[10px] uppercase tracking-wide text-green-700">Best Case Median</div>
                      <div className="text-lg font-semibold text-green-900">${(mcMedianIncomesSummary.best||0).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-1 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-md p-3">
                  <div className="text-sm font-semibold text-gray-800 mb-2">Range of Final Balances</div>
                  <div style={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={[
                        { label: 'p10', value: mcPercentiles.p10 },
                        { label: 'p25', value: mcPercentiles.p25 },
                        { label: 'p50', value: mcPercentiles.p50 },
                        { label: 'p75', value: mcPercentiles.p75 },
                        { label: 'p90', value: mcPercentiles.p90 },
                      ]} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <RechartsTooltip formatter={(v) => `$${Number(v).toLocaleString()}`} labelFormatter={(l) => l.toUpperCase()} />
                        <Bar dataKey="value" name="Final Balance" fill="#0ea5e9" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="text-sm font-semibold text-blue-900">Success Rate</div>
                  <div className="mt-1 text-xl font-bold text-blue-900">{Number.isFinite(mcSuccessRate) ? `${mcSuccessRate}%` : '—'}</div>
                  <div className="text-xs text-blue-800 mt-1">Probability of portfolio surviving {(Number(periodLength)||0)} years</div>
                  <div className="mt-2 bg-blue-100 border border-blue-200 rounded p-2">
                    <div className="text-[11px] font-semibold text-blue-900">Tested with a fixed income of ${((Number(monteCarloTargetIncome)||0).toLocaleString())}</div>
                  </div>
                  {mcIncomePercentiles && (
                    <div className="mt-3 bg-white/60 border border-blue-100 rounded p-2">
                      <div className="text-xs font-semibold text-blue-900 mb-2">Outcome percentiles</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr>
                            <th className="text-left text-blue-800 font-medium">Percentile</th>
                            <th className="text-right text-blue-800 font-medium">Income</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td className="text-blue-900">10th</td><td className="text-right text-blue-900">${(mcIncomePercentiles.p10||0).toLocaleString()}</td></tr>
                          <tr><td className="text-blue-900">25th</td><td className="text-right text-blue-900">${(mcIncomePercentiles.p25||0).toLocaleString()}</td></tr>
                          <tr><td className="text-blue-900">50th</td><td className="text-right text-blue-900">${(mcIncomePercentiles.p50||0).toLocaleString()}</td></tr>
                          <tr><td className="text-blue-900">75th</td><td className="text-right text-blue-900">${(mcIncomePercentiles.p75||0).toLocaleString()}</td></tr>
                          <tr><td className="text-blue-900">90th</td><td className="text-right text-blue-900">${(mcIncomePercentiles.p90||0).toLocaleString()}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* B2B Analytics & Reporting */}
          {b2bMode && (
            <div className="lg:col-span-3 mt-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-purple-800 mb-4">B2B Platform Analytics & Reporting</h3>
                
                {/* Platform Overview */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-purple-700 mb-3">Platform Overview</h4>
                  <div className="bg-white p-4 rounded-md border border-purple-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Current Tenant</div>
                        <div className="text-lg font-semibold text-purple-900">{tenantConfig.name}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">User Role</div>
                        <div className="text-lg font-semibold text-purple-900">{B2BManagementSystem.USER_ROLES[currentUser.role].name}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">API Requests</div>
                        <div className="text-lg font-semibold text-purple-900">{apiRequests} / {apiLimit}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Features Active</div>
                        <div className="text-lg font-semibold text-purple-900">
                          {Object.values(tenantConfig.features).filter(Boolean).length} / {Object.keys(tenantConfig.features).length}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* API Documentation */}
                {tenantConfig.features.apiAccess && (
                  <div className="mb-6">
                    <h4 className="text-md font-semibold text-purple-700 mb-3">API Documentation</h4>
                    <div className="bg-white p-4 rounded-md border border-purple-200">
                      <div className="space-y-4">
                        {Object.entries(B2BManagementSystem.API_ENDPOINTS).map(([endpoint, config]) => (
                          <div key={endpoint} className="border border-gray-200 rounded-md p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-3">
                                <span className={`px-2 py-1 text-xs font-semibold rounded ${
                                  config.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                                  config.method === 'GET' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {config.method}
                                </span>
                                <span className="text-sm font-medium text-gray-800">{config.path}</span>
                              </div>
                              <span className="text-xs text-gray-500">{config.rateLimit} req/hour</span>
                            </div>
                            <p className="text-sm text-gray-600">{config.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Usage Analytics */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-purple-700 mb-3">Usage Analytics</h4>
                  <div className="bg-white p-4 rounded-md border border-purple-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div>
                        <h5 className="text-sm font-semibold text-gray-800 mb-2">Calculation History</h5>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Total Calculations:</span>
                            <span className="font-medium">1,247</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">This Month:</span>
                            <span className="font-medium">89</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Historical Analysis:</span>
                            <span className="font-medium">456</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h5 className="text-sm font-semibold text-gray-800 mb-2">User Activity</h5>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Active Users:</span>
                            <span className="font-medium">23</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">API Users:</span>
                            <span className="font-medium">8</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Admin Users:</span>
                            <span className="font-medium">3</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h5 className="text-sm font-semibold text-gray-800 mb-2">Feature Usage</h5>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Monte Carlo:</span>
                            <span className="font-medium">67%</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Data Export:</span>
                            <span className="font-medium">34%</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">API Access:</span>
                            <span className="font-medium">12%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* System Health */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-purple-700 mb-3">System Health</h4>
                  <div className="bg-white p-4 rounded-md border border-purple-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Uptime</div>
                        <div className="text-lg font-semibold text-green-600">99.9%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Response Time</div>
                        <div className="text-lg font-semibold text-blue-600">142ms</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">Error Rate</div>
                        <div className="text-lg font-semibold text-green-600">0.01%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">API Status</div>
                        <div className="text-lg font-semibold text-green-600">Healthy</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* B2B Recommendations */}
                <div>
                  <h4 className="text-md font-semibold text-purple-700 mb-3">Platform Recommendations</h4>
                  <div className="bg-white p-4 rounded-md border border-purple-200">
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-2">•</span>
                        <span>Consider upgrading to premium tier for increased API rate limits</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-2">•</span>
                        <span>Enable advanced features for enhanced retirement planning capabilities</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-2">•</span>
                        <span>Implement custom branding for professional client presentation</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-600 mr-2">•</span>
                        <span>Set up automated reporting for regular portfolio monitoring</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>

        {/* Tutorial Panel - draggable and resizable */}
        {showTutorial && (
          <DraggableTutorialPanel onClose={() => setShowTutorial(false)}>
            <AdvancedTutorialGuide />
          </DraggableTutorialPanel>
        )}

        {/* Strategy Comparison Modal */}
        <FixedIncomeVariesNote note={fixedIncomeVariesNote} onClose={() => setFixedIncomeVariesNote(null)} />
        {showComparison && (
          <StrategyComparison
            inputs={{
              relationshipStatus,
              yourAge,
              yourSuper,
              partnerAge,
              partnerSuper,
              periodLength,
              targetFinalBalance,
              includePension,
              phasedRetirement,
              yourRetirementAge,
              partnerRetirementAge,
              yourSalary,
              partnerSalary,
              yourSuperContributions,
              partnerSuperContributions
            }}
            fullInputs={{
              periodLength: Number(periodLength) || 35,
              yourSuper: Number(yourSuper) || 0,
              partnerSuper: Number(partnerSuper) || 0,
              sp500: Number(sp500) || 0,
              ausShares: Number(ausShares) || 0,
              intlShares: Number(intlShares) || 0,
              bonds: Number(bonds) || 0,
              cash: Number(cash) || 0,
              adminFees: Number(adminFees) || 0,
              investmentFees: Number(investmentFees) || 0,
              insuranceFees: Number(insuranceFees) || 0,
              swrRate: Number(dynInitPct) || 4,
              vanguardInitialRate: Number(vgInitPct) || 4.5,
              floorInitialRate: Number(fcInitPct) || 5,
              floorRate: Number(fcFloorPct) || 4,
              ceilingRate: Number(fcCeilPct) || 6,
              phasedRetirement,
              yourRetirementAge: Number(yourRetirementAge) || yourAge,
              partnerRetirementAge: Number(partnerRetirementAge) || partnerAge,
              yourSalary: Number(yourSalary) || 0,
              partnerSalary: Number(partnerSalary) || 0,
              yourSuperContributions: Number(yourSuperContributions) || 0,
              partnerSuperContributions: Number(partnerSuperContributions) || 0,
              otherTaxableIncome: Number(otherTaxableIncome) || 0,
              otherNonTaxableIncome: Number(otherNonTaxableIncome) || 0,
              otherAssets: Number(otherAssets) || 0,
              isHomeowner,
              startYear: Number(startYear) || 1980,
              adminFeePercentage: Number(adminFeePercentage) || 0,
              pensionGrowthRate: Number(pensionGrowthRate) || 0
            }}
            msiResults={msiResults}
            historicalResults={historicalResults}
            mcResults={mcPercentiles}
            onClose={() => setShowComparison(false)}
          />
        )}
        
        {/* Autosave Indicator */}
        <LastSavedTicker savedAt={lastSaved} />

        {/* Loading Spinner - Calculations */}
        {isCalculating && (
          <MobileLoadingSpinner 
            message="Running comprehensive retirement analysis..."
            subMessage="This may take a few moments"
          />
        )}
        
        {/* Loading Spinner - PDF Export */}
        {isExporting && (
          <MobileLoadingSpinner 
            message="Generating PDF..."
            subMessage="Please wait while we create your report..."
          />
        )}

        {/* Cookie Consent Banner */}
        <CookieConsent />

        {/* Instructional Popup - Shows when user tries to modify inputs without free runs OR Monte Carlo Demo */}
        {showInstructionalPopup && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowInstructionalPopup(false)}
          >
            <div 
              className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-8 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setShowInstructionalPopup(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>

              {/* Show email gate if user has no free runs left */}
              {inputsLocked ? (
                <>
                  {/* Icon */}
                  <div className="text-center mb-4">
                    <div className="inline-block bg-orange-100 rounded-full p-4">
                      <svg className="w-12 h-12 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-center mb-4 text-gray-800">
                    You've used your free run
                  </h3>

                  {/* Message */}
                  <p className="text-gray-600 mb-6 text-sm">
                    Enter your email to unlock 1 more free run — no payment, no commitment.
                    <br /><br />
                    We'll also email you a Retirement Readiness Checklist. Unsubscribe anytime.
                  </p>

                  {/* Compliance Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-xs text-blue-700">
                    <strong>Privacy Notice:</strong> We use IP monitoring to ensure fair usage of our free tools. Your email will be stored securely and never shared. See our <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">Privacy Policy</a> for details.
                  </div>

                  {/* Email Input */}
                  <div className="mb-4">
                    <input
                      type="email"
                      value={modalEmail}
                      onChange={(e) => setModalEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      disabled={isSubmittingModalEmail}
                    />
                  </div>

                  {/* Unlock Button */}
                  <button
                    onClick={async () => {
                      if (!modalEmail.trim() || !modalEmail.includes('@')) {
                        alert('Please enter a valid email address');
                        return;
                      }
                      
                      setIsSubmittingModalEmail(true);
                      const trimmed = modalEmail.trim();
                      const source = 'free_runs_gate';
                      trackLeadCaptureEvent('free_runs_email_capture_attempt', {
                        source,
                        calculatorTier: 'advanced'
                      });
                      try {
                        const { ok, data } = await postCalculatorLeadCapture({
                          email: trimmed,
                          calculatorType: 'advanced',
                          source
                        }).catch(async () => {
                          const fallback = await fetch('/api/subscribe', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              email: trimmed,
                              source,
                              calculatorType: 'advanced'
                            })
                          });
                          let fallbackData = {};
                          try { fallbackData = await fallback.json(); } catch (_) {}
                          return { ok: fallback.ok, data: fallbackData };
                        });

                        SubscriptionManager.setAdvancedFreeEmail(trimmed);
                        const leadStored = ok && data.leadStored !== false;
                        if (leadStored) {
                          trackLeadCaptureEvent('free_runs_email_captured', {
                            source,
                            calculatorTier: 'advanced',
                            leadStored: true
                          });
                        } else {
                          trackLeadCaptureEvent('free_runs_email_capture_failed', {
                            source,
                            calculatorTier: 'advanced',
                            leadStored: false,
                            error: data.error || data.message || 'capture_failed'
                          });
                        }

                        const newStatus = SubscriptionManager.canRunAdvancedFree();
                        if (newStatus.canRun) {
                          setShowInstructionalPopup(false);
                          setIsDemoMode(false);
                          setModalEmail('');
                          if (typeof window !== 'undefined' && window.gtag) {
                            window.gtag('event', 'advanced_email_gate_captured', { event_category: 'free_trial', event_label: 'Advanced email gate', value: 1, calculator_tier: 'advanced' });
                          }
                        }
                      } catch (err) {
                        console.error('Email capture error:', err);
                        trackLeadCaptureEvent('free_runs_email_capture_failed', {
                          source: 'free_runs_gate',
                          calculatorTier: 'advanced',
                          leadStored: false,
                          error: err?.message || 'network_error'
                        });
                        alert('Failed to capture email. Please try again.');
                      } finally {
                        setIsSubmittingModalEmail(false);
                      }
                    }}
                    disabled={isSubmittingModalEmail || !modalEmail.trim()}
                    className="w-full bg-orange-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-orange-600 transition-colors mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingModalEmail ? 'Unlocking...' : 'Unlock 1 more run'}
                  </button>

                  {/* Divider */}
                  <div className="relative mb-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">Or skip to unlimited access:</span>
                    </div>
                  </div>

                  {/* Upgrade Button */}
                  <button
                    onClick={() => {
                      setShowInstructionalPopup(false);
                      if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('showPricingModal', {
                          detail: { source: 'advanced_demo', calculatorTier: 'advanced' }
                        }));
                      }
                    }}
                    className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Keep testing this plan — from $14.99/mo
                  </button>
                </>
              ) : (
                <>
                  {/* Monte Carlo Demo Instructions */}
                  {/* Icon */}
                  <div className="text-center mb-4">
                    <div className="inline-block bg-blue-100 rounded-full p-4">
                      <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-center mb-4 text-gray-800">
                    Advanced demo loaded
                  </h3>

                  {/* Message */}
                  <p className="text-gray-600 mb-6 text-lg">
                    This example is pre-filled so you can see the paid analysis before entering your own details.
                    <br /><br />
                    First, run the historical projection to see how this plan performs through real market sequences.
                    <br /><br />
                    Then run Monte Carlo to compare thousands of possible outcomes. Use your free run on your own numbers when you are ready.
                  </p>

                  {/* Button */}
                  <button
                    onClick={() => {
                      console.log('🎯 Popup button clicked - closing popup and running calculation');
                      setShowInstructionalPopup(false);
                      
                      // Run the calculation after a brief delay (exactly like basic calculator)
                      setTimeout(() => {
                        runAdvancedProjection();
                      }, 300);
                    }}
                    className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Run the demo projection
                  </button>
                </>
              )}
            </div>
          </div>
        )}
    </ScenarioWrapper>
  );
}
